// Universal price formatter — uses Shopify's own money format (same as product page)
// so currency display is always identical across hero search and product pages.
// Falls back to Intl.NumberFormat if Shopify formatter is unavailable.
function formatPriceByCurrency(amount, currencyCode) {
  // amount coming from Storefront API is in major units (e.g. 93.07), but
  // Shopify.formatMoney / Minimog formatMoney expect cents (integer).
  const amountInCents = Math.round(amount * 100);

  // Prefer Minimog's formatMoney (uses shop money_format setting — same as Liquid | money filter)
  if (window.MinimogSettings?.money_format && typeof formatMoney === 'function') {
    try {
      return formatMoney(amountInCents, window.MinimogSettings.money_format);
    } catch (e) {}
  }

  // Fallback: Shopify.formatMoney (also uses shop money_format)
  if (window.Shopify?.formatMoney) {
    try {
      return window.Shopify.formatMoney(amountInCents);
    } catch (e) {}
  }

  // Last resort: Intl.NumberFormat
  try {
    const locale = document.documentElement.lang || navigator.language || 'nl';
    return new Intl.NumberFormat(locale, {
      style: 'currency',
      currency: currencyCode || 'EUR',
      minimumFractionDigits: currencyCode === 'JPY' ? 0 : 2,
      maximumFractionDigits: currencyCode === 'JPY' ? 0 : 2
    }).format(amount);
  } catch (e) {
    return `${amount.toFixed(2)} ${currencyCode}`;
  }
}

// Helper function to render price using Storefront API data already on the variant object.
// No extra HTTP fetch needed — the Storefront API @inContext query already returns the
// correct price and currencyCode for the selected country/currency.
async function updatePriceDisplay(priceContainer, variant, quantity = 1, productHandle) {
  if (!priceContainer || !variant) return;

  const amount = (variant.price || 0) * quantity;
  const currencyCode = variant.currencyCode || window.currentCurrency || 'EUR';
  const formatted = formatPriceByCurrency(amount, currencyCode);

  priceContainer.innerHTML = `
    <div class="m-price">
      <div class="m-price__regular">
        <span class="m-price-item m-price-item--regular">${formatted}</span>
      </div>
    </div>`;
}

// Add region collection styles
function addRegionCollectionStyles() {
  const styleEl = document.createElement('style');
  styleEl.textContent = `
    .collection-product-card.not-in-region {
      opacity: 0.5;
      pointer-events: none;
    }
    .collection-item.disabled {
      opacity: 0.5 !important;
      pointer-events: none !important;
      cursor: not-allowed !important;
      filter: grayscale(100%);
    }
  `;
  document.head.appendChild(styleEl);
}

// Set Storefront API token globally for use throughout the app
window.storefrontAccessToken = window.storefrontAccessToken || '0d7419f54e0721cfdf56a0594c535214';

// Utility function to escape HTML entities
function escapeHtml(unsafe) {
  return unsafe
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#039;");
}

// Get translated country name based on current locale
function getTranslatedCountryName(countryName) {
  let locale = 'en'; // default

  // Prioritize HTML lang attribute for generic translation (Vertaalapp) compatibility
  if (document.documentElement.lang) {
    locale = document.documentElement.lang.split('-')[0].toLowerCase();
  } else if (window.currentLocale) {
    locale = window.currentLocale;
  }

  if (window.countryTranslations && window.countryTranslations[countryName]) {
    return window.countryTranslations[countryName][locale] || window.countryTranslations[countryName]['en'] || countryName;
  }

  return countryName;
}

// Remove accents and diacritics from a string for accent-insensitive search
function removeAccents(str) {
  return str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
}

// Shared with the eSIM hub page via assets/country-flag-icon.js (loaded before this
// file in sections/hero-search.liquid), so there is exactly one implementation of
// "how do we draw a flag" instead of one per page.
const createCountryFlagImage = window.createCountryFlagImage;

const COUNTRY_MATCH_ALIASES = {
  'turkiye': 'turkey',
  'the netherlands': 'netherlands',
  'saint vincent grenadines': 'saint vincent and the grenadines',
  'saint vincent the grenadines': 'saint vincent and the grenadines',
  'republic of the congo': 'congo',
  'republic of congo': 'congo'
};

function normalizeCountryMatchValue(value) {
  if (typeof value !== 'string') return '';

  const normalized = removeAccents(value)
    .toLowerCase()
    .replace(/&/g, ' and ')
    .replace(/[^a-z0-9]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

  return COUNTRY_MATCH_ALIASES[normalized] || normalized;
}

function normalizeTopUpValue(value) {
  if (value === true || value === 'true') return 'True';
  if (value === false || value === 'false') return 'False';
  return value;
}

function getVariantPlanOptionValue(variant) {
  const selectedOptions = Array.isArray(variant?.selectedOptions) ? variant.selectedOptions : [];
  const planOption = selectedOptions.find((option) =>
    typeof option?.name === 'string' && /^plans?$/i.test(option.name)
  );

  if (typeof planOption?.value === 'string' && planOption.value.trim()) {
    return planOption.value.trim();
  }

  const title = String(variant?.title || '');
  const titleParts = title.split('/').map((part) => part.trim()).filter(Boolean);
  if (titleParts.length > 1) {
    return titleParts[0];
  }

  return '';
}

function getVariantTopUpValue(variant) {
  const selectedOptions = Array.isArray(variant?.selectedOptions) ? variant.selectedOptions : [];
  const hasDaysOption = selectedOptions.some((option) =>
    typeof option?.name === 'string' && option.name.toLowerCase() === 'days'
  );
  if (hasDaysOption) return 'False';

  const planValue = getVariantPlanOptionValue(variant).toLowerCase();
  if (!planValue) return null;

  if (planValue.includes('fixed')) return 'True';
  if (planValue.includes('unlimited')) return 'False';
  return null;
}

function getHeroPlanAvailableVariantCount(product) {
  return (product?.variants || []).filter((variant) => variant.available !== false).length;
}

function prioritizeHeroPlanProducts(products, topUpValue) {
  return filterProductsByTopUp(products, topUpValue);
}

function getProductPlanAvailability(product) {
  const availability = { fixed: false, daily: false };
  const variants = Array.isArray(product?.variants) ? product.variants : [];
  variants.forEach((variant) => {
    if (variant?.available === false) return;

    const variantTopUp = getVariantTopUpValue(variant);
    if (variantTopUp === 'True') availability.fixed = true;
    if (variantTopUp === 'False') availability.daily = true;
  });

  return availability;
}

function getPlanAvailabilityFromProducts(products) {
  return (products || []).reduce((availability, product) => {
    const productAvailability = getProductPlanAvailability(product);
    if (productAvailability.fixed) availability.fixed = true;
    if (productAvailability.daily) availability.daily = true;
    return availability;
  }, { fixed: false, daily: false });
}

function productHasExactCountryTitle(product) {
  const countries = Array.isArray(product?.country) ? product.country : [product?.country];
  const titleKey = normalizeCountryMatchValue(product?.title || '');
  if (!titleKey) return false;

  return countries.some((country) =>
    typeof country === 'string' && normalizeCountryMatchValue(country) === titleKey
  );
}

function preferExactCountryTitleProducts(products, countryName) {
  const normalizedCountry = normalizeCountryMatchValue(countryName);
  if (!normalizedCountry) return Array.isArray(products) ? products : [];

  const exactTitleProducts = (products || []).filter((product) =>
    normalizeCountryMatchValue(product?.title || '') === normalizedCountry
  );

  return exactTitleProducts.length > 0 ? exactTitleProducts : (products || []);
}

function createPlanScopedProduct(product, topUpValue) {
  if (!product) return null;

  const normalizedTopUp = normalizeTopUpValue(topUpValue);
  const variants = Array.isArray(product.variants) ? product.variants : [];
  const variantScoped = variants.filter((variant) => getVariantTopUpValue(variant) === normalizedTopUp);

  if (variantScoped.length > 0) {
    return {
      ...product,
      variants: variantScoped,
      heroSearchPlanTopUp: normalizedTopUp
    };
  }

  return null;
}

function filterProductsByTopUp(products, topUpValue) {
  const normalizedTopUp = normalizeTopUpValue(topUpValue);
  const scopedProducts = (products || [])
    .map((product) => createPlanScopedProduct(product, normalizedTopUp))
    .filter(Boolean);

  const exactCountryTitleProducts = scopedProducts.filter((product) => productHasExactCountryTitle(product));

  if (exactCountryTitleProducts.length > 0) {
    return exactCountryTitleProducts;
  }

  return scopedProducts;
}

function parseCountryMetafieldValue(countryValue) {
  if (!countryValue || typeof countryValue !== 'string') {
    return countryValue;
  }

  try {
    if (countryValue.startsWith('[') && countryValue.endsWith(']')) {
      const parsed = JSON.parse(countryValue);
      if (Array.isArray(parsed)) {
        return parsed;
      }
    }
  } catch (e) {}

  if (countryValue.includes(',')) {
    return countryValue.split(',').map((country) => country.trim()).filter(Boolean);
  }

  return countryValue;
}

// Translates the display-only words in a variant label ("Unlimited", "N days")
// into the current locale. The underlying variant.title / selectedOptions
// values are left untouched in English elsewhere, since day-count regex,
// "Plus"-variant detection and sorting all depend on parsing English text —
// this only rewrites what actually gets put in the DOM.
function translateVariantDisplayText(text, t) {
  if (!text || typeof t !== 'function') return text;

  let result = text.replace(/\bUnlimited\b/i, () => t('variant_label_unlimited_word'));

  result = result.replace(/\b(\d+)\s*(days?)\b/i, (match, num) => {
    const word = parseInt(num, 10) === 1
      ? t('variant_label_day_singular')
      : t('variant_label_day_plural');
    return `${num} ${word}`;
  });

  return result;
}

function getLocalizedDayLabel(dayCount, t) {
  if (typeof t !== 'function') return Number(dayCount) === 1 ? 'day' : 'days';

  return Number(dayCount) === 1
    ? t('variant_label_day_singular')
    : t('variant_label_day_plural');
}

function appendUnlimitedDisplayDays(label, dayCount, t) {
  const normalizedLabel = String(label || '');
  const numericDayCount = Number(dayCount);

  if (!normalizedLabel || !Number.isFinite(numericDayCount) || numericDayCount <= 0) {
    return normalizedLabel;
  }

  const lowerLabel = normalizedLabel.toLowerCase();
  const isUnlimitedLabel = lowerLabel.includes('unlimited')
    || lowerLabel.includes('onbeperkt')
    || lowerLabel.includes('sınırsız')
    || lowerLabel.includes('sinirsiz');

  if (!isUnlimitedLabel) {
    return normalizedLabel;
  }

  if (/\b\d+\s*(days?|dagen|dag|gün|gun|gunn|jours?|tage|tag|dias|dia|giorni|giorno)\b/i.test(normalizedLabel)) {
    return normalizedLabel;
  }

  const durationLabel = numericDayCount === 1
    ? `${numericDayCount} ${getLocalizedDayLabel(numericDayCount, t)}`
    : typeof t === 'function'
      ? String(t('unlimited_days_short_label') || '').replace('#', String(numericDayCount))
      : '';

  return `${normalizedLabel} ${durationLabel || `${numericDayCount} ${getLocalizedDayLabel(numericDayCount, t)}`}`;
}

function productMatchesCountryName(product, countryName) {
  const normalizedCountry = normalizeCountryMatchValue(countryName);
  if (!normalizedCountry || !product) return false;

  const countryValues = Array.isArray(product.country) ? product.country : [product.country];
  const countryMatch = countryValues.some((country) =>
    typeof country === 'string' && normalizeCountryMatchValue(country) === normalizedCountry
  );

  if (countryMatch) {
    return true;
  }

  return normalizeCountryMatchValue(product.title) === normalizedCountry;
}

function buildCountryAvailabilityIndex(products) {
  const index = new Map();

  (products || []).forEach((product) => {
    const countries = Array.isArray(product.country) ? product.country : [product.country];
    const availability = getProductPlanAvailability(product);

    countries.forEach((country) => {
      if (typeof country !== 'string') return;

      const key = normalizeCountryMatchValue(country);
      if (!key) return;

      const existing = index.get(key) || { fixed: false, daily: false };
      if (availability.fixed) existing.fixed = true;
      if (availability.daily) existing.daily = true;
      index.set(key, existing);
    });

    const titleKey = normalizeCountryMatchValue(product.title);
    if (!titleKey) return;

    const existing = index.get(titleKey) || { fixed: false, daily: false };
    if (availability.fixed) existing.fixed = true;
    if (availability.daily) existing.daily = true;
    index.set(titleKey, existing);
  });

  return index;
}

// Find country by searching in any language, return English name
function findCountryByAnyLanguage(searchQuery) {
  const query = searchQuery.toLowerCase().trim();
  const queryNoAccents = removeAccents(query);

  if (!window.countryTranslations) {
    return null;
  }

  // Search through all countries and their translations
  for (const [englishName, translations] of Object.entries(window.countryTranslations)) {
    // Check all language translations for this country
    for (const [lang, translatedName] of Object.entries(translations)) {
      const translatedLower = translatedName.toLowerCase();
      const translatedNoAccents = removeAccents(translatedLower);

      // Match both with and without accents
      if (translatedLower.startsWith(query) || translatedNoAccents.startsWith(queryNoAccents)) {
        return englishName; // Always return the English name for internal processing
      }
    }
  }

  return null;
}

// Filter countries supporting multi-language search
function filterCountriesMultilingual(countries, searchQuery) {
  const query = searchQuery.toLowerCase().trim();
  const queryNoAccents = removeAccents(query);

  if (!query) return [];

  const matchedCountries = new Set(); // Use Set to avoid duplicates
  const locale = window.currentLocale || 'en';

  // If we don't have translations, fall back to English-only search
  if (!window.countryTranslations || Object.keys(window.countryTranslations).length === 0) {
    return countries
      .filter(country => {
        const countryLower = country.toLowerCase();
        const countryNoAccents = removeAccents(countryLower);
        return countryLower.startsWith(query) || countryNoAccents.startsWith(queryNoAccents);
      })
      .sort((a, b) => {
        // Sort alphabetically ignoring accents
        const aNoAccents = removeAccents(a);
        const bNoAccents = removeAccents(b);
        return aNoAccents.localeCompare(bNoAccents);
      });
  }

  let matchDetails = [];

  // Search through all countries and their translations
  countries.forEach(englishCountryName => {
    const translations = window.countryTranslations[englishCountryName];

    if (translations) {
      // Check all language translations for this country
      for (const [lang, translatedName] of Object.entries(translations)) {
        const translatedLower = translatedName.toLowerCase();
        const translatedNoAccents = removeAccents(translatedLower);

        // Check if ANY word in the translated name starts with the query
        // Split by spaces to get individual words
        const words = translatedNoAccents.split(/\s+/);
        const matchFound = words.some(word => word.startsWith(queryNoAccents));

        // Match both with and without accents
        if (matchFound || translatedLower.startsWith(query) || translatedNoAccents.startsWith(queryNoAccents)) {
          matchedCountries.add(englishCountryName);
          matchDetails.push(`✅ Found "${translatedName}" (${lang}) for "${englishCountryName}"`);
          break; // Found a match, no need to check other translations for this country
        }
      }
    } else {
      // Fallback: if no translations exist, check English name
      const englishLower = englishCountryName.toLowerCase();
      const englishNoAccents = removeAccents(englishLower);

      // Check if ANY word in the English name starts with the query
      const words = englishNoAccents.split(/\s+/);
      const matchFound = words.some(word => word.startsWith(queryNoAccents));

      if (matchFound || englishLower.startsWith(query) || englishNoAccents.startsWith(queryNoAccents)) {
        matchedCountries.add(englishCountryName);
        matchDetails.push(`✅ Found "${englishCountryName}" (fallback English)`);
      }
    }
  });



  // Convert Set back to Array and sort by the translated names in current locale
  // Sort alphabetically ignoring accents (Å sorts as A, Ö as O, etc.)
  const results = Array.from(matchedCountries).sort((a, b) => {
    const aTranslated = getTranslatedCountryName(a);
    const bTranslated = getTranslatedCountryName(b);
    const aNoAccents = removeAccents(aTranslated);
    const bNoAccents = removeAccents(bTranslated);
    return aNoAccents.localeCompare(bNoAccents);
  });

  return results;
}

// Utility to clean up orphaned tooltips
document.addEventListener('DOMContentLoaded', function () {
  const cleanUpOrphanedTooltips = () => {
    const tooltips = document.querySelectorAll('.tooltip-content');
    tooltips.forEach(tooltip => {
      if (!tooltip.parentElement.querySelector('.has-tooltip')) {
        tooltip.remove();
      }
    });
  };

  setInterval(cleanUpOrphanedTooltips, 5000);
});

// Function to fetch country data from metafields or use fallback
async function fetchCountryData() {
  // Collection ID for "Search Values" collection
  const collectionId = 657953194329;

  try {

    // First check if window.countryList is already populated by Liquid
    if (window.countryList && Array.isArray(window.countryList) && window.countryList.length > 0) {
      return window.countryList;
    }

    // If not populated by Liquid, try to fetch from API
    try {
      const response = await fetch(`/apps/metafields/countries?collection_id=${collectionId}`);
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          return data;
        }
      }
    } catch (error) {
    }

    // If API fails, try to load from a local JSON file
    try {
      const response = await fetch('/assets/countries.json');
      if (response.ok) {
        const data = await response.json();
        if (Array.isArray(data) && data.length > 0) {
          return data;
        }
      }
    } catch (error) {
    }

    // If all else fails, use the fallback list
    return [
      'Afghanistan', 'Albania', 'Algeria', 'Anguilla', 'Antigua and Barbuda',
      'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan', 'Bahrain',
      'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Bolivia',
      'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'British Virgin Islands',
      'Brunei Darussalam', 'Bulgaria', 'Burkina Faso', 'Cambodia', 'Cameroon',
      'Canada', 'Cayman Islands', 'Central African Republic', 'Chad', 'Chile',
      'China', 'Colombia', 'Combodia', 'Republic of the Congo', 'Costa Rica', "Cote d'Ivoire",
      'Croatia', 'Cyprus', 'Czech Republic', 'Democratic Republic of the Congo',
      'Denmark', 'Dominica', 'Dominican Republic', 'Ecuador', 'Egypt', 'El Salvador',
      'Estonia', 'Finland', 'France', 'French Guiana', 'French West Indies', 'Gabon',
      'Georgia', 'Germany', 'Ghana', 'Gibraltar', 'Greece', 'Grenada', 'Guadeloupe',
      'Guam', 'Guatemala', 'Guernsey', 'Guinea-Bissau', 'Honduras', 'Hong Kong',
      'Hungary', 'Iceland', 'India', 'Indonesia', 'Iraq', 'Ireland', 'Isle of Man',
      'Israel', 'Italy', 'Jamaica', 'Japan', 'Jersey', 'Jordan', 'Kazakhstan',
      'Kenya', 'Kosovo', 'Kuwait', 'Kyrgyzstan', 'Laos', 'Latvia', 'Liberia',
      'Liechtenstein', 'Lithuania', 'Luxembourg', 'Macao', 'Madagascar', 'Malawi',
      'Malaysia', 'Mali', 'Malta', 'Mauritius', 'Mexico', 'Moldova', 'Monaco',
      'Mongolia', 'Montenegro', 'Montserrat', 'Morocco', 'Mozambique', 'Nepal',
      'Netherlands', 'Netherlands Antilles', 'New Zealand', 'Nicaragua', 'Niger',
      'Nigeria', 'North Macedonia', 'North Macedonia ', 'Norway', 'Oman', 'Pakistan',
      'Panama', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal', 'Puerto Rico',
      'Qatar', 'Reunion', 'Romania', 'Russia', 'Saint Kitts and Nevis',
      'Saint Lucia', 'Saint Vincent and the Grenadines', 'Saudi Arabia', 'Senegal',
      'Serbia', 'Seychelles', 'Singapore', 'Slovakia', 'Slovenia', 'South Africa',
      'South Korea', 'Spain', 'Sri Lanka', 'Sudan', 'Swaziland', 'Sweden',
      'Switzerland', 'Taiwan', 'Tanzania', 'Thailand', 'Tunisia', 'Turkey',
      'Turks and Caicos Islands', 'Uganda', 'Ukraine', 'United Arab Emirates',
      'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan', 'Vietnam',
      'Yemen', 'Zambia'
    ];
  } catch (error) {
    console.error('Error in fetchCountryData:', error);

    // Return a minimal list of countries as a last resort
    return [
      'Australia', 'Canada', 'France', 'Germany', 'Italy', 'Japan', 'Spain',
      'United Kingdom', 'United States'
    ];
  }
}

// Define quantity input component if not already defined
if (!customElements.get('m-quantity-input')) {
  class MQuantityInput extends HTMLElement {
    get input() {
      return this.querySelector('input');
    }

    connectedCallback() {
      this.input.addEventListener('change', this.onInputChange.bind(this));
      this.querySelectorAll('button').forEach(button => {
        // Add click event listener
        button.addEventListener('click', this.onButtonClick.bind(this));

        // Add touch event listeners for mobile support
        button.addEventListener('touchstart', this.onButtonTouch.bind(this), { passive: false });
        button.addEventListener('touchend', this.onButtonTouchEnd.bind(this), { passive: false });

        // Prevent double-tap zoom on mobile
        button.style.touchAction = 'manipulation';
        button.style.userSelect = 'none';
        button.style.webkitUserSelect = 'none';
        button.style.webkitTouchCallout = 'none';
      });
    }

    onInputChange() {
      this.validateQtyRules();
    }

    onButtonClick(event) {
      event.preventDefault();
      this.handleButtonAction(event.currentTarget);
    }

    onButtonTouch(event) {
      // Prevent default to avoid ghost clicks
      event.preventDefault();
      event.stopPropagation();

      // Add visual feedback
      event.currentTarget.style.opacity = '0.7';
    }

    onButtonTouchEnd(event) {
      // Prevent default to avoid ghost clicks
      event.preventDefault();
      event.stopPropagation();

      // Remove visual feedback
      event.currentTarget.style.opacity = '1';

      // Handle the button action
      this.handleButtonAction(event.currentTarget);
    }

    handleButtonAction(button) {
      const previousValue = this.input.value;

      if (button.name === 'plus') {
        this.input.stepUp();
      } else {
        this.input.stepDown();
      }

      if (previousValue !== this.input.value) {
        this.input.dispatchEvent(new Event('change', { bubbles: true }));

        // Also trigger input event for mobile compatibility
        this.input.dispatchEvent(new Event('input', { bubbles: true }));

        // Direct call to hero search price update for mobile reliability
        if (window.heroSearchInstance && window.heroSearchInstance.updatePrice) {
          setTimeout(() => {
            window.heroSearchInstance.updatePrice();
          }, 10);
        }
      }
    }

    validateQtyRules() {
      const value = parseInt(this.input.value);
      const buttonMinus = this.querySelector("button[name='minus']");
      const buttonPlus = this.querySelector("button[name='plus']");

      if (this.input.min) {
        buttonMinus.classList.toggle('m:disabled', value <= parseInt(this.input.min));
      }
      if (this.input.max) {
        buttonPlus.classList.toggle('m:disabled', value >= parseInt(this.input.max));
      }
    }
  }

  customElements.define('m-quantity-input', MQuantityInput);
}

class HeroSearch {
  static HERO_REVIEW_CONTEXT_ICON = `
    <svg class="hero-search-review-card__context-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 400 400" aria-hidden="true" focusable="false">
      <g transform="matrix(1,0,0,1,-450,0)">
        <g transform="matrix(3.108108,0,0,3.108108,424.662162,5.743243)">
          <path d="M12.174,62.5C12.174,29.205 39.205,2.174 72.5,2.174C105.795,2.174 132.826,29.205 132.826,62.5C132.826,95.795 105.795,122.826 72.5,122.826C39.205,122.826 12.174,95.795 12.174,62.5ZM55.171,14.871C37.173,21.429 23.925,37.905 22.053,57.674L45.074,57.674C45.672,40.314 49.46,25.11 54.892,15.365C54.985,15.199 55.078,15.034 55.171,14.871ZM122.947,57.674C121.075,37.905 107.827,21.429 89.829,14.871C89.922,15.034 90.015,15.199 90.108,15.365C95.54,25.11 99.328,40.314 99.926,57.674L122.947,57.674ZM89.829,110.129C107.827,103.571 121.075,87.095 122.947,67.326L99.926,67.326C99.328,84.686 95.54,99.89 90.108,109.635C90.015,109.801 89.922,109.966 89.829,110.129ZM22.053,67.326C23.925,87.095 37.173,103.571 55.171,110.129C55.078,109.966 54.985,109.801 54.892,109.635C49.46,99.89 45.672,84.686 45.074,67.326L22.053,67.326ZM90.27,57.674C89.714,42.256 86.511,28.737 81.677,20.064C80.139,17.304 78.463,15.077 76.617,13.572C75.309,12.506 73.953,11.826 72.5,11.826C71.047,11.826 69.691,12.506 68.383,13.572C66.537,15.077 64.861,17.304 63.323,20.064C58.489,28.737 55.286,42.256 54.73,57.674L90.27,57.674ZM54.73,67.326C55.286,82.744 58.489,96.263 63.323,104.936C64.861,107.696 66.537,109.923 68.383,111.428C69.691,112.494 71.047,113.174 72.5,113.174C73.953,113.174 75.309,112.494 76.617,111.428C78.463,109.923 80.139,107.696 81.677,104.936C86.511,96.263 89.714,82.744 90.27,67.326L54.73,67.326Z"/>
        </g>
      </g>
    </svg>
  `;

  static JUDGE_ME_SOURCE_SELECTORS = [
    '.jdgm-carousel-wrapper',
    '.jdgm-carousel',
    '.jdgm-carousel__wrapper',
    '.jdgm-widget[data-widget-name*="carousel"]',
    '.jdgm-widget[data-widget*="carousel"]',
    '.jdgm-rev-widg',
    '.jdgm-all-reviews-widget',
    '.jdgm-widget.jdgm-review-widget',
    '.m-app-section .shopify-app-block:has([class*="jdgm"])',
    '.m-app-section [class*="jdgm"][class*="widget"]',
    '[class*="jdgm"][class*="carousel"]'
  ];

  static JUDGE_ME_REVIEW_SELECTORS = [
    '.jdgm-carousel-item',
    '.jdgm-carousel__item',
    '.jdgm-carousel-item__review',
    '.jdgm-rev-widg__reviews .jdgm-rev-widg__review',
    '.jdgm-rev-widg__review',
    '.jdgm-rev',
    '.jdgm-widget [class*="review-card"]',
    '.jdgm-widget [class*="carousel-item"]',
    '.jdgm-widget [class*="review-item"]',
    '[data-review-id]'
  ];

  initRichTextSlider() {
    if (this.isDestroyed) return;

    const sliderEl = document.querySelector('.hero-search .hero-text-wrapper .m-richtext-slider.hero-slider-container');
    if (!sliderEl) return;

    const previousSlider = sliderEl.__heroSlider;
    const previousSliderMeta = sliderEl.__heroSliderMeta || null;

    if (sliderEl.__heroSlider && typeof sliderEl.__heroSlider.destroy === 'function') {
      sliderEl.__heroSlider.destroy();
    }

    const allSlides = Array.from(sliderEl.querySelectorAll('.hero-slider-slide'));
    allSlides.forEach((slide) => {
      slide.classList.remove('is-active');
      slide.setAttribute('aria-hidden', 'true');
    });

    allSlides.forEach((slide, index) => {
      if (!slide.dataset.heroRuntimeKey) {
        slide.dataset.heroRuntimeKey = slide.hasAttribute('data-hero-judgeme-slide')
          ? 'judge-me-reviews'
          : `hero-slide-${index}`;
      }
    });

    const slides = allSlides.filter((slide) => !slide.hidden);
    if (!slides.length) return;

    const slideSignature = slides
      .map((slide) => slide.dataset.heroRuntimeKey)
      .join('|');

    const initialSlideIndex = previousSlider
      && previousSliderMeta
      && previousSliderMeta.signature === slideSignature
      && typeof previousSlider.getActiveIndex === 'function'
      ? previousSlider.getActiveIndex()
      : 0;

    const timing = parseInt(sliderEl.dataset.sliderTiming, 10) || 5;
    const showPagination = sliderEl.dataset.showPagination !== 'false';
    let paginationEl = null;

    if (showPagination) {
      paginationEl = sliderEl.querySelector('.hero-slider-pagination');
      if (!paginationEl) {
        paginationEl = document.createElement('div');
        paginationEl.className = 'hero-slider-pagination';
        sliderEl.appendChild(paginationEl);
      }
    } else {
      paginationEl = sliderEl.querySelector('.hero-slider-pagination');
      if (paginationEl) {
        paginationEl.remove();
      }
    }

    let activeIndex = 0;
    let autoRotateTimer = null;
    let bulletButtons = [];
    let touchStartX = 0;
    let touchStartY = 0;
    let rebuildTimer = null;
    const cleanupFns = [];

    const addListener = (target, eventName, handler, options) => {
      target.addEventListener(eventName, handler, options);
      cleanupFns.push(() => target.removeEventListener(eventName, handler, options));
    };

    const goToSlide = (index, emitChange = true) => {
      activeIndex = ((index % slides.length) + slides.length) % slides.length;

      slides.forEach((slide, slideIndex) => {
        const isActive = slideIndex === activeIndex;
        slide.classList.toggle('is-active', isActive);
        slide.setAttribute('aria-hidden', isActive ? 'false' : 'true');
      });

      bulletButtons.forEach((button, buttonIndex) => {
        const isActive = buttonIndex === activeIndex;
        button.classList.toggle('hero-slider-pagination-bullet-active', isActive);
        button.setAttribute('aria-current', isActive ? 'true' : 'false');
      });

      if (emitChange) {
        document.dispatchEvent(new CustomEvent('swiper:slideChange'));

        setTimeout(() => {
          if (window.heroSearch && typeof window.heroSearch.setupFindOutButtons === 'function') {
            window.heroSearch.setupFindOutButtons();
          }
        }, 100);
      }
    };

    const stopAutoRotate = () => {
      if (autoRotateTimer) {
        window.clearInterval(autoRotateTimer);
        autoRotateTimer = null;
      }
    };

    const startAutoRotate = () => {
      if (slides.length <= 1) return;

      stopAutoRotate();
      autoRotateTimer = window.setInterval(() => {
        goToSlide(activeIndex + 1);
      }, timing * 1000);
    };

    const restartSlider = () => {
      window.clearTimeout(rebuildTimer);
      rebuildTimer = window.setTimeout(() => {
        this.initRichTextSlider();
      }, 120);
    };

    if (showPagination && paginationEl) {
      paginationEl.innerHTML = '';

      bulletButtons = slides.map((_, index) => {
        const bullet = document.createElement('button');
        bullet.type = 'button';
        bullet.className = 'hero-slider-pagination-bullet';
        bullet.setAttribute('aria-label', `Go to slide ${index + 1}`);
        addListener(bullet, 'click', () => {
          goToSlide(index);
          startAutoRotate();
        });
        paginationEl.appendChild(bullet);
        return bullet;
      });
    }

    sliderEl.classList.add('hero-slider-initialized');

    slides.forEach((slide, index) => {
      slide.dataset.heroSlideIndex = index;
    });

    goToSlide(initialSlideIndex, false);
    startAutoRotate();

    addListener(sliderEl, 'mouseenter', () => {
      stopAutoRotate();
    });

    addListener(sliderEl, 'mouseleave', () => {
      startAutoRotate();
    });

    addListener(document, 'visibilitychange', () => {
      if (document.hidden) {
        stopAutoRotate();
      } else {
        startAutoRotate();
      }
    });

    addListener(sliderEl, 'touchstart', (event) => {
      const touch = event.changedTouches?.[0];
      if (!touch) return;
      touchStartX = touch.clientX;
      touchStartY = touch.clientY;
      stopAutoRotate();
    }, { passive: true });

    addListener(sliderEl, 'touchend', (event) => {
      const touch = event.changedTouches?.[0];
      if (!touch) {
        startAutoRotate();
        return;
      }

      const deltaX = touch.clientX - touchStartX;
      const deltaY = touch.clientY - touchStartY;

      if (Math.abs(deltaX) > 30 && Math.abs(deltaX) > Math.abs(deltaY)) {
        if (deltaX < 0) {
          goToSlide(activeIndex + 1);
        } else {
          goToSlide(activeIndex - 1);
        }
      }

      startAutoRotate();
    }, { passive: true });

    addListener(sliderEl, 'pointerdown', (event) => {
      if (event.pointerType === 'touch') return;

      touchStartX = event.clientX;
      touchStartY = event.clientY;
      stopAutoRotate();
    });

    addListener(sliderEl, 'pointerup', (event) => {
      if (event.pointerType === 'touch') return;

      const deltaX = event.clientX - touchStartX;
      const deltaY = event.clientY - touchStartY;

      if (Math.abs(deltaX) > 28 && Math.abs(deltaX) > Math.abs(deltaY)) {
        if (deltaX < 0) {
          goToSlide(activeIndex + 1);
        } else {
          goToSlide(activeIndex - 1);
        }
      }

      startAutoRotate();
    });

    addListener(window, 'focus', startAutoRotate);
    addListener(window, 'pageshow', startAutoRotate);
    addListener(window, 'resize', restartSlider);
    addListener(window, 'orientationchange', restartSlider);

    this.richTextSlider = {
      goToSlide,
      getActiveIndex: () => activeIndex,
      startAutoRotate,
      stopAutoRotate,
      destroy: () => {
        stopAutoRotate();
        window.clearTimeout(rebuildTimer);
        cleanupFns.forEach(cleanup => cleanup());
        cleanupFns.length = 0;
      }
    };
    sliderEl.__heroSlider = this.richTextSlider;
    sliderEl.__heroSliderMeta = {
      signature: slideSignature,
      count: slides.length
    };

    document.dispatchEvent(new CustomEvent('swiper:init'));

    setTimeout(() => {
      if (window.heroSearch && typeof window.heroSearch.setupFindOutButtons === 'function') {
        window.heroSearch.setupFindOutButtons();
      }
    }, 100);
  }
  constructor() {
    this.isDestroyed = false;
    this.heroResponsiveCleanupFns = [];
    this.heroRuntimeCleanupFns = [];
    this.heroResponsiveRaf = null;
    this.heroResponsiveTimer = null;
    this.heroResponsiveLateTimer = null;
    this.searchInput = document.querySelector('#search-input');
    this.searchBox = document.querySelector('.search-box');
    this.searchDropdown = document.querySelector('.predictive-search');
    this.productInfo = document.querySelector('.product-info');
    this.selectedItemContainer = document.querySelector('.selected-item');
    this.daysSlider = document.querySelector('.slider-container');
    this.daysDisplay = document.querySelector('.days-display');
    this.priceAmount = document.querySelector('.price-display .amount');
    this.addToCartButton = document.querySelector('.add-to-cart');

    // New screen elements
    this.screen1 = document.querySelector('.hero-screen-1');
    this.screen2 = document.querySelector('.hero-screen-2');
    this.screen3 = document.querySelector('.hero-screen-3');

    // Reset state
    this.currentScreen = 1;
    this.selectedProduct = null;
    this.currentVariant = null;
    this.days = 1;
    this.isDragging = false;
    this.isTransitioning = false;
    this.isOpeningDetailPanel = false;
    this.detailPanelRequestId = 0;
    this.selectedCountryHydrationToken = 0;
    this.selectedCountryPrefetchPromise = null;
    this.allProductsCachePromise = null;
    this.allProductsWithVariantsCachePromise = null;
    this.selectedCountryPlanCache = new Map();
    this.selectedCountryPlanCacheTime = new Map();
    this.selectedCountryPlanPromiseCache = new Map();
    this.backgroundVariantWarmupPromise = null;
    this.countryAvailabilityIndex = null;
    this.detailPanelProductLink = null;

    // Price cache for smooth slider experience
    this.variantPricesCache = new Map();
    this.priceUpdateTimer = null;
    this.priceUpdateAbortController = null;
    this.judgeMeObserver = null;
    this.judgeMeSourceObserver = null;
    this.judgeMeSourceRoot = null;
    this.judgeMeSourceHidden = false;
    this.judgeMeSlideWasActivated = false;
    this.judgeMeMirrorLocked = false;
    this.judgeMeSyncTimer = null;
    this.lastJudgeMeReviews = [];
    this.lastJudgeMeMeta = null;

    // Show first screen
    this.showScreen(1);

    // Make instance available globally for debugging
    window.heroSearchInstance = this;

    // PRELOAD product data immediately for smooth first experience
    this.preloadProductData();

    // Initialize component
    this.init();
    this.setupMobileInteraction();

    // Add viewport height handling
    this.handleMobileViewportHeight();
    this.setupHeroResponsiveSync();
    this.scheduleHeroResponsiveSync('init');

    // Initialize tooltip
    this.tooltipInitialized = false;

    // Bind methods
    this.setupColExplanationTooltip = this.setupColExplanationTooltip.bind(this);

    // Initialize tooltip only once when DOM is ready
    if (document.readyState === 'loading') {
      document.addEventListener('DOMContentLoaded', () => {
        this.setupColExplanationTooltip();
        this.tooltipInitialized = true;
      });
    } else {
      this.setupColExplanationTooltip();
      this.tooltipInitialized = true;
    }

    // Handle orientation changes
    this.boundDetailPanelOrientationSync = () => {
      // Initial delay to let the browser start orientation change
      setTimeout(() => {
        // First attempt
        if (this.detailPanel && this.detailPanel.classList.contains('active')) {
          this.updateDetailPanelPosition();
        }

        // Second attempt after layout settles
        setTimeout(() => {
          if (this.detailPanel && this.detailPanel.classList.contains('active')) {
            this.updateDetailPanelPosition();
          }

          // Final attempt after all transitions complete
          setTimeout(() => {
            if (this.detailPanel && this.detailPanel.classList.contains('active')) {
              this.updateDetailPanelPosition();
              // Force a reflow
              void this.detailPanel.offsetHeight;
            }
          }, 300);
        }, 150);
      }, 50);
    };
    window.addEventListener('orientationchange', this.boundDetailPanelOrientationSync);
    this.heroRuntimeCleanupFns.push(() => {
      window.removeEventListener('orientationchange', this.boundDetailPanelOrientationSync);
    });

    // Also listen for resize events (for desktop/tablet browsers)
    this.detailPanelResizeTimeout = null;
    this.boundDetailPanelResizeSync = () => {
      window.clearTimeout(this.detailPanelResizeTimeout);
      this.detailPanelResizeTimeout = window.setTimeout(() => {
        if (this.detailPanel && this.detailPanel.classList.contains('active')) {
          this.updateDetailPanelPosition();
          // Force a reflow after resize
          void this.detailPanel.offsetHeight;
        }
      }, 250); // Debounce resize events
    };
    window.addEventListener('resize', this.boundDetailPanelResizeSync);
    this.heroRuntimeCleanupFns.push(() => {
      window.removeEventListener('resize', this.boundDetailPanelResizeSync);
      window.clearTimeout(this.detailPanelResizeTimeout);
    });
  }

  handleMobileViewportHeight() {
    const liveViewportHeight = window.visualViewport
      ? Math.round(window.visualViewport.height)
      : window.innerHeight;

    // Get the actual viewport height
    const vh = liveViewportHeight * 0.01;
    // Set the custom property
    document.documentElement.style.setProperty('--vh', `${vh}px`);
    document.documentElement.style.setProperty('--hero-live-vh', `${liveViewportHeight}px`);

    const headerEl = window.innerWidth < 1024
      ? document.querySelector('header.m-header__mobile')
      : document.querySelector('header.m-header__desktop');
    if (headerEl) {
      const headerHeight = Math.round(headerEl.getBoundingClientRect().height || headerEl.offsetHeight || 0);
      document.documentElement.style.setProperty('--m-header-height', `${headerHeight}px`);
    }

    // Handle iOS Safari toolbar
    if (this.isIOS() && window.matchMedia("(orientation: landscape)").matches) {
      document.documentElement.style.setProperty('--safe-area-inset-top', 'env(safe-area-inset-top)');
      document.documentElement.style.setProperty('--safe-area-inset-bottom', 'env(safe-area-inset-bottom)');
    } else {
      document.documentElement.style.setProperty('--safe-area-inset-top', '0px');
      document.documentElement.style.setProperty('--safe-area-inset-bottom', '0px');
    }
  }

  isIOS() {
    return /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
  }

  getHeroViewportMode() {
    const width = window.visualViewport
      ? Math.round(window.visualViewport.width)
      : window.innerWidth;
    const height = window.visualViewport
      ? Math.round(window.visualViewport.height)
      : window.innerHeight;
    const isLandscape = width > height;

    if (isLandscape && width <= 980 && height <= 600) return 'mobile-landscape';
    if (!isLandscape && width <= 767) return 'mobile-portrait';
    if (!isLandscape && width <= 1040 && height >= 900) return 'tablet-portrait';
    if (isLandscape && width <= 1380) return 'tablet-landscape';

    return 'desktop';
  }

  scheduleHeroResponsiveSync(reason = 'resize') {
    if (this.isDestroyed) return;

    if (this.heroResponsiveRaf) {
      window.cancelAnimationFrame(this.heroResponsiveRaf);
      this.heroResponsiveRaf = null;
    }

    if (this.heroResponsiveTimer) {
      window.clearTimeout(this.heroResponsiveTimer);
      this.heroResponsiveTimer = null;
    }

    if (this.heroResponsiveLateTimer) {
      window.clearTimeout(this.heroResponsiveLateTimer);
      this.heroResponsiveLateTimer = null;
    }

    const runSync = () => {
      if (this.isDestroyed) return;

      this.handleMobileViewportHeight();

      const root = document.documentElement;
      const viewportMode = this.getHeroViewportMode();
      root.setAttribute('data-hero-viewport', viewportMode);
      root.setAttribute('data-hero-sync-reason', reason);

      if (typeof this.initRichTextSlider === 'function') {
        this.initRichTextSlider();
      }

      document.dispatchEvent(new CustomEvent('hero-search:layoutchange', {
        detail: { reason, viewportMode }
      }));
    };

    this.heroResponsiveRaf = window.requestAnimationFrame(() => {
      runSync();

      this.heroResponsiveTimer = window.setTimeout(() => {
        runSync();
      }, 160);

      this.heroResponsiveLateTimer = window.setTimeout(() => {
        runSync();
      }, 340);
    });
  }

  setupHeroResponsiveSync() {
    if (this.heroResponsiveCleanupFns.length) return;

    const syncResize = () => this.scheduleHeroResponsiveSync('resize');
    const syncOrientation = () => this.scheduleHeroResponsiveSync('orientationchange');
    const syncPageShow = () => this.scheduleHeroResponsiveSync('pageshow');
    const syncViewport = () => this.scheduleHeroResponsiveSync('visualViewport');
    const removeFns = [];

    const addCleanupListener = (target, eventName, handler, options) => {
      target.addEventListener(eventName, handler, options);
      removeFns.push(() => target.removeEventListener(eventName, handler, options));
    };

    addCleanupListener(window, 'resize', syncResize);
    addCleanupListener(window, 'orientationchange', syncOrientation);
    addCleanupListener(window, 'pageshow', syncPageShow);

    if (window.visualViewport) {
      addCleanupListener(window.visualViewport, 'resize', syncViewport);
    }

    const orientationMedia = window.matchMedia('(orientation: landscape)');
    const mediaHandler = () => this.scheduleHeroResponsiveSync('matchMedia');
    if (typeof orientationMedia.addEventListener === 'function') {
      orientationMedia.addEventListener('change', mediaHandler);
      removeFns.push(() => orientationMedia.removeEventListener('change', mediaHandler));
    } else if (typeof orientationMedia.addListener === 'function') {
      orientationMedia.addListener(mediaHandler);
      removeFns.push(() => orientationMedia.removeListener(mediaHandler));
    }

    this.heroResponsiveCleanupFns = removeFns;
  }

  setupMobileInteraction() {
    const searchInput = document.querySelector('.search-input');
    if (!searchInput) return;

    // Prevent touch event propagation
    searchInput.addEventListener('touchstart', (e) => {
      e.stopPropagation();
    }, { passive: true });

    searchInput.addEventListener('touchend', (e) => {
      e.stopPropagation();
      searchInput.focus();
    }, { passive: true });

    // Ensure input is focusable on iOS
    searchInput.addEventListener('click', (e) => {
      e.stopPropagation();
      searchInput.focus();
    }, { passive: true });
  }

  init() {

    try {
      // Initialize the rich text slider
      this.initRichTextSlider();

      // Initialize tooltip
      this.setupColExplanationTooltip();

      // Get elements for easy access - only if they weren't found in constructor
      if (!this.screen1) this.screen1 = document.querySelector('.hero-screen-1');
      if (!this.screen2) this.screen2 = document.querySelector('.hero-screen-2');
      if (!this.screen3) this.screen3 = document.querySelector('.hero-screen-3');
      if (!this.productCollection) this.productCollection = document.querySelector('.product-collection');

      // Ensure critical selectors are properly initialized
      if (!this.searchInput) this.searchInput = document.querySelector('#search-input');
      if (!this.searchBox) this.searchBox = document.querySelector('.search-box');
      if (!this.searchDropdown) this.searchDropdown = document.querySelector('.predictive-search');

      if (!this.selectedItemContainer) this.selectedItemContainer = document.querySelector('.selected-item');
      if (!this.detailPanel) this.detailPanel = document.querySelector('.detail-panel');
      if (!this.detailPanelContent) this.detailPanelContent = document.querySelector('.detail-panel-content');
      if (!this.detailPanelClose) this.detailPanelClose = document.querySelector('.detail-panel-close');
      if (!this.detailPanelBackdrop) this.detailPanelBackdrop = document.querySelector('.detail-panel-backdrop');
      if (!this.continueToPlansBtn) this.continueToPlansBtn = document.querySelector('#continue-to-plan');
      if (!this.backToSearchBtn) this.backToSearchBtn = document.querySelector('#back-to-search');
      if (!this.continueToDaysBtn) this.continueToDaysBtn = document.querySelector('#continue-to-days');
      if (!this.backToPlanBtn) this.backToPlanBtn = document.querySelector('#back-to-plan');
      if (!this.daysSlider) this.daysSlider = document.querySelector('.slider-container');
      if (!this.priceAmount) this.priceAmount = document.querySelector('.price-display .amount');
      if (!this.addToCartButton) this.addToCartButton = document.querySelector('.add-to-cart');
      if (!this.reviewSlide) this.reviewSlide = document.querySelector('[data-hero-judgeme-slide]');
      if (!this.reviewHeader) this.reviewHeader = document.querySelector('[data-hero-judgeme-header]');
      if (!this.reviewAverage) this.reviewAverage = document.querySelector('[data-hero-judgeme-average]');
      if (!this.reviewCards) this.reviewCards = document.querySelector('[data-hero-judgeme-cards]');

      // Remove selectedDestination reference

      // Add variant styling once during initialization
      this.addVariantStyles();

      // Setup the screen navigation
      this.setupScreenNavigation();

      // Setup the days slider
      this.setupDaysSlider();

      // Setup variant selection
      this.setupVariantSelector();

      // Setup add to cart functionality
      this.setupAddToCart();

      // Setup the Find Out buttons
      this.setupFindOutButtons();

      // Setup the "What's the difference?" button
      this.setupWhatsTheDifferenceButton();

      // Mirror live Judge.me content into the optional hero review slide
      this.initJudgeMeReviewMirror();

      // Load country data and set up search
      this.loadCountryData().then(() => {
        // Setup the search functionality after country data is loaded
        this.setupSearch();

        // Focus the search input after data is loaded
        if (this.searchInput) {
          setTimeout(() => {
            this.searchInput.focus();
          }, 300);
        }
      });

      // Resize: no manual tab content repositioning needed — CSS flex handles it.

      // Translate initial screen content immediately
      setTimeout(() => {
        this.translateDetailPanelInfo();
      }, 100);
    } catch (error) {
      console.error('Error initializing hero search component:', error);
    }
  }

  initJudgeMeReviewMirror() {
    if (!this.reviewSlide || !this.reviewCards) return;

    if (!this.lastJudgeMeReviews.length) {
      this.setJudgeMeSlideState('loading');
    }

    const sourceRoot = this.findJudgeMeReviewSource();
    if (sourceRoot) {
      this.watchJudgeMeSource(sourceRoot);
      this.syncJudgeMeReviews();
      return;
    }

    this.observeJudgeMeContainerDiscovery();
  }

  observeJudgeMeContainerDiscovery() {
    if (this.judgeMeObserver) return;

    const tryDiscoverSource = () => {
      this.judgeMeDiscoveryTimer = null;
      const sourceRoot = this.findJudgeMeReviewSource();
      if (!sourceRoot) return;

      this.judgeMeObserver.disconnect();
      this.judgeMeObserver = null;
      this.watchJudgeMeSource(sourceRoot);
      this.syncJudgeMeReviews();
    };

    this.judgeMeObserver = new MutationObserver(() => {
      // Throttle: check at most once per 120ms window instead of re-scanning
      // the whole document/every <script> tag on every single DOM mutation
      // during page hydration.
      if (this.judgeMeDiscoveryTimer) return;
      this.judgeMeDiscoveryTimer = window.setTimeout(tryDiscoverSource, 120);
    });

    this.judgeMeObserver.observe(document.body, {
      childList: true,
      subtree: true
    });
  }

  watchJudgeMeSource(sourceRoot) {
    if (!sourceRoot) return;

    this.judgeMeSourceRoot = sourceRoot;

    if (this.judgeMeSourceObserver) {
      this.judgeMeSourceObserver.disconnect();
      this.judgeMeSourceObserver = null;
    }

    this.judgeMeSourceObserver = new MutationObserver(() => {
      this.scheduleJudgeMeSync();
    });

    this.judgeMeSourceObserver.observe(sourceRoot, {
      childList: true,
      subtree: true,
      characterData: true
    });
  }

  scheduleJudgeMeSync() {
    if (this.judgeMeSyncTimer) {
      window.clearTimeout(this.judgeMeSyncTimer);
    }

    this.judgeMeSyncTimer = window.setTimeout(() => {
      this.judgeMeSyncTimer = null;
      this.syncJudgeMeReviews();
    }, 120);
  }

  stopJudgeMeReviewObservers() {
    if (this.judgeMeObserver) {
      this.judgeMeObserver.disconnect();
      this.judgeMeObserver = null;
    }

    if (this.judgeMeDiscoveryTimer) {
      window.clearTimeout(this.judgeMeDiscoveryTimer);
      this.judgeMeDiscoveryTimer = null;
    }

    if (this.judgeMeSourceObserver) {
      this.judgeMeSourceObserver.disconnect();
      this.judgeMeSourceObserver = null;
    }

    if (this.judgeMeSyncTimer) {
      window.clearTimeout(this.judgeMeSyncTimer);
      this.judgeMeSyncTimer = null;
    }
  }

  findJudgeMeReviewSource() {
    const configuredAppSource = this.findConfiguredJudgeMeAppSource();
    if (configuredAppSource) {
      return configuredAppSource;
    }

    let bestSource = null;
    let bestScore = -1;

    for (const selector of HeroSearch.JUDGE_ME_SOURCE_SELECTORS) {
      const candidates = Array.from(document.querySelectorAll(selector)).filter((node) => !node.closest('.hero-search'));

      candidates.forEach((node) => {
        const score = this.scoreJudgeMeReviewSource(node);
        if (score > bestScore) {
          bestScore = score;
          bestSource = node;
        }
      });
    }

    if (bestSource) {
      return bestSource;
    }

    const fallbackReviews = Array.from(document.querySelectorAll(HeroSearch.JUDGE_ME_REVIEW_SELECTORS.join(', ')))
      .filter((node) => !node.closest('.hero-search'));

    if (fallbackReviews.length >= 1) {
      const fallbackSource = fallbackReviews[0].closest('.shopify-app-block, .jdgm-widget, section, div') || fallbackReviews[0].parentElement;
      return fallbackSource;
    }

    return null;
  }

  getJudgeMeBlockId(sourceRoot) {
    if (!sourceRoot) return '';

    return (
      sourceRoot.getAttribute('data-jdgm-block-id') ||
      sourceRoot.querySelector?.('[data-jdgm-block-id]')?.getAttribute('data-jdgm-block-id') ||
      ''
    );
  }

  extractJudgeMeSourceConfig(sourceRoot) {
    const blockId = this.getJudgeMeBlockId(sourceRoot);
    if (!blockId) return null;

    const scripts = Array.from(document.querySelectorAll('script'));
    const configScript = scripts.find((script) => (script.textContent || '').includes(`const blockId = "${blockId}"`));
    const scriptText = configScript?.textContent || '';
    if (!scriptText) return null;

    const extractString = (key) => {
      const match = scriptText.match(new RegExp(`${key}:\\s*"([^"]*)"`, 'i'));
      return match ? match[1] : '';
    };

    const extractNumber = (key) => {
      const match = scriptText.match(new RegExp(`${key}:\\s*'?([0-9]+(?:\\.[0-9]+)?)'?`, 'i'));
      return match ? Number(match[1]) : null;
    };

    return {
      blockId,
      reviewsSelection: extractString('reviews_selection') || 'all',
      collectionId: extractString('collection_id'),
      productId: extractString('product_id'),
      productIdsRaw: extractString('product_ids'),
      customTagsRaw: extractString('custom_tags'),
      tagFilterRaw: extractString('tag_filter'),
      maxReviews: extractNumber('max_reviews') || 20,
      reviewsShown: extractNumber('reviews_shown') || 3,
      platform: extractString('platform') || 'shopify',
      shopDomain: extractString('shop_domain') || window.Shopify?.shop || '',
      url: extractString('url') || window.location.origin,
      primaryLanguage: extractString('primary_language') || document.documentElement.lang || 'en'
    };
  }

  buildJudgeMeApiQuery(config) {
    if (!config?.shopDomain) return null;

    const params = new URLSearchParams({
      widget_type: 'cards_carousel',
      page: '1',
      per_page: String(Math.max(config.maxReviews || 20, config.reviewsShown || 3, 3)),
      sort_by: 'created_at',
      sort_dir: 'desc',
      shop_domain: config.shopDomain,
      platform: config.platform || 'shopify'
    });

    const parseJsonArray = (rawValue) => {
      if (!rawValue) return [];
      try {
        const parsed = JSON.parse(rawValue);
        return Array.isArray(parsed) ? parsed : [];
      } catch (error) {
        return [];
      }
    };

    const reviewsSelection = config.reviewsSelection || 'all';
    if (reviewsSelection === 'current_product' && config.productId) {
      params.set('product_id', config.productId);
    } else if (reviewsSelection === 'current_collection' && config.collectionId) {
      params.set('collection_id', config.collectionId);
    } else if (reviewsSelection === 'custom_products') {
      parseJsonArray(config.productIdsRaw).forEach((productId) => {
        params.append('select_products[]', String(productId));
      });
    } else if (reviewsSelection === 'featured') {
      params.set('featured_only', 'true');
    } else if (reviewsSelection === 'store_reviews') {
      params.set('review_type', 'store_reviews');
    } else if (reviewsSelection === 'product_reviews') {
      params.set('review_type', 'product_reviews');
    }

    parseJsonArray(config.customTagsRaw).forEach((tag) => {
      params.append('custom_tags[]', String(tag));
    });

    parseJsonArray(config.tagFilterRaw).forEach((tag) => {
      params.append('tag_filter[]', String(tag));
    });

    return params;
  }

  parseJudgeMeApiReviewsFromHtml(html) {
    if (!html) return [];

    const doc = new DOMParser().parseFromString(html, 'text/html');
    const reviewNodes = Array.from(doc.querySelectorAll('.jdgm-rev, .jdgm-rev-widg__review'));

    return reviewNodes.map((reviewEl, index) => {
      const queryText = (selectors) => {
        for (const selector of selectors) {
          const match = reviewEl.querySelector(selector);
          if (!match) continue;

          const text = match.textContent?.replace(/\s+/g, ' ').trim();
          if (text) return text;
        }

        return '';
      };

      const timestampEl = reviewEl.querySelector('.jdgm-rev__timestamp, .jdgm-rev-widg__date, time');
      const rawDate =
        timestampEl?.getAttribute('datetime') ||
        timestampEl?.getAttribute('data-content') ||
        queryText(['time', '.jdgm-rev__timestamp', '.jdgm-rev-widg__date']);
      const parsedTime = rawDate ? Date.parse(rawDate) : NaN;

      return {
        index,
        reviewId:
          reviewEl.getAttribute('data-review-id') ||
          reviewEl.dataset?.reviewId ||
          '',
        title: queryText([
          '.jdgm-rev__title',
          '.jdgm-rev-widg__title',
          '[class*="review-title"]'
        ]),
        body: queryText([
          '.jdgm-rev__body',
          '.jdgm-rev__content-body',
          '.jdgm-rev-widg__body',
          'p'
        ]),
        author: queryText([
          '.jdgm-rev__author',
          '.jdgm-rev-widg__author',
          '.jdgm-reviewer-name'
        ]),
        productName: queryText([
          '.jdgm-rev__prod-link',
          '.jdgm-rev__product',
          '.jdgm-product-name',
          '.jdgm-rev__prod-info-wrapper a'
        ]),
        verified: queryText([
          '.jdgm-rev__buyer-badge',
          '.jdgm-rev__verified-badge'
        ]),
        stars: this.extractJudgeMeStars(reviewEl),
        timestamp: Number.isNaN(parsedTime) ? null : parsedTime
      };
    }).filter((review) => review && (review.body || review.title || review.author));
  }

  async fetchJudgeMeReviewsFromApi(sourceRoot) {
    const config = this.extractJudgeMeSourceConfig(sourceRoot);
    const params = this.buildJudgeMeApiQuery(config);
    if (!config || !params || typeof fetch !== 'function' || typeof jdgm === 'undefined' || !jdgm.API_HOST) {
      return [];
    }

    const requestSignature = params.toString();
    if (this.judgeMeApiReviewCache?.signature === requestSignature) {
      return this.judgeMeApiReviewCache.reviews || [];
    }

    const baseUrl = jdgm.API_HOST.endsWith('/') ? jdgm.API_HOST : `${jdgm.API_HOST}/`;
    const response = await fetch(`${baseUrl}reviews/all_reviews_js_based?${requestSignature}`, {
      credentials: 'omit'
    });
    if (!response.ok) {
      throw new Error(`Judge.me API request failed with ${response.status}`);
    }

    const payload = await response.json();
    const reviews = this.parseJudgeMeApiReviewsFromHtml(payload?.html || '');
    this.judgeMeApiReviewCache = {
      signature: requestSignature,
      reviews
    };

    return reviews;
  }

  findConfiguredJudgeMeAppSource() {
    const appWrapperCandidates = Array.from(document.querySelectorAll(
      [
        '.m-app-section .shopify-app-block .jdgm-carousel-wrapper',
        '.m-app-section .shopify-app-block .jdgm-carousel',
        '.m-app-section .shopify-app-block .jdgm-carousel__wrapper',
        '.m-app-section .shopify-app-block .jdgm-widget[data-widget-name*="carousel"]',
        '.m-app-section .shopify-app-block .jdgm-widget[data-widget*="carousel"]',
        '.m-app-section .shopify-app-block [class*="jdgm"][class*="carousel"]',
        '.m-app-section .shopify-app-block .jdgm-widget'
      ].join(', ')
    )).filter((node) => !node.closest('.hero-search'));

    let bestSource = null;
    let bestScore = -1;

    appWrapperCandidates.forEach((node) => {
      const score = this.scoreJudgeMeReviewSource(node);
      if (score > bestScore) {
        bestScore = score;
        bestSource = node;
      }
    });

    return bestSource;
  }

  scoreJudgeMeReviewSource(sourceRoot) {
    if (!sourceRoot) return -1;

    const reviews = this.collectJudgeMeReviews(sourceRoot);
    if (!reviews.length) return -1;

    const sourceText = [
      sourceRoot.className || '',
      sourceRoot.getAttribute('data-widget-name') || '',
      sourceRoot.getAttribute('data-widget') || '',
      sourceRoot.closest('.m-app-section') ? 'app-section' : ''
    ].join(' ');
    const hasTimestamps = reviews.some((review) => review.timestamp);
    const numericIds = reviews.filter((review) => this.parseJudgeMeReviewId(review.reviewId) !== null).length;
    const storeReviewBoost = /carousel|cards|store/i.test(sourceText) ? 3 : 0;
    const appWrapperBoost = sourceRoot.closest('.m-app-section .shopify-app-block') ? 20 : 0;

    return (reviews.length * 10) + (hasTimestamps ? 5 : 0) + numericIds + storeReviewBoost + appWrapperBoost;
  }

  collectJudgeMeReviews(sourceRoot) {
    if (!sourceRoot) return [];

    const reviews = Array.from(sourceRoot.querySelectorAll(HeroSearch.JUDGE_ME_REVIEW_SELECTORS.join(', ')))
      .filter((reviewEl) => !reviewEl.closest('.hero-search'))
      .map((reviewEl, index) => this.extractJudgeMeReview(reviewEl, index))
      .filter((review) => review && (review.body || review.title || review.author));

    if (!reviews.length) {
      const heuristicReviews = this.collectJudgeMeReviewsHeuristically(sourceRoot);
      if (heuristicReviews.length) {
        return heuristicReviews;
      }
    }

    if (!reviews.length) return [];

    const hasIndexedRealCards = reviews.some((review) => review.carouselIndex !== null && !review.isClone);
    if (hasIndexedRealCards) {
      return reviews
        .filter((review) => review.carouselIndex !== null && !review.isClone)
        .sort((a, b) => {
          if (a.carouselIndex === b.carouselIndex) return a.index - b.index;
          return a.carouselIndex - b.carouselIndex;
        });
    }

    const uniqueReviews = [];
    const seenReviews = new Set();

    reviews.forEach((review) => {
      const identity = this.getJudgeMeReviewIdentity(review);
      if (!identity || seenReviews.has(identity)) return;
      seenReviews.add(identity);
      uniqueReviews.push(review);
    });

    const sortableReviews = uniqueReviews.length ? uniqueReviews : reviews;
    const hasParseableDates = sortableReviews.some((review) => review.timestamp);
    if (hasParseableDates) {
      return sortableReviews.sort((a, b) => {
        const aTime = a.timestamp || 0;
        const bTime = b.timestamp || 0;
        if (aTime === bTime) {
          const aId = this.parseJudgeMeReviewId(a.reviewId);
          const bId = this.parseJudgeMeReviewId(b.reviewId);
          if (aId !== null && bId !== null && aId !== bId) return bId - aId;
          return b.index - a.index;
        }
        return bTime - aTime;
      });
    }

    const hasNumericReviewIds = sortableReviews.some((review) => this.parseJudgeMeReviewId(review.reviewId) !== null);
    if (hasNumericReviewIds) {
      return sortableReviews.sort((a, b) => {
        const aId = this.parseJudgeMeReviewId(a.reviewId);
        const bId = this.parseJudgeMeReviewId(b.reviewId);
        if (aId === null && bId === null) return b.index - a.index;
        if (aId === null) return 1;
        if (bId === null) return -1;
        if (aId === bId) return b.index - a.index;
        return bId - aId;
      });
    }

    return sortableReviews.sort((a, b) => b.index - a.index);
  }

  collectJudgeMeReviewsHeuristically(sourceRoot) {
    const candidateSelectors = [
      '.jdgm-widget article',
      '.jdgm-widget li',
      '.jdgm-widget .swiper-slide',
      '.jdgm-widget [role="listitem"]',
      '.jdgm-widget > div > div'
    ];

    const candidates = candidateSelectors.flatMap((selector) =>
      Array.from(sourceRoot.querySelectorAll(selector))
    );

    const uniqueCandidates = Array.from(new Set(candidates)).filter((candidate) => {
      if (!candidate || candidate.closest('.hero-search')) return false;

      const text = candidate.textContent?.replace(/\s+/g, ' ').trim() || '';
      if (text.length < 20) return false;

      const hasStars =
        candidate.querySelector('[aria-label*="star"], [data-score], .jdgm-star, .jdgm-star-rating, [class*="star"]') ||
        /★/.test(text);

      return Boolean(hasStars);
    });

    return uniqueCandidates
      .map((reviewEl, index) => this.extractJudgeMeReview(reviewEl, index))
      .filter((review) => review && (review.body || review.title || review.author));
  }

  extractJudgeMeReview(reviewEl, index) {
    if (!reviewEl) return null;

    const queryText = (selectors) => {
      for (const selector of selectors) {
        const match = reviewEl.querySelector(selector);
        if (!match) continue;

        const text = match.textContent?.replace(/\s+/g, ' ').trim();
        if (text) return text;
      }

      return '';
    };

    const title = queryText([
      '.jdgm-rev__title',
      '.jdgm-rev-widg__title',
      '[class*="review-title"]',
      '[class*="card-title"]',
      'h3',
      'h4',
      '[class*="title"]'
    ]);
    const body = queryText([
      '.jdgm-rev__body',
      '.jdgm-rev-widg__body',
      '.jdgm-rev__content-body',
      '[class*="review-body"]',
      '[class*="content"]',
      '[class*="excerpt"]',
      'blockquote',
      'p',
      '[class*="body"]'
    ]);
    const author = queryText([
      '.jdgm-rev__author',
      '.jdgm-rev-widg__author',
      '.jdgm-rev__author-wrapper',
      '[class*="reviewer"]',
      '[class*="author-name"]',
      '[class*="user-name"]',
      '[class*="author"]'
    ]);
    const verified = queryText([
      '.jdgm-rev__verified-badge',
      '.jdgm-rev__buyer-badge',
      '[class*="verified-badge"]',
      '[class*="verified"]',
      '[class*="buyer-badge"]',
      '[class*="verified"]'
    ]);
    const productName = queryText([
      '.jdgm-rev__product',
      '.jdgm-carousel-item__product',
      '.jdgm-rev-widg__product',
      '[class*="product-title"]',
      '[class*="product-name"]',
      '[class*="review-product"]'
    ]);
    const dateText = queryText([
      'time',
      '.jdgm-rev__timestamp',
      '.jdgm-rev-widg__date',
      '[class*="review-date"]',
      '[class*="date"]'
    ]);

    const timeEl = reviewEl.querySelector('time');
    const rawDate = timeEl?.getAttribute('datetime') || dateText;
    const parsedTime = rawDate ? Date.parse(rawDate) : NaN;
    const reviewId =
      reviewEl.getAttribute('data-review-id') ||
      reviewEl.dataset?.reviewId ||
      reviewEl.dataset?.id ||
      reviewEl.querySelector('[data-review-id]')?.getAttribute('data-review-id') ||
      reviewEl.querySelector('[data-id]')?.getAttribute('data-id') ||
      reviewEl.getAttribute('data-id') ||
      reviewEl.id ||
      '';
    const rawCarouselIndex = reviewEl.getAttribute('data-card-index');
    const parsedCarouselIndex = rawCarouselIndex !== null ? Number(rawCarouselIndex) : NaN;
    const cloneType = reviewEl.getAttribute('data-clone-type') || '';

    return {
      index,
      reviewId,
      carouselIndex: Number.isNaN(parsedCarouselIndex) ? null : parsedCarouselIndex,
      isClone: Boolean(cloneType),
      title,
      body,
      author,
      productName,
      verified,
      stars: this.extractJudgeMeStars(reviewEl),
      timestamp: Number.isNaN(parsedTime) ? null : parsedTime
    };
  }

  parseJudgeMeReviewId(reviewId) {
    if (reviewId === null || reviewId === undefined) return null;

    const numericId = String(reviewId).match(/\d+/);
    if (!numericId) return null;

    const parsed = Number(numericId[0]);
    return Number.isFinite(parsed) ? parsed : null;
  }

  getJudgeMeReviewIdentity(review) {
    if (!review) return '';

    const numericId = this.parseJudgeMeReviewId(review.reviewId);
    if (numericId !== null) {
      return `id:${numericId}`;
    }

    const signatureParts = [
      review.author,
      review.title,
      review.body,
      review.timestamp || '',
      review.stars
    ];

    return `content:${signatureParts.map((part) => (part || '').toString().trim().toLowerCase()).join('|')}`;
  }

  extractJudgeMeStars(reviewEl) {
    const ratingEl = reviewEl.querySelector('[aria-label*="star"], [data-score], .jdgm-rev__rating, .jdgm-star-rating, [class*="rating"]');
    const explicitScore = ratingEl?.getAttribute('data-score') || reviewEl.getAttribute('data-score');

    if (explicitScore && !Number.isNaN(Number(explicitScore))) {
      return Math.max(0, Math.min(5, Math.round(Number(explicitScore))));
    }

    const ariaLabel = ratingEl?.getAttribute('aria-label') || reviewEl.getAttribute('aria-label') || '';
    const ariaMatch = ariaLabel.match(/([0-5](?:\.[0-9])?)/);
    if (ariaMatch) {
      return Math.max(0, Math.min(5, Math.round(Number(ariaMatch[1]))));
    }

    const starNodes = reviewEl.querySelectorAll('.jdgm-star, .jdgm-rev__rating .jdgm-star');
    if (starNodes.length) {
      const activeStars = Array.from(starNodes).filter((star) => {
        const className = typeof star.className === 'string' ? star.className : '';
        return /on|full|active|filled/.test(className);
      }).length;

      if (activeStars) return Math.max(0, Math.min(5, activeStars));
    }

    return 5;
  }

  async syncJudgeMeReviews() {
    if (!this.reviewSlide || !this.reviewCards) return;

    const sourceRoot = this.judgeMeSourceRoot || this.findJudgeMeReviewSource();
    if (!sourceRoot) {
      if (this.lastJudgeMeReviews.length) {
        this.renderJudgeMeMeta(this.lastJudgeMeMeta);
        this.renderJudgeMeReviews(this.lastJudgeMeReviews);
        this.setJudgeMeSlideState('ready');
        return;
      }
      this.setJudgeMeSlideState('loading');
      return;
    }

    this.judgeMeSourceRoot = sourceRoot;
    const sourceMeta = this.extractJudgeMeMeta(sourceRoot);
    let latestReviews = this.collectJudgeMeReviews(sourceRoot).slice(0, 3);

    if (!latestReviews.length) {
      try {
        latestReviews = (await this.fetchJudgeMeReviewsFromApi(sourceRoot)).slice(0, 3);
      } catch (error) {
        latestReviews = [];
      }
    }

    if (latestReviews.length < 1) {
      if (this.lastJudgeMeReviews.length) {
        this.renderJudgeMeMeta(this.lastJudgeMeMeta || sourceMeta);
        this.renderJudgeMeReviews(this.lastJudgeMeReviews);
        this.setJudgeMeSlideState('ready');
        return;
      }
      this.setJudgeMeSlideState('empty');
      return;
    }

    this.lastJudgeMeMeta = sourceMeta;
    this.lastJudgeMeReviews = latestReviews;
    this.renderJudgeMeMeta(sourceMeta);
    this.renderJudgeMeReviews(latestReviews);
    this.hideJudgeMeSourceWidget(sourceRoot);
    this.setJudgeMeSlideState('ready');
  }

  extractJudgeMeMeta(sourceRoot) {
    const sourceScope = sourceRoot?.closest('.shopify-app-block, .m-app-section, section') || sourceRoot;
    if (!sourceScope) {
      return { header: '', averageMarkup: '' };
    }

    const pickText = (selectors) => {
      for (const selector of selectors) {
        const matches = Array.from(sourceScope.querySelectorAll(selector)).filter((el) => {
          if (!el || el.closest('.hero-search')) return false;
          if (el.closest('.jdgm-carousel-item, .jdgm-carousel__item, .jdgm-rev, .jdgm-rev-widg__review, [data-review-id]')) return false;
          const text = el.textContent?.replace(/\s+/g, ' ').trim();
          return Boolean(text);
        });

        if (matches.length) {
          return matches[0].textContent.replace(/\s+/g, ' ').trim();
        }
      }

      return '';
    };

    const pickMarkup = (selectors) => {
      for (const selector of selectors) {
        const match = Array.from(sourceScope.querySelectorAll(selector)).find((el) => {
          if (!el || el.closest('.hero-search')) return false;
          if (el.closest('.jdgm-carousel-item, .jdgm-carousel__item, .jdgm-rev, .jdgm-rev-widg__review, [data-review-id]')) return false;
          return Boolean(el.textContent?.replace(/\s+/g, ' ').trim());
        });

        if (match) {
          return match.innerHTML.trim();
        }
      }

      return '';
    };

    return {
      header: pickText([
        '.jdgm-carousel-title',
        '.jdgm-carousel__title',
        '.jdgm-rev-widg__title',
        '.jdgm-widget-title',
        '[class*="carousel-title"]',
        '[class*="widget-title"]',
        '[class*="header"] h2',
        '[class*="header"] h3'
      ]),
      averageMarkup: pickMarkup([
        '.jdgm-carousel__ar',
        '.jdgm-carousel__avg-rating',
        '.jdgm-carousel__average-rating',
        '.jdgm-rev-widg__summary',
        '.jdgm-widget__summary',
        '.jdgm-all-reviews-rating-wrapper',
        '[class*="average-rating"]',
        '[class*="rating-summary"]',
        '[class*="rating-display"]'
      ])
    };
  }

  renderJudgeMeMeta(meta) {
    if (!this.reviewHeader || !this.reviewAverage) return;

    const normalizedMeta = meta || { header: '', averageMarkup: '' };
    const usesStaticHeader = this.reviewHeader.dataset.staticHeader === 'true';
    const headerText = usesStaticHeader
      ? (this.reviewHeader.textContent || '').trim()
      : (normalizedMeta.header || '');
    const averageMarkup = normalizedMeta.averageMarkup || '';

    if (!usesStaticHeader) {
      this.reviewHeader.textContent = headerText;
      this.reviewHeader.hidden = !headerText;
    } else {
      this.reviewHeader.hidden = !headerText;
    }

    if (averageMarkup) {
      this.reviewAverage.innerHTML = averageMarkup;
      this.reviewAverage.hidden = false;
    } else {
      this.reviewAverage.innerHTML = '';
      this.reviewAverage.hidden = true;
    }
  }

  renderJudgeMeReviews(reviews) {
    if (!this.reviewCards) return;

    const starMarkup = (stars) => {
      return Array.from({ length: 5 }, (_, index) => {
        const filled = index < stars;
        return `<span class="hero-search-review-card__star${filled ? ' is-filled' : ''}" aria-hidden="true">★</span>`;
      }).join('');
    };

    this.reviewCards.innerHTML = reviews.map((review, index) => `
      <article
        class="hero-search-review-card hero-search-review-card--${index + 1}"
        data-hero-judgeme-card
        tabindex="0"
        role="button"
        aria-label="Open all customer reviews"
      >
        <div class="hero-search-review-card__rating" aria-label="${review.stars} out of 5 stars">
          ${starMarkup(review.stars)}
        </div>
        ${review.author ? `<div class="hero-search-review-card__author">${escapeHtml(review.author)}</div>` : ''}
        ${review.body ? `<p class="hero-search-review-card__body">${escapeHtml(review.body)}</p>` : ''}
        ${(review.title || review.productName || review.verified) ? `
          <div class="hero-search-review-card__meta">
            ${review.title ? `<div class="hero-search-review-card__context">${HeroSearch.HERO_REVIEW_CONTEXT_ICON}<span>${escapeHtml(review.title)}</span></div>` : ''}
            ${!review.title && review.productName ? `<div class="hero-search-review-card__context">${HeroSearch.HERO_REVIEW_CONTEXT_ICON}<span>${escapeHtml(review.productName)}</span></div>` : ''}
            ${review.verified ? `<span class="hero-search-review-card__verified">${escapeHtml(review.verified)}</span>` : ''}
          </div>
        ` : ''}
      </article>
    `).join('');

    this.setupJudgeMeCardInteractions();
  }

  findJudgeMeModalTrigger() {
    const trustRoot = document.querySelector(
      [
        '.m-footer__judgeme-slot .m-footer__judgeme-trust-badge',
        '.m-footer__judgeme-trust-badge',
        '.shopify-section-group-footer-group.custom-liquid .m-footer__judgeme-trust-badge'
      ].join(', ')
    );

    if (!trustRoot) return null;

    return trustRoot.querySelector(
      [
        '.jdgm-trust-badge.jdgm-trust-badge--clickable',
        '.jdgm-trust-badge--clickable',
        '.jdgm-trust-badge',
        '.jdgm-trust-badge-widget .jdgm-trust-badge',
        'a',
        'button',
        '[role="button"]',
        '.jdgm-widget a',
        '.jdgm-widget button',
        '.jdgm-trust-badge-widget [tabindex]',
        '.jdgm-trust-badge-widget'
      ].join(', ')
    ) || trustRoot;
  }

  openJudgeMeModalFromHeroCard() {
    const trigger = this.findJudgeMeModalTrigger();
    if (!trigger) return;

    const clickTarget = trigger.matches?.('.jdgm-trust-badge-widget')
      ? trigger.querySelector('.jdgm-trust-badge')
      : trigger;

    const target = clickTarget || trigger;

    if (typeof target.focus === 'function') {
      target.focus({ preventScroll: true });
    }

    const eventSequence = ['pointerdown', 'mousedown', 'pointerup', 'mouseup', 'click'];

    eventSequence.forEach((eventName) => {
      const EventConstructor = eventName.startsWith('pointer') && typeof PointerEvent === 'function'
        ? PointerEvent
        : MouseEvent;

      target.dispatchEvent(new EventConstructor(eventName, {
        bubbles: true,
        cancelable: true,
        view: window
      }));
    });

    if (typeof target.click === 'function') {
      target.click();
    }
  }

  setupJudgeMeCardInteractions() {
    if (!this.reviewCards) return;

    const cards = this.reviewCards.querySelectorAll('[data-hero-judgeme-card]');
    if (!cards.length) return;
    const reviewStack = this.reviewCards.closest('.hero-search-review-stack');

    const setActiveCard = (card) => {
      if (!reviewStack || !card) return;
      const match = Array.from(card.classList).find((className) => className.startsWith('hero-search-review-card--'));
      reviewStack.dataset.activeReviewCard = match ? match.split('--')[1] : '';
    };

    const clearActiveCard = () => {
      if (!reviewStack) return;
      delete reviewStack.dataset.activeReviewCard;
    };

    const getNearestCardFromPointer = (clientX) => {
      if (!reviewStack || !cards.length) return null;

      const stackRect = reviewStack.getBoundingClientRect();
      const pointerX = clientX - stackRect.left;

      let nearestCard = cards[0];
      let nearestDistance = Number.POSITIVE_INFINITY;

      cards.forEach((card) => {
        const cardCenterX = card.offsetLeft + (card.offsetWidth / 2);
        const distance = Math.abs(pointerX - cardCenterX);

        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestCard = card;
        }
      });

      return nearestCard;
    };

    const suppressHoverUntilExit = () => {
      if (!reviewStack) return;
      reviewStack.dataset.jdgmHoverSuppressed = 'true';
    };

    const releaseHoverSuppression = () => {
      if (!reviewStack) return;
      delete reviewStack.dataset.jdgmHoverSuppressed;
    };

    if (reviewStack && reviewStack.dataset.jdgmHoverBound !== 'true') {
      reviewStack.dataset.jdgmHoverBound = 'true';
      reviewStack.addEventListener('pointermove', (event) => {
        if (event.pointerType && event.pointerType !== 'mouse') return;
        if (reviewStack.dataset.jdgmHoverSuppressed === 'true') return;

        const nearestCard = getNearestCardFromPointer(event.clientX);
        if (nearestCard) {
          setActiveCard(nearestCard);
        }
      });
      reviewStack.addEventListener('mouseleave', () => {
        clearActiveCard();
        releaseHoverSuppression();
      });
      reviewStack.addEventListener('focusout', (event) => {
        if (reviewStack.contains(event.relatedTarget)) return;
        clearActiveCard();
      });
    }

    cards.forEach((card) => {
      if (card.dataset.jdgmInteractiveBound === 'true') return;

      card.dataset.jdgmInteractiveBound = 'true';
      card.addEventListener('focus', () => {
        setActiveCard(card);
      });

      card.addEventListener('click', (event) => {
        clearActiveCard();
        suppressHoverUntilExit();
        if (typeof card.blur === 'function') {
          window.setTimeout(() => card.blur(), 0);
        }
        this.openJudgeMeModalFromHeroCard();
      });

      card.addEventListener('keydown', (event) => {
        if (event.key !== 'Enter' && event.key !== ' ') return;
        event.preventDefault();
        clearActiveCard();
        suppressHoverUntilExit();
        if (typeof card.blur === 'function') {
          window.setTimeout(() => card.blur(), 0);
        }
        this.openJudgeMeModalFromHeroCard();
      });
    });
  }

  setJudgeMeSlideState(state) {
    if (!this.reviewSlide) return;

    this.reviewSlide.dataset.reviewState = state;

    if (state === 'ready') {
      this.reviewSlide.hidden = false;

      if (!this.judgeMeSlideWasActivated) {
        this.judgeMeSlideWasActivated = true;
        this.initRichTextSlider();
      }

      return;
    }

    if ((state === 'loading' || state === 'empty') && !this.lastJudgeMeReviews.length) {
      this.reviewSlide.classList.remove('is-active');
      this.reviewSlide.setAttribute('aria-hidden', 'true');
      this.reviewSlide.hidden = true;
      if (this.judgeMeSlideWasActivated) {
        this.initRichTextSlider();
        this.judgeMeSlideWasActivated = false;
      }
    }
  }

  destroy() {
    this.isDestroyed = true;

    if (this.heroResponsiveRaf) {
      window.cancelAnimationFrame(this.heroResponsiveRaf);
      this.heroResponsiveRaf = null;
    }

    if (this.heroResponsiveTimer) {
      window.clearTimeout(this.heroResponsiveTimer);
      this.heroResponsiveTimer = null;
    }

    if (this.heroResponsiveLateTimer) {
      window.clearTimeout(this.heroResponsiveLateTimer);
      this.heroResponsiveLateTimer = null;
    }

    if (this.judgeMeSyncTimer) {
      window.clearTimeout(this.judgeMeSyncTimer);
      this.judgeMeSyncTimer = null;
    }

    this.heroResponsiveCleanupFns.forEach((cleanup) => cleanup());
    this.heroResponsiveCleanupFns = [];

    this.heroRuntimeCleanupFns.forEach((cleanup) => cleanup());
    this.heroRuntimeCleanupFns = [];

    if (this.richTextSlider && typeof this.richTextSlider.destroy === 'function') {
      this.richTextSlider.destroy();
    }

    if (this.judgeMeObserver) {
      this.judgeMeObserver.disconnect();
      this.judgeMeObserver = null;
    }

    if (this.judgeMeSourceObserver) {
      this.judgeMeSourceObserver.disconnect();
      this.judgeMeSourceObserver = null;
    }
  }

  hideJudgeMeSourceWidget(sourceRoot) {
    if (!sourceRoot || this.judgeMeSourceHidden) return;

    const hideTarget = sourceRoot.closest('.shopify-app-block, .jdgm-widget, section, div') || sourceRoot;
    if (!hideTarget || hideTarget.closest('.hero-search')) return;

    hideTarget.classList.add('hero-search-judgeme-source--visually-hidden');
    this.judgeMeSourceHidden = true;
  }

  // Add variant styling once during initialization
  addVariantStyles() {
    // Check if styles already exist
    if (document.getElementById('hero-search-variant-styles')) {
      return;
    }

    // Create style element
    const variantStyleEl = document.createElement('style');
    variantStyleEl.id = 'hero-search-variant-styles';

    variantStyleEl.textContent = `
      .collection-product-variant {
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        padding: 15px;
        cursor: pointer;
        transition: all 0.2s ease;
        display: flex;
        flex-direction: column;
        justify-content: space-between;
        background-color: #fff;
        height: 100%;
      }
      
      .variant-info {
        flex-grow: 1;
      }
    
      .variant-product-title {
        font-size: 0.85em;
        color: #666;
        margin-top: 4px;
      }
      
      .variants-list, .daypass-variants-list {
        padding: 15px 0;
      }
      
      .no-variants-message {
        padding: 20px;
        text-align: center;
        color: #666;
      }
    `;

    // Add the styles to the document
    document.head.appendChild(variantStyleEl);
  }

  setupColExplanationTooltip() {
    const tooltip = document.querySelector('.col-explanation-tooltip');
    const button = document.querySelector('.col-explanation-link');
    const transitionDuration = 650;

    if (!tooltip || !button) return; // Guard clause

    // Clean up any existing event listeners
    const newButton = button.cloneNode(true);
    button.parentNode.replaceChild(newButton, button);

    const addListener = (element, event, handler, options = {}) => {
      element.addEventListener(event, handler, options);
    };

    const openTooltip = () => {
      if (tooltip._hideTimeout) {
        clearTimeout(tooltip._hideTimeout);
        tooltip._hideTimeout = null;
      }
      // First set display block to ensure the element is in the DOM
      tooltip.style.display = 'block';
      // Force a reflow to ensure the display change is applied
      tooltip.offsetHeight;
      // Then add active class for transition
      tooltip.classList.add('active');
      document.body.classList.add('detail-panel-open');
      document.documentElement.classList.add('detail-panel-open');
    };

    const closeTooltip = () => {
      tooltip.classList.remove('active');
      // Only clear detail-panel-open if the actual detail panel isn't the
      // one keeping it set — this tooltip and the detail panel both toggle
      // the same shared class independently.
      if (!this.detailPanel || !this.detailPanel.classList.contains('active')) {
        document.body.classList.remove('detail-panel-open');
        document.documentElement.classList.remove('detail-panel-open');
      }
      if (tooltip._hideTimeout) {
        clearTimeout(tooltip._hideTimeout);
      }
      tooltip._hideTimeout = setTimeout(() => {
        if (!tooltip.classList.contains('active')) {
          tooltip.style.display = 'none';
        }
        tooltip._hideTimeout = null;
      }, transitionDuration);
    };

    const handleButtonClick = (event) => {
      event.preventDefault();
      event.stopPropagation();
      if (tooltip.classList.contains('active')) {
        closeTooltip();
      } else {
        openTooltip();
      }
    };

    const handleOutsideClick = (event) => {
      if (!tooltip.contains(event.target) && !button.contains(event.target)) {
        closeTooltip();
      }
    };

    const handleEscapeKey = (event) => {
      if (event.key === 'Escape') {
        closeTooltip();
      }
    };

    const handleOrientationChange = () => {
      if (tooltip.classList.contains('active')) {
        // Force reflow to handle orientation change
        tooltip.style.display = 'none';
        tooltip.offsetHeight;
        tooltip.style.display = 'block';
      }
    };

    addListener(newButton, 'click', handleButtonClick);
    addListener(document, 'click', handleOutsideClick);
    addListener(document, 'keydown', handleEscapeKey);
    addListener(window, 'orientationchange', handleOrientationChange);

    const closeButton = tooltip.querySelector('.tooltip-close-button');
    if (closeButton) {
      const newCloseButton = closeButton.cloneNode(true);
      closeButton.parentNode.replaceChild(newCloseButton, closeButton);
      addListener(newCloseButton, 'click', closeTooltip);
    }

    // Initial state
    tooltip.style.display = 'none';
    tooltip.classList.remove('active');
  }

  // Update Screen 2 country title and flag
  async updateScreen2CountryTitle() {
    if (!this.selectedProduct) return;

    // Update the screen 2 header text
    const screen2Title = document.querySelector('.hero-screen-2 .screen-title');
    const titleCountryLine = screen2Title?.querySelector('.title-country-line');

    if (titleCountryLine) {
      const countryNameSpan = titleCountryLine.querySelector('.m-product-title-country');
      const countryName = formatProductDisplayTitle(this.selectedProduct.title || '');

      // Update country name
      if (countryNameSpan) {
        countryNameSpan.textContent = getTranslatedCountryName(countryName);
      }

      // Ensure the line is visible
      titleCountryLine.style.display = 'flex';

      // Flag URL comes from window.countryFlagImages, inlined by Liquid from the
      // country-sync catalog — available synchronously, so it renders with the text
      // instead of popping in after a live product image request resolves.
      const imagePlaceholder = titleCountryLine.querySelector('.m-product-title-image-placeholder');
      const existingFlag = titleCountryLine.querySelector('.m-country-flag-icon');
      const flag = createCountryFlagImage(countryName, { eager: true });

      if (flag) {
        if (existingFlag) {
          existingFlag.replaceWith(flag);
        } else if (imagePlaceholder) {
          imagePlaceholder.replaceWith(flag);
        } else if (countryNameSpan) {
          titleCountryLine.insertBefore(flag, countryNameSpan);
        } else {
          titleCountryLine.appendChild(flag);
        }
      } else if (existingFlag) {
        existingFlag.remove();
      }
    }
  }

  setupScreenNavigation() {

    try {
      // Continue to plans screen (from search)
      // Re-query the button to ensure we have the latest reference
      this.continueToPlansBtn = document.querySelector('#continue-to-plan');

      if (this.continueToPlansBtn) {

        // Remove any existing event listeners to prevent duplicates
        const newContinueButton = this.continueToPlansBtn.cloneNode(true);
        this.continueToPlansBtn.parentNode.replaceChild(newContinueButton, this.continueToPlansBtn);
        this.continueToPlansBtn = newContinueButton;

        // Add new event listener
        this.continueToPlansBtn.addEventListener('click', (e) => {
          // If button is disabled, prevent action
          if (this.continueToPlansBtn.disabled) {
            e.preventDefault();
            e.stopPropagation();
            return;
          }

          if (this.selectedProduct) {
            // Update the screen 2 header text and show screen
            Promise.resolve(this.updateScreen2CountryTitle()).finally(() => {
              this.showScreen(2);
            });
          }
        });
      } else {
      }

      // Back to search screen (from plans)
      // Re-query the button to ensure we have the latest reference
      this.backToSearchBtn = document.querySelector('#back-to-search');

      if (this.backToSearchBtn) {
        // Remove any existing event listeners
        const newBackButton = this.backToSearchBtn.cloneNode(true);
        this.backToSearchBtn.parentNode.replaceChild(newBackButton, this.backToSearchBtn);
        this.backToSearchBtn = newBackButton;

        // Add new event listener
        this.backToSearchBtn.addEventListener('click', () => {

          // Reset plan selection UI
          const selectedItems = document.querySelectorAll('.collection-item.selected');
          selectedItems.forEach(item => {
            item.classList.remove('selected');
          });

          // Close detail panel if open
          if (this.detailPanel && this.detailPanel.classList.contains('active')) {
            // Hide backdrop (mobile only)
            if (this.detailPanelBackdrop) {
              this.detailPanelBackdrop.classList.remove('active');
              setTimeout(() => {
                this.detailPanelBackdrop.style.display = 'none';
              }, 300);
            }

            // Hide content immediately before removing active class
            if (this.detailPanelContent) {
              const tabContents = this.detailPanelContent.querySelectorAll('.detail-panel-tab-content');
              tabContents.forEach(content => {
                content.style.display = 'none';
              });
            }

            this.detailPanel.classList.remove('active');
            this.detailPanel.style.opacity = '0';

            // Reset content after animation completes
            setTimeout(() => {
              if (this.detailPanelContent) {
                this.detailPanelContent.innerHTML = '';
              }
            }, 300);
          }

          // Disable continue to days button
          if (this.continueToDaysBtn) {
            this.continueToDaysBtn.disabled = true;
          }

          // Show screen 1 first, then reset screen 2 title AFTER transition completes
          // so the user never sees the title-country-line disappear mid-animation
          this.showScreen(1);

          setTimeout(() => {
            const screen2Title = document.querySelector('.hero-screen-2 .screen-title');
            const titleCountryLine = screen2Title?.querySelector('.title-country-line');

            if (titleCountryLine) {
              titleCountryLine.style.display = 'none';

              // Reset the flag placeholder for next use
              const existingFlag = titleCountryLine.querySelector('.m-country-flag-icon');
              if (existingFlag) {
                const placeholder = document.createElement('span');
                placeholder.className = 'm-product-title-image-placeholder';
                existingFlag.replaceWith(placeholder);
              }
            }
          }, 500); // Match screen transition duration
        });
      }

      // Continue to days screen (from plans)
      if (this.continueToDaysBtn) {
        // Remove any existing event listeners
        const newContinueButton = this.continueToDaysBtn.cloneNode(true);
        this.continueToDaysBtn.parentNode.replaceChild(newContinueButton, this.continueToDaysBtn);
        this.continueToDaysBtn = newContinueButton;

        // Add new event listener
        this.continueToDaysBtn.addEventListener('click', () => {
          if (this.currentVariant) {
            // Make sure the slider and controls are enabled for screen 3
            this.enableControls();

            // Update the price display before showing screen 3
            this.updatePrice();

            // Show screen 3
            this.showScreen(3);
          } else {
            console.error('No current variant selected, cannot proceed to screen 3');
          }
        });
      }

      // Back to plans screen (from days)
      if (this.backToPlanBtn) {
        // Remove any existing event listeners
        const newBackButton = this.backToPlanBtn.cloneNode(true);
        this.backToPlanBtn.parentNode.replaceChild(newBackButton, this.backToPlanBtn);
        this.backToPlanBtn = newBackButton;

        // Add new event listener
        this.backToPlanBtn.addEventListener('click', () => {

          // Clear price cache to prevent showing old prices
          this.variantPricesCache.clear();

          // Cancel any pending price updates
          if (this.priceUpdateTimer) {
            clearTimeout(this.priceUpdateTimer);
            this.priceUpdateTimer = null;
          }
          if (this.priceUpdateAbortController) {
            this.priceUpdateAbortController.abort();
            this.priceUpdateAbortController = null;
          }

          // Reset days slider
          const thumb = this.daysSlider?.querySelector('.thumb');
          const thumbValue = this.daysSlider?.querySelector('.thumb-value');
          const progress = this.daysSlider?.querySelector('.slider-progress');

          if (thumb) thumb.style.left = '0px';
          if (thumbValue) thumbValue.textContent = '1';
          if (progress) {
            progress.style.width = '0px';
            progress.setAttribute('data-value', '1');
          }
          this.days = 1;

          // Reset quantity input
          if (this.quantityInput) {
            this.quantityInput.value = '1';
          }

          // Close detail panel if open
          if (this.detailPanel && this.detailPanel.classList.contains('active')) {
            // Hide backdrop (mobile only)
            if (this.detailPanelBackdrop) {
              this.detailPanelBackdrop.classList.remove('active');
              setTimeout(() => {
                this.detailPanelBackdrop.style.display = 'none';
              }, 300);
            }

            // Hide content immediately before removing active class
            if (this.detailPanelContent) {
              const tabContents = this.detailPanelContent.querySelectorAll('.detail-panel-tab-content');
              tabContents.forEach(content => {
                content.style.display = 'none';
              });
            }

            this.detailPanel.classList.remove('active');
            this.detailPanel.style.opacity = '0';

            // Reset content after animation completes
            setTimeout(() => {
              if (this.detailPanelContent) {
                this.detailPanelContent.innerHTML = '';
              }
            }, 300);
          }

          this.showScreen(2);
        });
      }

    } catch (error) {
      console.error('Error setting up screen navigation:', error);
    }
  }

  showScreen(screenNumber) {
    // Validate screen number
    if (screenNumber < 1 || screenNumber > 3) {
      console.error('Invalid screen number:', screenNumber);
      return;
    }

    // Don't do anything if we're already on this screen
    if (this.currentScreen === screenNumber) {
      return;
    }

    // Get screen elements
    const fromScreen = document.querySelector(`.hero-screen-${this.currentScreen}`);
    const toScreen = document.querySelector(`.hero-screen-${screenNumber}`);

    if (!fromScreen || !toScreen) {
      console.error('Screen elements not found:', { fromScreen, toScreen });
      return;
    }

    // Determine direction (forward or backward)
    const isForward = screenNumber > this.currentScreen;

    // Set initial positions
    fromScreen.style.transition = 'transform 0.5s ease-in-out, opacity 0.5s ease-in-out';
    toScreen.style.transition = 'transform 0.5s ease-in-out, opacity 0.5s ease-in-out';

    // Position the target screen off-screen initially
    toScreen.style.transform = isForward ? 'translateX(100%)' : 'translateX(-100%)';
    toScreen.style.opacity = '0';
    toScreen.style.visibility = 'visible';

    // Trigger reflow to ensure transition works
    void toScreen.offsetWidth;

    // Animate the screens
    fromScreen.style.transform = isForward ? 'translateX(-100%)' : 'translateX(100%)';
    fromScreen.style.opacity = '0';
    toScreen.style.transform = 'translateX(0)';
    toScreen.style.opacity = '1';

    // Update current screen
    this.currentScreen = screenNumber;

    // Special handling for each screen transition
    if (screenNumber === 1) {
      // Going back to search screen
      // Make sure search input is visible if no selection
      if (this.searchInput && !this.selectedProduct) {
        this.searchInput.classList.remove('has-selection');
        this.searchInput.focus();
      }

      // Reset any plan selection
      if (this.productCollection) {
        const selectedItems = this.productCollection.querySelectorAll('.collection-item.selected');
        selectedItems.forEach(item => item.classList.remove('selected'));
      }

      // Disable continue button if no selection
      if (this.continueToPlansBtn && !this.selectedProduct) {
        this.continueToPlansBtn.disabled = true;
      }
    }
    else if (screenNumber === 2) {
      // Going to plan selection screen
      // Load product collection based on selected product
      if (this.selectedProduct) {
        this.loadProductCollection(this.selectedProduct.handle);
      }

      // Reset detail panel state when going back to screen 2
      if (this.detailPanel) {
        this.reinitializeDetailPanel();
      }

      // Reset days slider
      const thumb = this.daysSlider?.querySelector('.thumb');
      const thumbValue = this.daysSlider?.querySelector('.thumb-value');
      const progress = this.daysSlider?.querySelector('.slider-progress');

      if (thumb) thumb.style.left = '0px';
      if (thumbValue) thumbValue.textContent = '1';
      if (progress) {
        progress.style.width = '0px';
        progress.setAttribute('data-value', '1');
      }
      this.days = 1;

      // Reset the has-value class to show placeholder again
      if (this.daysSlider) {
        this.daysSlider.classList.remove('has-value');
      }

      // Reset quantity input
      if (this.quantityInput) {
        this.quantityInput.value = '1';
      }
    }
    else if (screenNumber === 3) {
      // Going to days selection screen

      // Clear the price display immediately to prevent showing old prices
      const priceDisplay = document.querySelector('.hero-screen-3 .price-display');
      if (priceDisplay) {
        priceDisplay.innerHTML = '<div class="m-price"><div class="m-price__regular"><span class="m-price-item m-price-item--regular m:text-xl">Loading...</span></div></div>';
      }

      // PERFORMANCE: Do NOT clear variantPricesCache here.
      // The price cache is variant-ID keyed, so cached prices from the detail panel
      // remain valid on screen 3. Clearing it here forced an unnecessary re-fetch
      // every time the user reached screen 3, even for the same variant.
      // The cache is intentionally cleared in the back-to-plan handler instead,
      // where a new plan type may have been selected.
      // this.variantPricesCache.clear(); // <-- removed for performance

      // Cancel any pending price updates from previous interactions
      if (this.priceUpdateTimer) {
        clearTimeout(this.priceUpdateTimer);
        this.priceUpdateTimer = null;
      }
      if (this.priceUpdateAbortController) {
        this.priceUpdateAbortController.abort();
        this.priceUpdateAbortController = null;
      }

      // Clean up any remaining variants grid from screen 2
      const variantsGrid = document.querySelector('.variants-grid');
      if (variantsGrid) {
        variantsGrid.remove();
      }

      // Also clean up any detail panel content that might be overlapping
      if (this.detailPanel) {
        // Hide backdrop (mobile only)
        if (this.detailPanelBackdrop) {
          this.detailPanelBackdrop.classList.remove('active');
          this.detailPanelBackdrop.style.display = 'none';
        }

        this.detailPanel.style.display = 'none';
        this.detailPanel.classList.remove('active');
        if (this.detailPanelContent) {
          this.detailPanelContent.innerHTML = '';
        }
      }

      // Reset the has-value class to show placeholder again
      if (this.daysSlider) {
        this.daysSlider.classList.remove('has-value');
      }

      // Initialize the days slider and other components
      this.setupDaysSlider();
      this.setupQuantityInput();
      this.setupVariantSelector();
      this.setupAddToCart();

      // Update the price display
      this.updatePrice();
    }

    // Hide the previous screen after transition completes
    setTimeout(() => {
      fromScreen.style.visibility = 'hidden';

      // Emit a custom event for screen change
      document.dispatchEvent(new CustomEvent('hero-search:screen-change', {
        detail: { screen: screenNumber }
      }));
    }, 500);
  }

  async loadProductCollection(productHandle) {
    try {

      // Reset the detail panel using our new method
      this.reinitializeDetailPanel();

      // Clear current collection grid
      const collectionGrid = document.querySelector('.collection-grid');
      this.collectionGrid = collectionGrid;
      if (collectionGrid) {
        collectionGrid.innerHTML = '';
      }

      // Also clear the product collection area
      if (this.productCollection) {
        this.productCollection.innerHTML = '';
      }

      // Create the collection buttons
      const singleButton = document.createElement('div');
      singleButton.className = 'collection-item';
      singleButton.setAttribute('data-filter-type', 'single');
      singleButton.innerHTML = `
          <div class="collection-item-details">
          <h3 class="collection-item-title">Fixed Plan</h3>
          </div>
        `;

      const multiButton = document.createElement('div');
      // ============================================================================
      // REGIONAL PASS CONFIGURATION
      // ============================================================================
      // CURRENT SETUP (2024): Opens Daily Plan for selected single country
      // - data-filter-type: 'single-daily' 
      // - Opens detail panel with filterType='single' and auto-switches to Daily Plan tab
      //
      // TO RE-ENABLE TRUE REGIONAL PLANS IN THE FUTURE:
      // 1. Change data-filter-type back to 'multi'
      // 2. Update click handler below to use 'multi' instead of 'single-daily'
      // 3. The filtering logic for type='multi' is fully preserved in:
      //    - fetchProductsFromStorefrontAPI() (lines ~2000-2400)
      //    - findProductsWithFilters() (lines ~5053-5475)
      //    - applyFilter() (lines ~5692-5884)
      // 4. All regional plan filtering logic remains intact and functional
      // ============================================================================
      multiButton.className = 'collection-item';
      multiButton.setAttribute('data-filter-type', 'single-daily');
      multiButton.innerHTML = `
          <div class="collection-item-details">
          <h3 class="collection-item-title">Unlimited Plan</h3>
          </div>
        `;

      // Add buttons to the grid
      if (collectionGrid) {
        // Render buttons immediately to prevent empty screen delay
        collectionGrid.appendChild(singleButton);
        collectionGrid.appendChild(multiButton);

        const applyPlanButtonState = (button, isAvailable, unavailableTitle) => {
          button.classList.toggle('disabled', !isAvailable);
          if (isAvailable) {
            button.removeAttribute('title');
          } else {
            button.title = unavailableTitle;
          }
        };

        applyPlanButtonState(singleButton, false, 'Checking fixed plan availability');
        applyPlanButtonState(multiButton, false, 'Checking unlimited plan availability');

        // Check availability asynchronously without blocking the UI.
        // Use the selected-country prefetch first; the full catalog preload is
        // intentionally not awaited here because it can make screen 2 feel slow.
        (async () => {
          let availability = null;
          const selectedCountryTitle = this.selectedProduct?.title || '';

          // The selected-country plan fetch is the source of truth for the new
          // merged Shopify structure, so prefer it over the temporary hydration
          // placeholder availability object.
          if (this.selectedCountryPrefetchPromise && this.selectedProduct) {
            try {
              const { fixedProducts, dailyProducts } = await this.selectedCountryPrefetchPromise;
              availability = getPlanAvailabilityFromProducts([
                ...fixedProducts,
                ...dailyProducts
              ]);
            } catch (e) {
            }
          }

          // If screen 2 opens before the shared prefetch promise is ready or set,
          // fetch the selected country's merged-plan structure directly here so
          // button state always reflects the Plan option split.
          if (!availability && selectedCountryTitle) {
            try {
              const { fixedProducts, dailyProducts } = await this.prefetchSelectedCountryPlans(selectedCountryTitle);
              availability = getPlanAvailabilityFromProducts([
                ...fixedProducts,
                ...dailyProducts
              ]);
            } catch (e) {
            }
          }

          if (!availability && this.selectedProduct?.planAvailabilityResolved && this.selectedProduct?.availability) {
            availability = {
              fixed: Boolean(this.selectedProduct.availability.fixed),
              daily: Boolean(this.selectedProduct.availability.daily)
            };
          } else if (!availability && this.allProductsCache && this.selectedProduct) {
            const countryName = this.selectedProduct.title;
            // console.log(`Checking availability for: ${countryName}`);

            // Find all products for this country in our cache
            const productsForCountry = this.allProductsCache.filter((product) =>
              productMatchesCountryName(product, countryName)
            );

            if (productsForCountry.length > 0) {
              availability = getPlanAvailabilityFromProducts(productsForCountry);
            }
          }

          if (!availability) {
            availability = { fixed: false, daily: false };
          }

          if (this.selectedProduct?.title) {
            this.selectedProduct = {
              ...this.selectedProduct,
              availability,
              planAvailabilityResolved: true
            };
          }

          applyPlanButtonState(singleButton, availability.fixed, 'Fixed plans not available for this destination');
          applyPlanButtonState(multiButton, availability.daily, 'Unlimited plans not available for this destination');
        })();
      }

      // Set up click handlers for both buttons
      const setupButtonClickHandler = (button, filterType) => {
        button.addEventListener('click', async () => {
          // Don't do anything if the button is disabled
          if (button.classList.contains('disabled') || this.isOpeningDetailPanel) {
            return;
          }

          // Don't do anything if the button is already selected
          if (button.classList.contains('selected')) {
            return;
          }

          // Remove selected class from all buttons
          const allButtons = document.querySelectorAll('.collection-item');
          allButtons.forEach(btn => {
            btn.classList.remove('selected', 'is-loading');
            btn.removeAttribute('aria-busy');
          });

          // Add selected class to clicked button
          button.classList.add('selected');
          button.classList.add('is-loading');
          button.setAttribute('aria-busy', 'true');

          // Store the selected collection button name for error messages
          const buttonTitle = button.querySelector('.collection-item-title');
          this.selectedCollectionName = buttonTitle ? buttonTitle.textContent.trim() : 'Plan';

          // ============================================================================
          // BUTTON CLICK HANDLER - Direct filter loading (no tabs)
          // ============================================================================
          // Each button now directly loads its specific filter without tab switching
          // 'single' = Fixed Plan (top_up.True)
          // 'single-daily' = Daily Plan (top_up.False)
          // ============================================================================
          this.isOpeningDetailPanel = true;

          try {
            if (filterType === 'single-daily') {
              await this.showSimplifiedDetail('single', 'top_up.False');
            } else if (filterType === 'single') {
              await this.showSimplifiedDetail('single', 'top_up.True');
            } else {
              await this.showSimplifiedDetail(filterType, 'top_up.True');
            }

            if (this.continueToDaysBtn) {
              this.continueToDaysBtn.disabled = false;
            }
          } finally {
            this.isOpeningDetailPanel = false;
            allButtons.forEach(btn => {
              btn.classList.remove('is-loading');
              btn.removeAttribute('aria-busy');
            });
          }
        });
      };

      // Set up click handlers
      setupButtonClickHandler(singleButton, 'single');
      setupButtonClickHandler(multiButton, 'single-daily'); // Change to 'multi' to re-enable regional plans

      // ===== TRANSLATION INTEGRATION START =====

      // Translate buttons and content immediately
      setTimeout(() => {
        this.translateCollectionButtons();
        // Translate all data-i18n-key elements
        this.translateDetailPanelInfo();
        // Update popup content
        this.updatePopupContent();
      }, 100);

      // Listen for language changes via mutation observer on html lang attribute (Vertaalapp compatible)
      const langObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === 'attributes' && mutation.attributeName === 'lang') {
            const newLang = document.documentElement.lang.split('-')[0].toLowerCase();
            const prevLang = window.currentLocale || 'en';

            if (newLang !== prevLang) {
              // Update window.currentLocale
              window.currentLocale = newLang;

              // Retranslate collection buttons
              setTimeout(() => {
                this.translateCollectionButtons();
                // Translate info section items
                this.translateDetailPanelInfo();
                // Update popup content with new translations
                this.updatePopupContent();

                // Re-apply dynamic content for Screen 2 if a product is selected
                if (this.selectedProduct) {
                  this.updateScreen2CountryTitle();
                }
              }, 50);
            }
          }
        });
      });
      langObserver.observe(document.documentElement, { attributes: true });
      // ===== TRANSLATION INTEGRATION END =====

    } catch (error) {
      console.error('Error loading product collection:', error);
    }
  }

  // Helper method to create or reset loading bar in a container
  createOrResetLoadingBar() {
    // Removed — loading bar replaced by SVG spinner (dp-dots-loader).
    return null;
  }

  // Method to finish the loading process and complete the bar animation
  completeLoadingBar() {
    // Removed — loading bar replaced by SVG spinner.
  }

  resolveSelectedProductPageUrl() {
    const activeTabContent = this.detailPanelContent?.querySelector('.detail-panel-tab-content');
    const currentProduct =
      activeTabContent?.product ||
      activeTabContent?.selectedProduct ||
      this.selectedVariant?.productData ||
      this.selectedProduct;

    if (!currentProduct) return '';

    const activeTabFilter = activeTabContent?.getAttribute('data-filter') || '';
    const landingPlan = activeTabFilter === 'top_up.True'
      ? 'fixed'
      : activeTabFilter === 'top_up.False'
        ? 'unlimited'
        : '';

    const appendLandingPlanParam = (rawUrl) => {
      if (typeof rawUrl !== 'string' || !rawUrl.trim()) return '';

      try {
        const url = new URL(rawUrl.trim(), window.location.origin);
        if (landingPlan) {
          url.searchParams.set('hero_plan', landingPlan);
        }
        return url.pathname + url.search + url.hash;
      } catch (error) {
        return rawUrl.trim();
      }
    };

    if (typeof currentProduct.url === 'string' && currentProduct.url.trim()) {
      return appendLandingPlanParam(currentProduct.url);
    }

    if (typeof currentProduct.handle !== 'string' || !currentProduct.handle.trim()) {
      return '';
    }

    const rootPath = window.Shopify?.routes?.root || '/';
    const normalizedRoot = rootPath.endsWith('/') ? rootPath : `${rootPath}/`;
    return appendLandingPlanParam(`${normalizedRoot}products/${currentProduct.handle.trim()}`);
  }

  async showSimplifiedDetail(filterType, specificFilter = null) {
    const requestId = ++this.detailPanelRequestId;

    // Re-find detail panel elements if they're not available
    if (!this.detailPanel || !this.detailPanelContent) {
      this.detailPanel = document.querySelector('.detail-panel');
      this.detailPanelContent = document.querySelector('.detail-panel-content');
      this.detailPanelClose = document.querySelector('.detail-panel-close');
      this.detailPanelBackdrop = document.querySelector('.detail-panel-backdrop');
    }

    if (!this.detailPanelProductLink) {
      this.detailPanelProductLink = document.querySelector('.detail-panel-product-link');
    }

    // If still not found, we can't proceed
    if (!this.detailPanel || !this.detailPanelContent) {
      console.error('Detail panel elements not found, cannot show detail panel');
      return;
    }

    // Save current scroll position before preventing scroll
    this.savedScrollPosition = window.pageYOffset || document.documentElement.scrollTop;

    // Implement robust scroll prevention (like compatibility modal)
    this.preventBackgroundScroll();

    // Store the filter type for later reference
    this.activeFilterType = filterType;
    const filterToUse = specificFilter || 'top_up.True';
    const existingTabContent = this.detailPanelContent.querySelector('.detail-panel-tab-content');
    const existingFooter = this.detailPanelContent.querySelector('.detail-panel-footer');
    const reuseExistingShell = this.detailPanel.classList.contains('active') && existingTabContent && existingFooter;

    // Clear previous content only when opening a fresh detail panel shell.
    if (!reuseExistingShell) {
      this.detailPanelContent.innerHTML = '';
    }

    // PERFORMANCE OPTIMIZATION: Country-aware cache invalidation.
    // Only clear tabData when the selected country changes. This is safe because
    // the cache key in applyFilter uses the full filter string (e.g. "top_up.True"),
    // and we additionally guard with the country key below. When the user re-opens
    // the panel for the same country (e.g. toggling Fixed/Daily), results are
    // served from memory with zero API calls and zero loading spinner.
    const _currentCountryKey = this.selectedProduct?.title || '__none__';
    if (this._lastDetailPanelCountry !== _currentCountryKey) {
      // Country changed — discard stale results and record the new country
      this.tabData = {};
      this._lastDetailPanelCountry = _currentCountryKey;
    } else {
      // Same country — preserve tabData so we can serve from cache immediately
      if (!this.tabData) this.tabData = {};
    }

    // Track loading start time
    this.loadingStartTime = Date.now();

    const warmupPromises = [];
    // Do NOT add preloadPromise here — it is the full background catalog fetch
    // and has nothing to do with rendering the drawer. Adding it causes the drawer
    // to block on an unrelated slow background operation.
    // selectedCountryPrefetchPromise is already awaited below before drawer opens,
    // but keep it here as a safety fallback for edge cases.
    if (this.selectedCountryPrefetchPromise) {
      warmupPromises.push(this.selectedCountryPrefetchPromise);
    }

    // The button is already in its loading state when we get here.
    // Wait for the selected-country prefetch before opening the drawer so
    // the first open can render from warm country data instead of a spinner.
    if (this.selectedCountryPrefetchPromise) {
      await this.selectedCountryPrefetchPromise.catch(() => {});
    }

    const selectedCountryTopUpValue = filterToUse.includes('.') ? filterToUse.split('.')[1] : '';
    const hasWarmSelectedCountryPlanData = filterType === 'single' &&
      !!this.getSelectedCountryPlanCacheEntry(this.selectedProduct?.title, selectedCountryTopUpValue);

    // Create and add loading bar using our helper method - start it immediately
    if (!reuseExistingShell && !hasWarmSelectedCountryPlanData) {
      // Add hidden loading indicator (for compatibility with existing code)
      this.detailPanelContent.innerHTML = '<div class="loading" aria-hidden="true"></div>';
    }

    // Position the panel before making it visible
    this.updateDetailPanelPosition();

    // Make sure panel is in the correct position before showing it
    if (!reuseExistingShell) {
      this.detailPanel.style.opacity = '0';
      // Must be flex, not block — the panel is a flex column (header / tab-content / footer).
      // display:block here was breaking the entire flex layout, collapsing tab content to 0.
      this.detailPanel.style.display = 'flex';
    }

    // Force a reflow to ensure position is applied before showing the panel
    if (!reuseExistingShell) {
      void this.detailPanel.offsetHeight;
    }

    // Ensure header and tabs are properly positioned
    const header = this.detailPanel.querySelector('.detail-panel-header');
    const tabsWrapper = this.detailPanel.querySelector('.detail-panel-tabs-wrapper');

    if (!reuseExistingShell && header && tabsWrapper) {
      // Reset any previous inline styles
      header.style.position = '';
      tabsWrapper.style.position = '';

      // Force a reflow to ensure styles are applied
      void header.offsetHeight;
      void tabsWrapper.offsetHeight;

      // Apply sticky positioning
      header.style.position = 'sticky';
      tabsWrapper.style.position = 'sticky';
    }

    // Show backdrop (mobile only)
    if (!reuseExistingShell && this.detailPanelBackdrop) {
      this.detailPanelBackdrop.style.display = 'block';
      // Force reflow
      void this.detailPanelBackdrop.offsetHeight;
      this.detailPanelBackdrop.classList.add('active');
    }

    // Now make it visible with the correct position
    if (!reuseExistingShell) {
      this.detailPanel.style.opacity = '1';
      this.detailPanel.style.visibility = 'visible';
      this.detailPanel.classList.add('active');
    }

    // Add class to body to lower header/footer z-index
    if (!reuseExistingShell) {
      document.body.classList.add('detail-panel-open');
      document.documentElement.classList.add('detail-panel-open');
    }

    // Tab content layout is owned by CSS flex — no JS measurement needed here.

    // Set up backdrop click to close (mobile only)
    if (!reuseExistingShell && this.detailPanelBackdrop) {
      const newBackdrop = this.detailPanelBackdrop.cloneNode(true);
      this.detailPanelBackdrop.parentNode.replaceChild(newBackdrop, this.detailPanelBackdrop);
      this.detailPanelBackdrop = newBackdrop;

      this.detailPanelBackdrop.addEventListener('click', () => {
        // Trigger the close button click
        if (this.detailPanelClose) {
          this.detailPanelClose.click();
        }
      });
    }

    if (!reuseExistingShell && this.detailPanelProductLink) {
      const newProductLinkButton = this.detailPanelProductLink.cloneNode(true);
      this.detailPanelProductLink.parentNode.replaceChild(newProductLinkButton, this.detailPanelProductLink);
      this.detailPanelProductLink = newProductLinkButton;

      this.detailPanelProductLink.addEventListener('click', () => {
        const productPageUrl = this.resolveSelectedProductPageUrl();
        if (!productPageUrl) return;
        window.location.href = productPageUrl;
      });
    }

    // Set up the close button
    if (!reuseExistingShell && this.detailPanelClose) {
      const newCloseButton = this.detailPanelClose.cloneNode(true);
      this.detailPanelClose.parentNode.replaceChild(newCloseButton, this.detailPanelClose);
      this.detailPanelClose = newCloseButton;

      this.detailPanelClose.addEventListener('click', () => {
        const closeAnimationDuration = 300;

        // Hide backdrop (mobile only)
        if (this.detailPanelBackdrop) {
          this.detailPanelBackdrop.classList.remove('active');
          setTimeout(() => {
            this.detailPanelBackdrop.style.display = 'none';
          }, closeAnimationDuration);
        }

        // Hide the entire detail panel
        if (this.detailPanel) {
          // Hide content immediately before removing active class
          if (this.detailPanelContent) {
            const tabContents = this.detailPanelContent.querySelectorAll('.detail-panel-tab-content');
            tabContents.forEach(content => {
              content.style.display = 'none';
            });
          }

          // Hide the product selector if visible
          const selectorWrapper = document.querySelector('.product-selector-wrapper');
          if (selectorWrapper) {
            selectorWrapper.style.display = 'none';
          }

          // Close the detail panel completely
          this.detailPanel.style.opacity = '0';
          this.detailPanel.style.visibility = 'hidden';
          setTimeout(() => {
            this.detailPanel.style.display = 'none';
            this.detailPanel.classList.remove('active');

            // Reset content
            if (this.detailPanelContent) {
              this.detailPanelContent.innerHTML = '';
            }

            // Reset any selected product/variant state
            this.selectedVariant = null;
            this.restoreBackgroundScroll();
          }, closeAnimationDuration);
        }

        // Unselect buttons using the helper method
        this.unselectCollectionButtons();
      });
    }

    try {
      // Set a fixed title
      const titleText = this.getDetailPanelText('choose_your_pack_detail');

      // PERFORMANCE: Check if we already have cached results for this filter.
      // If the tab cache is warm (same country, panel opened before), we skip the
      // loading spinner entirely so the panel appears to render instantly.
      const _hasCachedResults = !!(
        (this.tabData && this.tabData[filterToUse] &&
          typeof this.tabData[filterToUse].products !== 'undefined') ||
        hasWarmSelectedCountryPlanData
      );

      // Create HTML structure for the panel WITHOUT tabs - direct content only
      // SHOW THIS IMMEDIATELY - don't wait for data loading
      const panelHTML = `
        <div class="detail-panel-header" style="text-align: center;">
          <h2 class="collection-detail-title" style="font-size: 1.25rem; font-weight: 600; text-align: center;">
            ${titleText}
          </h2>
        </div>
        
          <!-- Direct content without tabs -->
          <div class="detail-panel-tab-content active" data-filter="${filterToUse}">

            <div class="detail-panel-svg-loader" style="${_hasCachedResults ? 'display:none !important;' : ''}">
              <div class="dp-dots-loader">
                <span class="dp-dot"></span>
                <span class="dp-dot"></span>
                <span class="dp-dot"></span>
              </div>
            </div>
          </div>
        </div>
        
        <div class="detail-panel-footer">
          <div class="detail-panel-actions">
            <button class="continue-with-variant" disabled>${this.getDetailPanelText('continue')}</button>
          </div>
        </div>
      `;

      if (!reuseExistingShell) {
        // Replace loading indicator with actual content IMMEDIATELY
        this.detailPanelContent.innerHTML = panelHTML;
      } else {
        existingTabContent.setAttribute('data-filter', filterToUse);
        existingTabContent.innerHTML = _hasCachedResults ? '' : '<div class="loading" aria-hidden="true"></div>';
      }

      // Note: All text in detail panel is now translated via getDetailPanelText()





      // Apply a data attribute to mark what type of panel this is
      this.detailPanelContent.setAttribute('data-filter-type', filterType);

      // Get references to the elements
      const tabContent = this.detailPanelContent.querySelector('.detail-panel-tab-content');
      const continueBtn = this.detailPanelContent.querySelector('.continue-with-variant');

      // Add styling for the continue button and footer to ensure proper display
      const styleEl = document.createElement('style');
      styleEl.textContent = `
        .detail-panel-actions {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 2rem;
          width: 100%;
          margin: 0 auto;
        }

        .continue-with-variant {
          width: 100%;
          max-width: 200px;
          padding: 10px 20px;
          border: none;
          border-radius: 1.5rem;
          background-color: #ccc;
          color: white;
          opacity: 1;
          cursor: not-allowed;
          margin: 0 auto;
          font-size: 1rem;
          transition: background-color 0.2s ease;
          font-weight:500;
        }
        
      `;

      // PERFORMANCE: Only inject the dynamic style block once — not on every panel open.
      // Repeatedly appending identical <style> blocks grows the DOM unnecessarily
      // and can cause micro-pauses during style recalculation on mobile devices.
      if (!document.getElementById('detail-panel-dynamic-styles')) {
        styleEl.id = 'detail-panel-dynamic-styles';
        document.head.appendChild(styleEl);
      }

      // Set up continue button click handler
      if (!reuseExistingShell && continueBtn) {
        continueBtn.addEventListener('click', () => {

          // Ensure the current variant is set from the selected variant
          if (this.selectedVariant) {
            this.currentVariant = this.selectedVariant;

            // DON'T clear price cache - we want to reuse the already-loaded price!
            // The price was already fetched in the detail panel, so keep it

            // Cancel any pending price updates
            if (this.priceUpdateTimer) {
              clearTimeout(this.priceUpdateTimer);
              this.priceUpdateTimer = null;
            }
            if (this.priceUpdateAbortController) {
              this.priceUpdateAbortController.abort();
              this.priceUpdateAbortController = null;
            }

            const closeAnimationDuration = 300;

            // Close the detail panel
            if (this.detailPanel) {
              // Hide backdrop (mobile only)
              if (this.detailPanelBackdrop) {
                this.detailPanelBackdrop.classList.remove('active');
                setTimeout(() => {
                  this.detailPanelBackdrop.style.display = 'none';
                }, closeAnimationDuration);
              }

              this.detailPanel.classList.remove('active');
              this.detailPanel.style.opacity = '0';
              this.detailPanel.style.visibility = 'hidden';

              // Make sure the slider and controls are enabled for screen 3
              this.enableControls();

              // Update the price display before showing screen 3
              // This will now use cached price data for instant display
              this.updatePrice();

              // Navigate directly to screen 3
              this.showScreen(3);
              setTimeout(() => {
                this.restoreBackgroundScroll();
              }, closeAnimationDuration);
            }
          }
        });
      }

      // Load content directly without tabs - much faster!
      if (tabContent) {
        this.selectedVariant = null;
        this.currentVariant = null;

        if (continueBtn) {
          continueBtn.disabled = true;
          continueBtn.style.backgroundColor = '#ccc';
          continueBtn.style.cursor = 'not-allowed';
        }

        // Clear any existing product selectors
        const existingSelector = document.querySelector('.product-selector-wrapper');
        if (existingSelector) {
          existingSelector.remove();
        }

        // Get the filter from the tab content
        const filter = tabContent.getAttribute('data-filter');

        // Set currentTabType based on the filter for days slider logic
        // top_up.False = Daily Plans (shows days slider)
        // top_up.True = Fixed Plans (no days slider)
        this.currentTabType = filter === 'top_up.False' ? 'Daily Plans' : 'Fixed Plans';

        if (warmupPromises.length > 0) {
          await Promise.allSettled(warmupPromises);
        }

        if (requestId !== this.detailPanelRequestId) {
          return;
        }

        // Load products directly using applyFilter
        await this.applyFilter(filterType, filter, tabContent);
      }

    } catch (error) {
      console.error('Error showing simplified detail:', error);
      if (this.detailPanelContent) {
        // Use the stored collection button name (e.g., "Fixed Plan" or "Daily Plan")
        const collectionName = this.selectedCollectionName || 'plan';
        this.detailPanelContent.innerHTML = `
          <div class="error-message">
            <p>${collectionName} aren’t available for this destination yet</p>
          </div>
        `;
      }
    }
  }

  // ============================================================================
  // REMOVED METHOD: showSimplifiedDetailWithTab (2024)
  // ============================================================================
  // This method was removed as part of the tab removal optimization.
  // The detail panel now loads content directly without tabs, eliminating the
  // need for tab-switching logic. Each button passes a specific filter to
  // showSimplifiedDetail() which loads that content immediately.
  // ============================================================================

  // Helper method to fetch variants for a specific product using AJAX (same as product pages)
  async fetchProductVariants(productId, productHandle) {

    try {
      // Check if we already have product data in window._themeProducts (same as product pages)
      if (window._themeProducts && window._themeProducts[productId]) {
        const productData = window._themeProducts[productId];
        return productData.variants || [];
      }

      // Fallback: fetch product data via AJAX (same approach as product pages)
      if (!productHandle) {
        return [];
      }

      const productUrl = `/products/${productHandle}`;
      const requestUrl = `${productUrl}?section_id=main-product`;

      const response = await fetch(requestUrl);
      if (!response.ok) {
        throw new Error(`Failed to fetch product data: ${response.status}`);
      }

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      // Extract product data from the rendered HTML (same as product pages)
      const productScript = doc.querySelector('script[data-product-json], script:contains("window._themeProducts")');

      if (productScript) {
        // Execute the script to get product data
        try {
          // Create a temporary script element to extract the product data
          const tempScript = document.createElement('script');
          tempScript.textContent = productScript.textContent;

          // Create a temporary container to safely execute and extract data
          const originalProducts = window._themeProducts;
          window._themeProducts = window._themeProducts || {};

          document.head.appendChild(tempScript);
          document.head.removeChild(tempScript);

          // Get the product data that was just added
          const productData = window._themeProducts[productId];
          if (productData && productData.variants) {
            return productData.variants;
          }
        } catch (error) {
          console.error('Error executing product script:', error);
        }
      }

      // Final fallback: try to find and extract variant data from any JSON script tags
      const jsonScripts = doc.querySelectorAll('script[type="application/json"]');
      for (const script of jsonScripts) {
        try {
          const data = JSON.parse(script.textContent);
          if (data.variants || (Array.isArray(data) && data[0]?.variants)) {
            const variants = data.variants || data[0].variants;
            return variants;
          }
        } catch (e) {
          // Continue to next script
        }
      }

      return [];

    } catch (error) {
      console.error('Error fetching product variants:', error);
      return [];
    }
  }

  getHeroProductId(product) {
    const rawId = product?.id;
    if (!rawId) return '';
    return String(rawId).split('/').pop();
  }

  getHeroVariantCount(product) {
    const variantsCount = product?.variantsCount;
    if (typeof variantsCount === 'number') return variantsCount;
    if (typeof variantsCount?.count === 'number') return variantsCount.count;
    return Number(variantsCount?.count || product?.variants_count || product?.totalVariants || 0);
  }

  buildHeroVariantFromStorefrontNode(node, productHandle) {
    return {
      id: String(node.id || '').split('/').pop(),
      title: node.title,
      available: Boolean(node.availableForSale),
      price: parseFloat(node.price?.amount || 0),
      currencyCode: node.price?.currencyCode || window.currentCurrency || 'EUR',
      compare_at_price: node.compareAtPrice ? parseFloat(node.compareAtPrice.amount || 0) : null,
      selectedOptions: node.selectedOptions || [],
      handle: productHandle
    };
  }

  mergeFetchedHeroVariantsIntoProduct(product, variants) {
    if (!product || !Array.isArray(product.variants) || !Array.isArray(variants) || !variants.length) return;

    const existingVariantIds = new Set(product.variants.map((variant) => String(variant.id)));

    variants.forEach((variant) => {
      if (!variant?.id || existingVariantIds.has(String(variant.id))) return;

      product.variants.push(variant);
      existingVariantIds.add(String(variant.id));
    });
  }

  getHeroSearchContextCountry(options = {}) {
    if (options.country) return options.country;

    if (window.currentCurrency) {
      const currencyCountry = this.getCountryContext(window.currentCurrency);
      if (currencyCountry) return currencyCountry;
    }

    return window.currentCountry || 'US';
  }

  getHeroVisibleVariantCache(product, options = {}) {
    const productId = this.getHeroProductId(product);
    const contextCountry = this.getHeroSearchContextCountry(options);
    const cacheKey = `${productId || product?.handle}:${contextCountry}`;

    window.__heroSearchVariantCache = window.__heroSearchVariantCache || {};
    window.__heroSearchVariantCache[cacheKey] = window.__heroSearchVariantCache[cacheKey] || {
      promise: null,
      resolved: null,
      visiblePromise: null,
      visibleResolved: false,
      visibleVariants: []
    };

    return {
      cache: window.__heroSearchVariantCache[cacheKey],
      contextCountry
    };
  }

  getHeroVariantDayValue(variant) {
    const dayOption = (variant?.selectedOptions || []).find((option) => option.name === 'Days');
    if (dayOption?.value) return Number(dayOption.value);

    const variantTitle = variant?.title || '';
    const dayMatch = variantTitle.match(/(\d+)\s*days?/i) || variantTitle.match(/\/\s*(\d+)\s*$/);
    return dayMatch ? Number(dayMatch[1]) : null;
  }

  getHeroVariantPlanKey(variant) {
    const selectedOptions = variant?.selectedOptions || [];
    const planOption = selectedOptions.find((option) => option.name !== 'Days');
    if (planOption?.value) return planOption.value;

    const titleParts = String(variant?.title || '').split('/');
    if (titleParts.length > 1) {
      titleParts.pop();
      return titleParts.join('/').trim();
    }

    return String(variant?.title || '').trim();
  }

  getHeroExpectedVisibleDailyVariantCount(options = []) {
    const productOptions = Array.isArray(options) ? options : [];
    const hasDaysOption = productOptions.some((option) => option?.name === 'Days');
    if (!hasDaysOption) return 0;

    const planOption = productOptions.find((option) => option?.name !== 'Days');
    return Array.isArray(planOption?.values) ? planOption.values.length : 0;
  }

  hasHeroVisibleDailyVariants(product, expectedVisibleCount = 0) {
    if (!expectedVisibleCount || !Array.isArray(product?.variants)) return false;

    const visiblePlanKeys = new Set();
    product.variants.forEach((variant) => {
      if (variant.available === false || this.getHeroVariantDayValue(variant) !== 1) return;

      const planKey = this.getHeroVariantPlanKey(variant);
      if (planKey) visiblePlanKeys.add(planKey);
    });

    return visiblePlanKeys.size >= expectedVisibleCount;
  }

  async fetchDay1HeroVariants(product, options = {}) {
    // Fast-path: fetch only enough variants to get Day 1 for each plan type.
    // Daily products have variants ordered as: Plan1/Day1, Plan1/Day2...Plan1/Day30, Plan2/Day1...
    // So Day 1 variants for N plan types are at positions 0, 30, 60, 90... (every 30 items).
    // We request the first 250 but stop processing as soon as we have one Day 1 per plan key.
    // This is the same single request already made by the initial GraphQL fetch — if that
    // returned variants, we already have at least some Day 1 variants without any extra fetch.
    if (!product?.handle || !Array.isArray(product.variants)) return [];

    // Check if we already have Day 1 variants from the initial fetch
    const existingDay1 = product.variants.filter(v => {
      return this.getHeroVariantDayValue(v) === 1;
    });

    if (existingDay1.length > 0) {
      // Already have Day 1 variants — return them immediately, no fetch needed
      return existingDay1;
    }

    // No Day 1 variants yet — fire a single targeted fetch of first 50 variants
    // (enough to cover 1-2 plan types worth of day variants before Day 1 of next plan appears)
    const { contextCountry } = this.getHeroVisibleVariantCache(product, options);
    const shopifyDomain = window.Shopify?.shop || document.location.host;
    const storefrontAccessToken = window.storefrontAccessToken || '0d7419f54e0721cfdf56a0594c535214';
    if (!storefrontAccessToken) return [];

    try {
      const query = `
        query ProductDay1Variants($handle: String!, $country: CountryCode!)
        @inContext(country: $country) {
          product(handle: $handle) {
            options { name values }
            variants(first: 250) {
              edges {
                node {
                  id title availableForSale
                  price { amount currencyCode }
                  compareAtPrice { amount currencyCode }
                  selectedOptions { name value }
                }
              }
            }
          }
        }
      `;

      const response = await fetch(`https://${shopifyDomain}/api/2026-04/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
          'Accept-Language': window.currentLocale || document.documentElement.lang || 'en'
        },
        body: JSON.stringify({ query, variables: { handle: product.handle, country: contextCountry } })
      });

      if (!response.ok) return [];

      const payload = await response.json();
      const edges = payload?.data?.product?.variants?.edges || [];

      const fetched = edges.map(({ node }) => this.buildHeroVariantFromStorefrontNode(node, product.handle));
      this.mergeFetchedHeroVariantsIntoProduct(product, fetched);

      // Return only Day 1 variants for immediate render
      return product.variants.filter(v => this.getHeroVariantDayValue(v) === 1);
    } catch (e) {
      return [];
    }
  }

  fetchVisibleHeroSearchVariants(product, options = {}) {
    if (!product?.handle || !Array.isArray(product.variants)) return Promise.resolve(product?.variants || []);

    const expectedVariantCount = this.getHeroVariantCount(product);
    if (!expectedVariantCount || product.variants.length >= expectedVariantCount) {
      return Promise.resolve(product.variants);
    }

    const { cache, contextCountry } = this.getHeroVisibleVariantCache(product, options);
    if (cache.resolved) {
      this.mergeFetchedHeroVariantsIntoProduct(product, cache.resolved);
      return Promise.resolve(product.variants);
    }

    if (cache.visibleResolved) {
      this.mergeFetchedHeroVariantsIntoProduct(product, cache.visibleVariants);
      return Promise.resolve(product.variants);
    }

    if (!cache.visiblePromise) {
      cache.visiblePromise = (async () => {
        const shopifyDomain = window.Shopify?.shop || document.location.host;
        const storefrontAccessToken = window.storefrontAccessToken || '0d7419f54e0721cfdf56a0594c535214';
        if (!storefrontAccessToken) return product.variants;

        const query = `
          query ProductVisibleVariants($handle: String!, $first: Int!, $after: String, $country: CountryCode!)
          @inContext(country: $country) {
            product(handle: $handle) {
              options {
                name
                values
              }
              variants(first: $first, after: $after) {
                pageInfo {
                  hasNextPage
                  endCursor
                }
                edges {
                  node {
                    id
                    title
                    availableForSale
                    price {
                      amount
                      currencyCode
                    }
                    compareAtPrice {
                      amount
                      currencyCode
                    }
                    selectedOptions {
                      name
                      value
                    }
                  }
                }
              }
            }
          }
        `;

        let after = null;
        let hasNextPage = true;
        let expectedVisibleCount = 0;

        while (hasNextPage) {
          const response = await fetch(`https://${shopifyDomain}/api/2026-04/graphql.json`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
              'Accept-Language': window.currentLocale || document.documentElement.lang || 'en'
            },
            body: JSON.stringify({
              query,
              variables: {
                handle: product.handle,
                first: 100,
                after,
                country: contextCountry
              }
            })
          });

          if (!response.ok) {
            throw new Error(`Storefront API request failed with status ${response.status}`);
          }

          const payload = await response.json();
          const storefrontProduct = payload?.data?.product;
          const variantsConnection = storefrontProduct?.variants;
          if (!variantsConnection) break;

          if (!expectedVisibleCount) {
            expectedVisibleCount = this.getHeroExpectedVisibleDailyVariantCount(storefrontProduct.options);
          }

          const pageVariants = variantsConnection.edges.map(({ node }) => {
            return this.buildHeroVariantFromStorefrontNode(node, product.handle);
          });
          cache.visibleVariants.push(...pageVariants);
          this.mergeFetchedHeroVariantsIntoProduct(product, pageVariants);

          if (this.hasHeroVisibleDailyVariants(product, expectedVisibleCount)) {
            cache.visibleResolved = true;
            break;
          }

          hasNextPage = Boolean(variantsConnection.pageInfo?.hasNextPage);
          after = variantsConnection.pageInfo?.endCursor || null;
        }

        return product.variants;
      })();
    }

    return cache.visiblePromise.catch((error) => {
      console.error('Failed to fetch visible hero search variants:', error);
      return this.fetchMissingHeroSearchVariants(product, options);
    });
  }

  fetchMissingHeroSearchVariants(product, options = {}) {
    if (!product?.handle || !Array.isArray(product.variants)) return Promise.resolve([]);

    const expectedVariantCount = this.getHeroVariantCount(product);
    if (!expectedVariantCount || product.variants.length >= expectedVariantCount) {
      return Promise.resolve(product.variants);
    }

    const { cache, contextCountry } = this.getHeroVisibleVariantCache(product, options);
    if (cache.resolved) {
      this.mergeFetchedHeroVariantsIntoProduct(product, cache.resolved);
      return Promise.resolve(product.variants);
    }

    if (!cache.promise) {
      cache.promise = (async () => {
        const shopifyDomain = window.Shopify?.shop || document.location.host;
        const storefrontAccessToken = window.storefrontAccessToken || '0d7419f54e0721cfdf56a0594c535214';
        if (!storefrontAccessToken) return [];

        const query = `
          query ProductVariants($handle: String!, $first: Int!, $after: String, $country: CountryCode!)
          @inContext(country: $country) {
            product(handle: $handle) {
              variants(first: $first, after: $after) {
                pageInfo {
                  hasNextPage
                  endCursor
                }
                edges {
                  node {
                    id
                    title
                    availableForSale
                    price {
                      amount
                      currencyCode
                    }
                    compareAtPrice {
                      amount
                      currencyCode
                    }
                    selectedOptions {
                      name
                      value
                    }
                  }
                }
              }
            }
          }
        `;

        const fetchedVariants = [];
        let after = null;
        let hasNextPage = true;

        while (hasNextPage) {
          const response = await fetch(`https://${shopifyDomain}/api/2026-04/graphql.json`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'X-Shopify-Storefront-Access-Token': storefrontAccessToken,
              'Accept-Language': window.currentLocale || document.documentElement.lang || 'en'
            },
            body: JSON.stringify({
              query,
              variables: {
                handle: product.handle,
                first: 100,
                after,
                country: contextCountry
              }
            })
          });

          if (!response.ok) {
            throw new Error(`Storefront API request failed with status ${response.status}`);
          }

          const payload = await response.json();
          const variantsConnection = payload?.data?.product?.variants;
          if (!variantsConnection) break;

          variantsConnection.edges.forEach(({ node }) => {
            fetchedVariants.push(this.buildHeroVariantFromStorefrontNode(node, product.handle));
          });

          hasNextPage = Boolean(variantsConnection.pageInfo?.hasNextPage);
          after = variantsConnection.pageInfo?.endCursor || null;
        }

        cache.resolved = fetchedVariants;
        return fetchedVariants;
      })();
    }

    return cache.promise
      .then((variants) => {
        this.mergeFetchedHeroVariantsIntoProduct(product, variants);
        return product.variants;
      })
      .catch((error) => {
        console.error('Failed to fetch complete hero search variants:', error);
        return product.variants;
      });
  }

  // Method to fetch products based on filters using Shopify API
  async fetchFilteredProducts(filters) {

    try {
      // Use Storefront API directly with GraphQL
      const products = await this.fetchProductsFromStorefrontAPI(filters);

      // Log detailed information about each matching product
      if (products.length > 0) {
        products.forEach(product => {
          const metafields = product.metafields || {};
        });
      } else {
      }

      return products;
    } catch (error) {
      console.error('Error with Storefront API:', error);
      return [];
    }
  }

  // Method to fetch products using Shopify Storefront API with GraphQL
  async fetchProductsFromStorefrontAPI(filters) {

    try {
      // Ensure we have the Shopify domain
      const shopifyDomain = window.Shopify?.shop || document.location.host;

      // Get access token - this should be provided by your Shopify theme
      // For security, this should be a public access token with only the necessary scopes
      const storefrontAccessToken = window.storefrontAccessToken || 'YOUR_PUBLIC_TOKEN';

      if (false) { // token optional
        return [];
      }

      // Format filter values with proper case sensitivity
      const countryFilter = filters.country || '';
      const typeFilter = (filters.type || '').toLowerCase(); // type should be lowercase ('single' or 'multi')
      const topUpFilter = filters.top_up ?
        filters.top_up.charAt(0).toUpperCase() + filters.top_up.slice(1).toLowerCase() : ''; // 'True' or 'False'

      // IMPORTANT DEBUGGING INFORMATION:
      // The issue might be with how metafields are stored in Shopify
      // Let's try a simpler approach - fetch all products and filter them manually

      // Get current country for price context
      const currentCountry = window.currentCountry || 'US';

      // Build a simple GraphQL query to get all products with their metafields
      // Use @inContext directive to get prices in the correct currency
      const query = `
        query ProductsWithMetafields($country: CountryCode!) @inContext(country: $country) {
          products(first: 100) {
            edges {
              node {
                id
                title
                handle
                availableForSale
                tags
                featuredImage {
                  url(transform: {maxWidth: 100, maxHeight: 100})
                  altText
                }
                variantsCount {
                  count
                }
                metafields(identifiers: [
                  {namespace: "filters", key: "country"},
                  {namespace: "filters", key: "type"},
                  {namespace: "global", key: "country"},
                  {namespace: "global", key: "type"}
                ]) {
                  namespace
                  key
                  value
                }
              variants(first: 250) {
                edges {
                  node {
                    id
                    title
                    availableForSale
                    price {
                      amount
                      currencyCode
                    }
                    compareAtPrice {
                      amount
                      currencyCode
                    }
                    selectedOptions {
                      name
                      value
                    }
                  }
                }
              }
            }
            }
          }
        }
      `;

      // Make the GraphQL request to Storefront API with country variable
      const response = await fetch(`https://${shopifyDomain}/api/2026-04/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(storefrontAccessToken ? {'X-Shopify-Storefront-Access-Token': storefrontAccessToken} : {}),
          'Accept-Language': window.currentLocale || document.documentElement.lang || 'en'
        },
        body: JSON.stringify({
          query,
          variables: { country: currentCountry }
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        throw new Error(`Storefront API request failed with status ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      // Debug the raw response

      // Check for errors in the response
      if (data.errors) {
        console.error('GraphQL errors:', data.errors);
        throw new Error(`GraphQL query returned errors: ${data.errors.map(e => e.message).join(', ')}`);
      }

      // Process the response to extract products and format them consistently
      const productEdges = data.data?.products?.edges || [];
      const products = [];

      // Process products sequentially to handle async variant fetching
      for (const edge of productEdges) {
        const product = edge.node;

        // Format metafields into a flat object for easier access
        const metafields = {};
        if (product.metafields && Array.isArray(product.metafields)) {
          product.metafields.forEach(metafield => {
            if (metafield) {
              // Store all metafields for debugging
              const key = metafield.namespace ? `${metafield.namespace}.${metafield.key}` : metafield.key;

              // Store original value
              metafields[key] = metafield.value;

              // ALSO store metafields in the format we expect for filtering
              if (metafield.namespace === 'filters') {
                metafields[`filters.${metafield.key}`] = metafield.value;
              }

              // Try alternative formats that might be used
              if (metafield.key === 'country' || metafield.key === 'type') {
                metafields[`filters.${metafield.key}`] = metafield.value;
              }
            }
          });
        }

        // If no metafields are found, try to infer from tags or title
        if (Object.keys(metafields).length === 0 && product.tags) {
          // For debugging - since we might not have metafields, try to extract from tags

          if (product.title) {
            // Infer country from product title
            metafields['filters.country'] = product.title.split(' ')[0]; // Assume first word is country
          }

          // Check for specific tags
          if (Array.isArray(product.tags)) {
            product.tags.forEach(tag => {
              if (tag.toLowerCase().includes('single')) {
                metafields['filters.type'] = 'single';
              } else if (tag.toLowerCase().includes('multi')) {
                metafields['filters.type'] = 'multi';
              }

            });
          }
        }

        // Format variants into a consistent structure
        const variants = product.variants?.edges?.map(variantEdge => {
          const variant = variantEdge.node;
          return {
            id: variant.id.split('/').pop(), // Extract the numeric ID from the Storefront API ID
            title: variant.title,
            available: variant.availableForSale,
            price: parseFloat(variant.price.amount),
            currencyCode: variant.price.currencyCode, // Store currency code for proper display
            compare_at_price: variant.compareAtPrice ? parseFloat(variant.compareAtPrice.amount || 0) : null,
            selectedOptions: variant.selectedOptions || [],
            handle: product.handle // Store handle for later AJAX price correction
          };
        }) || [];

        // Add the formatted product to products array
        products.push({
          id: product.id.split('/').pop(), // Extract the numeric ID
          title: product.title,
          handle: product.handle,
          featuredImage: product.featuredImage, // Include image for detail panel header
          available: product.availableForSale,
          metafields: metafields,
          variantsCount: product.variantsCount,
          variants: variants.filter(v => v.available) // Only include available variants
        });
      }

      // Log all products and their metafields for debugging
      products.forEach(product => {
      });

      // Apply additional filtering to ensure exact metafield matches
      const filteredProducts = products.filter(product => {
        // Get the product's metafields
        const metafields = product.metafields || {};

        // For debugging
        let matchesCountry = true;
        let matchesType = true;
        // Try different metafield key formats for country
        const possibleCountryKeys = ['filters.country', 'country', 'global.country'];
        let countryValue = null;

        for (const key of possibleCountryKeys) {
          if (metafields[key]) {
            countryValue = metafields[key];
            break;
          }
        }

        // Handle country filter matching
        if (countryFilter && countryValue) {
          if (Array.isArray(countryValue)) {
            // For arrays, check if the filter country is included (case insensitive)
            matchesCountry = countryValue.some(country =>
              typeof country === 'string' &&
              country.toLowerCase() === countryFilter.toLowerCase()
            );

            // Special case for regional products (multi type)
            if (!matchesCountry && typeFilter === 'multi') {
              // For regional plans, country matching is less strict

              // First, check if we can find a regional plan with this country included
              if (metafields['filters.type'] === 'multi') {
                // Keep multi-type products always for regional filtering
                matchesCountry = true;
              }
            }
          } else {
            // For regular strings, do case insensitive match
            matchesCountry = countryValue.toLowerCase() === countryFilter.toLowerCase();
          }

          if (!matchesCountry) {
            return false;
          }
        } else if (countryFilter) {
          return false;
        }

        // Try different metafield key formats for type
        const possibleTypeKeys = ['filters.type', 'type', 'global.type'];
        let typeValue = null;

        for (const key of possibleTypeKeys) {
          if (metafields[key]) {
            typeValue = metafields[key];
            break;
          }
        }

        // Handle type filter matching
        if (typeFilter && typeValue) {
          // Case insensitive matching for type values
          matchesType = typeValue.toLowerCase() === typeFilter.toLowerCase();

          if (!matchesType) {
            return false;
          }
        } else if (typeFilter) {
          return false;
        }

        if (topUpFilter) {
          const matchingVariants = (product.variants || []).filter((variant) => {
            const variantTopUp = getVariantTopUpValue(variant);
            return variantTopUp === topUpFilter;
          });

          if (!matchingVariants.length) {
            return false;
          }

          product.variants = matchingVariants;
        }

        // If we got here, all filters match
        return true;
      });

      // Post-process to get correct currency prices using AJAX (same as product pages)

      for (const product of filteredProducts) {
        try {
          const productId = product.id;
          const correctVariants = await this.fetchProductVariants(productId, product.handle);
          if (correctVariants && correctVariants.length > 0) {
            product.variants = correctVariants.filter(v => v.available !== false);
          }
          this.fetchMissingHeroSearchVariants(product, { country: currentCountry }).catch(() => {});
        } catch (error) {
        }
      }

      return filteredProducts;

    } catch (error) {
      console.error('Error in fetchProductsFromStorefrontAPI:', error);
      return [];
    }
  }

  // Method to fetch products from a custom backend endpoint
  async fetchProductsFromBackend(filters) {

    try {
      // Prepare the filter values with proper capitalization for metafields
      const countryFilter = filters.country || '';
      const typeFilter = filters.type || '';
      let topUpFilter = '';

      if (filters.top_up) {
        // Ensure proper capitalization for boolean values (True or False)
        // The backend expects True/False with capital first letter
        topUpFilter = filters.top_up.charAt(0).toUpperCase() + filters.top_up.slice(1).toLowerCase();
      }

      // Create the request body
      const requestBody = {
        filters: {
          country: countryFilter,
          type: typeFilter.toLowerCase(), // Ensure lowercase for type
          top_up: topUpFilter
        }
      };

      // Make the API request to our custom backend endpoint
      const response = await fetch('/apps/metafield-filter/products', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      if (!response.ok) {
        console.error(`Backend API request failed with status ${response.status}`);
        throw new Error('Backend API request failed');
      }

      const data = await response.json();

      // Return the products
      return data.products;
    } catch (error) {
      console.error('Error in backend fetch:', error);
      throw error;
    }
  }

  // Method to fetch products using GraphQL API
  async fetchProductsFromGraphQL(filters) {

    try {
      // Use the collection endpoint directly but with our custom filtering
      return await this.fetchProductsFromCollection(filters);
    } catch (error) {
      console.error('Error in GraphQL fetch:', error);
      throw error;
    }
  }

  // Fallback method using collection endpoint
  async fetchProductsFromCollection(filters) {
    try {

      // Base collection - this should be your main products collection
      // We'll first get all products and then filter by metafields
      const baseCollection = 'all';
      const url = `/collections/${baseCollection}/products.json`;

      // Make the API request
      const response = await fetch(url);

      if (!response.ok) {
        console.error(`API request failed with status ${response.status}`);
        return [];
      }

      const data = await response.json();

      // Extract products from the response
      let products = data.products || [];

      // Use Storefront API to get products with proper metafields
      return await this.fetchProductsFromStorefrontAPI(filters);

    } catch (error) {
      console.error('Error fetching products from collection:', error);
      return [];
    }
  }

  // Helper method to update debug panel with variant information
  updateDebugVariantsInfo(totalVariants) {
    // Debug panel has been removed
    return;
  }

  // Helper method to display product variants
  async displayProductVariants(product, tabContent) {
    await this.displayProductVariantsInTab(product, tabContent);
  }

  // Simulate fetching products based on filters
  simulateFetchProducts(filters) {
    return [];
  }

  updateDetailPanelPosition() {
    if (!this.detailPanel) return;

    const heroSearchLeft = document.querySelector('.hero-search__left');
    if (!heroSearchLeft) return;

    const isMobile = window.innerWidth < 768;

    if (isMobile) {
      // Mobile: CSS media query owns positioning entirely — clear any stale inline
      // styles that would fight the CSS rules and cause the snap-back jump.
      this.detailPanel.style.position = '';
      this.detailPanel.style.top = '';
      this.detailPanel.style.bottom = '';
      this.detailPanel.style.left = '';
      this.detailPanel.style.right = '';
      this.detailPanel.style.transform = '';
    } else {
      // Desktop/tablet: position the panel to the right of the phone bezel using
      // the actual measured rect — the CSS left:calc(320px+4rem) is hardcoded and
      // doesn't match the real bezel position at different viewport widths.
      const leftRect = heroSearchLeft.getBoundingClientRect();
      this.detailPanel.style.position = 'absolute';
      this.detailPanel.style.top = `${leftRect.top + (leftRect.height / 2)}px`;
      this.detailPanel.style.left = `${leftRect.right + 20}px`;
      this.detailPanel.style.transform = 'translateY(-50%)';
      this.detailPanel.style.bottom = '';
      this.detailPanel.style.right = '';
    }

    if (!this.outsideClickListenerAdded) {
      document.addEventListener('click', this.handleOutsideDetailPanelClick.bind(this));
      this.outsideClickListenerAdded = true;
    }
  }

  // Helper method to handle clicking outside the detail panel
  handleOutsideDetailPanelClick(event) {
    if (!this.detailPanel) return;

    // If the detail panel is active and the click is outside it
    if (this.detailPanel.classList.contains('active') &&
      !this.detailPanel.contains(event.target) &&
      !event.target.closest('.collection-item')) {

      const closeAnimationDuration = 300;

      // Hide backdrop (mobile only)
      if (this.detailPanelBackdrop) {
        this.detailPanelBackdrop.classList.remove('active');
        setTimeout(() => {
          this.detailPanelBackdrop.style.display = 'none';
        }, closeAnimationDuration);
      }

      // Close the detail panel completely
      this.detailPanel.style.opacity = '0';
      this.detailPanel.style.visibility = 'hidden';

      // Use a timeout to ensure the animation completes before hiding
      setTimeout(() => {
        this.detailPanel.style.display = 'none';
        this.detailPanel.classList.remove('active');

        // Reset content
        if (this.detailPanelContent) {
          this.detailPanelContent.innerHTML = '';
        }

        // Reset any selected product/variant state
        this.selectedVariant = null;

        // Reset cached data
        this.tabData = {};
        this.activeFilterType = null;
        this.restoreBackgroundScroll();
      }, closeAnimationDuration);

      // Unselect buttons using the helper method
      this.unselectCollectionButtons();
    }
  }

  // Helper method to unselect collection buttons
  unselectCollectionButtons() {
    const collectionGrid = document.querySelector('.collection-grid');
    if (collectionGrid) {
      const selectedItems = collectionGrid.querySelectorAll('.collection-item.selected');
      selectedItems.forEach(item => {
        item.classList.remove('selected');
      });
    }

    // Reset any selected variants in panels
    const variantItems = document.querySelectorAll('.collection-product-variant.selected');
    variantItems.forEach(item => {
      item.classList.remove('selected');
    });

    // Disable continue to days button
    if (this.continueToDaysBtn) {
      this.continueToDaysBtn.disabled = true;
    }

    // Disable continue with variant button
    const continueWithVariantBtn = document.querySelector('.continue-with-variant');
    if (continueWithVariantBtn) {
      continueWithVariantBtn.disabled = true;
    }
  }

  async showSelectedItem(title) {
    if (!this.selectedItemContainer || !this.searchInput) return;

    // Get translated name for display, but keep original for processing
    const translatedTitle = getTranslatedCountryName(title);

    // Update search input with translated selection, but keep it visible
    this.searchInput.value = translatedTitle;
    this.searchInput.classList.add('has-selection');
    this.searchInput.setAttribute('readonly', 'readonly'); // Prevent editing

    // Store original title for processing
    this.searchInput.dataset.originalValue = title;

    // Clear any existing content in selected item container
    this.selectedItemContainer.innerHTML = '';

    // Create the selected item elements
    const selectedText = document.createElement('div');
    selectedText.className = 'selected-text';
    selectedText.title = title; // Add tooltip for very long text

    const selectedFlag = createCountryFlagImage(title, { eager: true });
    if (selectedFlag) selectedText.appendChild(selectedFlag);

    const selectedLabel = document.createElement('span');
    selectedLabel.className = 'selected-text-label';
    selectedLabel.textContent = translatedTitle;
    selectedText.appendChild(selectedLabel);

    const removeButton = document.createElement('button');
    removeButton.className = 'remove-button';
    removeButton.setAttribute('aria-label', 'Remove selected item');
    removeButton.innerHTML = `
          <svg viewBox="0 0 24 24">
            <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/>
          </svg>
    `;

    // Add event listener to remove button
    removeButton.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation();
      this.handleRemoveSelection();
    });

    // Append elements to container
    this.selectedItemContainer.appendChild(selectedText);
    this.selectedItemContainer.appendChild(removeButton);

    // Show the container
    this.selectedItemContainer.style.display = 'flex';

    // Ensure the search box remains visible
    if (this.searchBox) {
      this.searchBox.style.display = 'block';
    }

    // Position the selected item in the center of the input
    // This ensures the selected item appears properly in the middle
    this.searchInput.style.color = 'transparent'; // Hide the text in the input
    this.searchInput.style.caretColor = 'transparent'; // Hide the caret

    // Hide dropdown
    if (this.searchDropdown) {
      this.searchDropdown.classList.remove('active');
      if (this.searchInput) this.searchInput.setAttribute('aria-expanded', 'false');
    }

    this.hydrateSelectedCountryProduct(title);
  }

  handleRemoveSelection() {
    if (!this.selectedItemContainer || !this.searchInput) return;

    // Hide the selected item container
    this.selectedItemContainer.style.display = 'none';

    // Clear and reset the search input
    this.searchInput.value = '';
    this.searchInput.classList.remove('has-selection');
    this.searchInput.classList.remove('no-results');
    this.searchInput.removeAttribute('readonly'); // Allow editing again
    delete this.searchInput.dataset.originalValue; // Clear the original value

    // Reset all inline styles that might have been added
    this.searchInput.style.display = 'block';
    this.searchInput.style.visibility = 'visible';
    this.searchInput.style.opacity = '1';
    this.searchInput.style.color = ''; // Reset to default color
    this.searchInput.style.caretColor = ''; // Reset caret color

    // Focus the input
    this.searchInput.focus();

    // Make sure the search box is properly displayed
    if (this.searchBox) {
      this.searchBox.style.display = 'block';
    }

    // Disable the continue button
    this.setContinueToPlansState(false);

    // Reset the selected product
    this.selectedProduct = null;
    this.selectedCountryPrefetchPromise = null;
  }

  // Build one dropdown row: flag (if we have an image URL) + translated label. Only the
  // first few rows (the ones visible without scrolling, ~6) load eagerly; the rest stay
  // lazy since most of a "show all countries" list is off-screen until scrolled to.
  createCountryDropdownItem(country, index) {
    const item = document.createElement('div');
    item.className = 'predictive-search__item';
    item.setAttribute('role', 'option');
    item.setAttribute('aria-selected', 'false');
    item.dataset.englishName = country; // Store English name for processing

    const flag = createCountryFlagImage(country, { eager: typeof index === 'number' && index < 6 });
    if (flag) item.appendChild(flag);

    const label = document.createElement('span');
    label.className = 'predictive-search__item-label';
    label.textContent = getTranslatedCountryName(country); // Display in current language
    item.appendChild(label);

    // Handle item click
    item.addEventListener('click', () => {
      // Select this country using English name
      this.showSelectedItem(country);
    });

    return item;
  }

  filterAndDisplayResults(query) {
    if (!this.countries || !this.searchDropdown) return;

    // Use new multilingual search
    const results = filterCountriesMultilingual(this.countries, query);

    // Clear previous results
    this.searchDropdown.innerHTML = '';

    // Show dropdown
    this.searchDropdown.classList.add('active');
    this.searchInput.setAttribute('aria-expanded', 'true');

    // Handle no results case
    if (results.length === 0) {
      // Add red border to search input
      this.searchInput.classList.add('no-results');

      // Show no results message in current language
      const noResults = document.createElement('div');
      noResults.className = 'predictive-search__item no-results';
      noResults.textContent = this.getDetailPanelText('no_matching_countries');
      this.searchDropdown.appendChild(noResults);
    } else {
      // Remove red border if previously added
      this.searchInput.classList.remove('no-results');

      // Create result items - display in current language but store English name
      results.forEach((country, index) => {
        const item = this.createCountryDropdownItem(country, index);
        this.searchDropdown.appendChild(item);
      });
    }
  }

  // Helper function to get translations for all hero search text
  // Supports English (default) and Dutch
  getDetailPanelText(key, networkCount = null) {
    if (window.heroSearchLocaleStrings && typeof window.heroSearchLocaleStrings[key] === 'string') {
      return window.heroSearchLocaleStrings[key];
    }

    // 1. Try centralized translations first (The "Pro" Way)
    if (window.themeTranslations && typeof window.themeTranslations.t === 'function') {
      // Handle special cases handled by centralized translation if needed, 
      // or just pass the key.

      // For network text, determine singular or plural key
      if (key === 'with_network' && networkCount !== null) {
        key = networkCount === 1 ? 'with_network_singular' : 'with_network_plural';
      }

      const translated = window.themeTranslations.t(key);

      // If the translation system returns the key itself (meaning missing), 
      // we can fall back to internal dictionary or just return it.
      // But let's check if the centralized system actually has it.
      if (translated !== key) {
        return translated;
      }
    }

    // 2. Fallback to internal dictionary (legacy support)
    // Get current language from generic generic Vertaalapp or window.currentLocale
    let locale = 'en'; // default

    if (document.documentElement.lang) {
      locale = document.documentElement.lang.split('-')[0].toLowerCase();
    } else if (window.currentLocale) {
      locale = window.currentLocale;
    }

    const translations = {
      // Loader
      'loading': {
        'en': 'Loading...',
        'nl': 'Laden...',
        'tr': 'Yükleniyor...'
      },
      // Hero Screen 1
      'hero_main_title': {
        'en': 'Japan eSIM and travel eSIM for 180+ countries.',
        'nl': 'Japan eSIM en reis-eSIM voor 180+ landen.',
        'tr': 'Japonya eSIM ve 180+ ülke için seyahat eSIM\'i.'
      },
      'hero_search_label': {
        'en': 'Get started. <br>Your eSIM awaits.',
        'nl': 'Begin hier. <br>Je eSIM. Voor elke reis.'
      },
      'search_placeholder': {
        'en': 'Type a country',
        'nl': 'Typ een land'
      },
      'hero_search_sublabel': {
        'en': '...and we\'ll get you travel ready <br>in seconds.',
        'nl': '...en we maken je binnen <br>seconden reisklaar.'
      },
      // Hero Screen 2
      'choose_plan_subtitle': {
        'en': 'Choose a set amount of data,<br>or stay connected with unlimited data.',
        'nl': 'Kies een vaste hoeveelheid data,<br>of blijf verbonden met onbeperkte data.',
        'tr': 'Belirli bir miktar internet seç<br>veya sınırsız internetle bağlantıda kal.'
      },
      'whats_difference': {
        'en': "What's the difference?",
        'nl': 'Wat is het verschil?'
      },
      // Hero Screen 3
      'almost_set_title': {
        'en': "You're almost set.",
        'nl': 'Bijna klaar.'
      },
      'set_days_quantity': {
        'en': 'Set your days and quantity.',
        'nl': 'Kies de dagen en aantal eSIMs.'
      },
      'set_quantity': {
        'en': 'Set your quantity.',
        'nl': 'Kies je aantal eSIMs.'
      },
      'days_label': {
        'en': 'Days',
        'nl': 'Dagen'
      },
      'esims_label': {
        'en': 'eSIMs',
        'nl': 'eSIMs'
      },
      // Collection buttons
      'fixed_plan': {
        'en': 'Fixed Plan',
        'nl': 'Vast plan',
        'tr': 'Sabit Paket'
      },
      'daily_plan': {
        'en': 'Unlimited Plan',
        'nl': 'Onbeperkt plan',
        'tr': 'Sınırsız Paket'
      },
      'unlimited_custom_days': {
        'en': 'Custom days',
        'nl': 'Dagen op maat',
        'tr': 'Kendin belirle'
      },
      'unlimited_days_label': {
        'en': '# days Unlimited',
        'nl': '# dagen Onbeperkt',
        'tr': '# gün Sınırsız'
      },
      'unlimited_days_short_label': {
        'en': '# days',
        'nl': '# dagen',
        'tr': '# gün'
      },
      'variant_label_unlimited_word': {
        'en': 'Unlimited',
        'nl': 'Onbeperkt',
        'tr': 'Sınırsız'
      },
      'variant_label_day_singular': {
        'en': 'day',
        'nl': 'dag',
        'tr': 'gün'
      },
      'variant_label_day_plural': {
        'en': 'days',
        'nl': 'dagen',
        'tr': 'gün'
      },
      'unlimited_speed_note': {
        'en': '2 GB/day full speed, then 1 Mbps.',
        'nl': '2 GB/dag hoge snelheid, daarna 1 Mbps.',
        'tr': '2 GB/gün yüksek hız, sonra 1 Mbps.'
      },
      // Hero Screen 2
      'choose_your_pack': {
        'en': 'Choose your Plan',
        'nl': 'Kies je Plan'
      },
      // Detail panel header
      'choose_your_pack_detail': {
        'en': 'Choose your Pack',
        'nl': 'Kies je Bundel'
      },
      'title_prefix_for': {
        'en': 'for',
        'nl': 'voor'
      },
      'with_network_singular': {
        'en': 'with top local network',
        'nl': 'aangesloten op lokaal netwerk'
      },
      'with_network_plural': {
        'en': 'with top local networks',
        'nl': 'aangesloten op lokale netwerken'
      },
      // Detail panel footer buttons
      'continue': {
        'en': 'Continue',
        'nl': 'Verder'
      },
      'view_product': {
        'en': 'View product',
        'nl': 'Bekijk product',
        'tr': 'Ürünü görüntüle'
      },
      // Search results
      'no_matching_countries': {
        'en': 'No countries found',
        'nl': 'Geen landen gevonden'
      },
      // Info section text
      'info_topup_available': {
        'en': 'Top-up available',
        'nl': 'Opwaarderen mogelijk',
        'tr': 'Sonradan veri eklenebilir'
      },
      'info_topup_unavailable': {
        'en': 'No top-up available',
        'nl': 'Niet opwaardeerbaar',
        'tr': 'Sonradan veri eklenemez'
      },
      'info_speed_stop': {
        'en': 'Data stops at limit',
        'nl': 'Data stopt bij de limiet',
        'tr': 'Veri limitte durur'
      },
      'info_speed_slow': {
        'en': 'Speed reduced after limit',
        'nl': 'Lagere snelheid na limiet',
        'tr': 'Limit sonrası hız düşer'
      },
      'info_start_arrival': {
        'en': 'Postpone activation',
        'nl': 'Activatie uitstellen',
        'tr': 'Aktivasyonu ertele'
      },
      'info_hotspot': {
        'en': 'Hotspot enabled',
        'nl': 'Hotspot toegestaan',
        'tr': 'Hotspot kullanılabilir'
      },
      'info_local_networks': {
        'en': 'Local networks',
        'nl': 'Lokale netwerken',
        'tr': 'Yerel operatörler'
      },
    };

    // For network text, determine singular or plural
    if (key === 'with_network' && networkCount !== null) {
      const networkKey = networkCount === 1 ? 'with_network_singular' : 'with_network_plural';
      return translations[networkKey] && translations[networkKey][locale]
        ? translations[networkKey][locale]
        : (networkCount === 1 ? 'with top local network' : 'with top local networks');
    }

    return translations[key] && translations[key][locale]
      ? translations[key][locale]
      : key;
  }

  // Translate collection buttons based on current language
  translateCollectionButtons() {
    // Get current language from generic source
    let locale = 'en';
    if (document.documentElement.lang) {
      locale = document.documentElement.lang.split('-')[0].toLowerCase();
    } else {
    }

    // Find the collection buttons, scoped to this instance's collection grid
    // and keyed by data-filter-type rather than DOM order (order-independent, instance-safe)
    const collectionScope = this.collectionGrid || document;
    const fixedPlanButton = collectionScope.querySelector('[data-filter-type="single"] .collection-item-title');
    const dailyPlanButton = collectionScope.querySelector('[data-filter-type="single-daily"] .collection-item-title');

    if (fixedPlanButton) {
      const translatedText = this.getDetailPanelText('fixed_plan');
      // Only update if we have a valid translation (not just the key)
      if (translatedText !== 'fixed_plan') {
        fixedPlanButton.textContent = translatedText;
      }
    }

    if (dailyPlanButton) {
      const translatedText = this.getDetailPanelText('daily_plan');
      if (translatedText !== 'daily_plan') {
        dailyPlanButton.textContent = translatedText;
      }
    }

    // Update Detail Panel buttons if they exist
    const continueBtn = document.querySelector('.continue-with-variant');
    if (continueBtn) {
      const text = this.getDetailPanelText('continue');
      if (text !== 'continue') {
        continueBtn.textContent = text;
      }
    }

    const viewProductLabel = document.querySelector('.detail-panel-product-link__label');
    if (viewProductLabel) {
      const text = this.getDetailPanelText('view_product');
      if (text !== 'view_product') {
        viewProductLabel.textContent = text;
      }
    }

  }

  // Translate all elements with data-i18n-key in the hero section
  translateDetailPanelInfo() {
    // Select all elements with data-i18n-key
    const elements = document.querySelectorAll('[data-i18n-key]');

    if (elements.length > 0) {
      elements.forEach(el => {
        const key = el.getAttribute('data-i18n-key');
        if (key) {
          const text = this.getDetailPanelText(key);

          // Only update if meaningful translation found and not just the key returned
          if (text !== key) {
            if (el.tagName === 'INPUT') {
              // Update placeholder for inputs
              el.setAttribute('placeholder', text);
            } else {
              // Use innerHTML to handle HTML entities and <br> tags safely
              el.innerHTML = text;
            }
          }
        }
      });
    }
  }

  disableControls() {
    if (this.daysSlider) {
      this.daysSlider.style.opacity = '0.5';
      this.daysSlider.style.pointerEvents = 'none';
    }

    if (this.addToCartButton) {
      this.addToCartButton.disabled = true;
      this.addToCartButton.style.opacity = '0.5';
    }
  }

  enableControls() {
    if (this.daysSlider) {
      this.daysSlider.style.opacity = '1';
      this.daysSlider.style.pointerEvents = 'auto';
    }

    if (this.addToCartButton) {
      this.addToCartButton.disabled = false;
      this.addToCartButton.style.opacity = '1';
    }
  }

  // Remove resetToInitialState function and go directly to loadCountryData
  async loadCountryData() {
    try {
      if (!this.countries || this.countries.length === 0) {
        this.countries = await fetchCountryData();
      } else {
      }
      return this.countries;
    } catch (error) {
      console.error('Error loading country data:', error);
      // Provide a fallback if there's an error
      this.countries = [
        'Australia', 'Canada', 'France', 'Germany', 'Italy', 'Japan', 'Spain',
        'United Kingdom', 'United States'
      ];
      return this.countries;
    }
  }

  setContinueToPlansState(isEnabled) {
    this.continueToPlansBtn = this.continueToPlansBtn || document.querySelector('#continue-to-plan');
    if (!this.continueToPlansBtn) return;

    this.continueToPlansBtn.disabled = !isEnabled;
    this.continueToPlansBtn.setAttribute('aria-disabled', String(!isEnabled));
  }

  getCountryAvailability(countryName) {
    const key = normalizeCountryMatchValue(countryName);
    if (!key) {
      return { fixed: false, daily: false };
    }

    return this.countryAvailabilityIndex?.get(key) || { fixed: false, daily: false };
  }

  async hydrateSelectedCountryProduct(title) {
    const hydrationToken = ++this.selectedCountryHydrationToken;
    const applyAvailability = (availability, options = {}) => {
      const { resolved = true } = options;
      if (hydrationToken !== this.selectedCountryHydrationToken || this.selectedProduct?.title !== title) {
        return;
      }

      this.selectedProduct = {
        ...this.selectedProduct,
        availability,
        planAvailabilityResolved: resolved
      };
      this.setContinueToPlansState(true);
    };

    this.selectedProduct = {
      title,
      handle: title,
      featuredImage: null,
      availability: {
        fixed: false,
        daily: false
      },
      planAvailabilityResolved: false
    };
    this.setContinueToPlansState(true);

    if (this.countryAvailabilityIndex) {
      const cachedAvailability = this.getCountryAvailability(title);
      if (cachedAvailability.fixed || cachedAvailability.daily) {
        applyAvailability(cachedAvailability, { resolved: false });
      }
    } else if (title) {
      this.findProductsWithCountry(title)
        .then(() => {
          const cachedAvailability = this.getCountryAvailability(title);
          if (cachedAvailability.fixed || cachedAvailability.daily) {
            applyAvailability(cachedAvailability, { resolved: false });
          }
        })
        .catch(() => {});
    }

    if (title) {
      // Set promise synchronously so showSimplifiedDetail can await it
      // even if the user clicks a plan button before this async function progresses further
      const planPrefetchPromise = this.prefetchSelectedCountryPlans(title);
      this.selectedCountryPrefetchPromise = planPrefetchPromise;
      planPrefetchPromise.then(({ fixedProducts, dailyProducts }) => {
        const availability = {
          fixed: fixedProducts.length > 0,
          daily: dailyProducts.length > 0
        };

        if (hydrationToken === this.selectedCountryHydrationToken && this.selectedProduct?.title === title) {
          applyAvailability(availability);
        }

        const allVariants = [];
        const seen = new Set();

        [...fixedProducts, ...dailyProducts].forEach(product => {
          (product.variants || []).forEach(variant => {
            if (!seen.has(variant.id) && !variant._renderedPrice) {
              seen.add(variant.id);
              allVariants.push({ variant, handle: product.handle });
            }
          });
        });

        allVariants.forEach(({ variant, handle }) => {
          this.fetchServerRenderedPrice(null, variant, handle)
            .then(priceText => {
              if (priceText) {
                variant._renderedPrice = priceText;
              }
            })
            .catch(() => {});
        });
      }).catch(() => {
        if (hydrationToken === this.selectedCountryHydrationToken && this.selectedProduct?.title === title) {
          this.setContinueToPlansState(true);
        }
      });
    } else {
      this.selectedCountryPrefetchPromise = null;
    }

    try {
      const products = await this.findProductsWithCountry(title);
      if (hydrationToken !== this.selectedCountryHydrationToken || this.selectedProduct?.title !== title) {
        return;
      }

      if (products && products.length > 0) {
        const product = products[0];
        this.selectedProduct = {
          ...this.selectedProduct,
          title,
          productTitle: product.title,
          handle: product.handle,
          featuredImage: product.featuredImage ? {
            url: product.featuredImage.url,
            altText: product.featuredImage.altText || product.title
          } : null
        };

        // Ensure the title image is restored even if it was cleared before the product data finished hydrating.
        this.updateScreen2CountryTitle();
      }
    } catch (error) {
      console.error('Error fetching product data:', error);
    }
  }

  setupSearch() {
    if (!this.searchInput || !this.searchDropdown) {
      console.error('Search elements not found, cannot set up search');
      return;
    }

    // Prevent multiple initializations
    if (this._searchInitialized) {
      return;
    }
    this._searchInitialized = true;

    // Load country data when setting up search
    this.loadCountryData().then(countries => {
      this.countries = countries;

      // Focus the search input after data is loaded
      setTimeout(() => {
        this.searchInput.focus();
      }, 500);
    }).catch(error => {
      console.error('Failed to load country data for search:', error);
    });

    // ===== GLOBAL VERTAALAPP LISTENER FOR SEARCH DROPDOWN =====
    const searchLangObserver = new MutationObserver((mutations) => {
      mutations.forEach((mutation) => {
        if (mutation.type === 'attributes' && mutation.attributeName === 'lang') {
          const newLang = document.documentElement.lang.split('-')[0].toLowerCase();
          window.currentLocale = newLang;

          // If search input has value, re-render dropdown with new language
          if (this.searchInput && this.searchInput.value.trim() !== '') {
            // Get the current query (raw input)
            const query = this.searchInput.value.trim();

            // Re-run the filter and display logic which now uses the updated getCurrentLang()
            this.filterAndDisplayResults(query);
          }
        }
      });
    });
    searchLangObserver.observe(document.documentElement, { attributes: true });
    // =======================================================

    // Handle input events for search
    this.searchInput.addEventListener('input', (e) => {
      let query = e.target.value;

      // Reset no-results class
      this.searchInput.classList.remove('no-results');

      // Don't show dropdown if input is empty (after trimming for check only)
      if (query.trim() === '') {
        this.searchDropdown.classList.remove('active');
      if (this.searchInput) this.searchInput.setAttribute('aria-expanded', 'false');
        this.searchDropdown.innerHTML = '';
        this.searchInput.setAttribute('aria-expanded', 'false');
        return;
      }

      // Capitalize the first letter only, preserve spaces
      if (query.length > 0) {
        query = query.charAt(0).toUpperCase() + query.slice(1);
        this.searchInput.value = query; // Update input value with capitalized version
      }

      // Use trimmed version for filtering, but keep original in input
      this.filterAndDisplayResults(query.trim());
    });

    // Handle focus events
    this.searchInput.addEventListener('focus', () => {
      // Check if an item is already selected
      if (this.searchInput.classList.contains('has-selection')) {
        // If an item is selected, don't show dropdown
        return;
      }

      let query = this.searchInput.dataset.originalValue || this.searchInput.value.trim();
      if (query !== '') {
        // Capitalize the first letter on focus too
        if (query.length > 0 && !this.searchInput.dataset.originalValue) {
          query = query.charAt(0).toUpperCase() + query.slice(1);
          this.searchInput.value = query; // Update input value with capitalized version
        }
        this.filterAndDisplayResults(query);
      }
    });

    // Additionally, prevent click from opening dropdown when item is selected
    this.searchInput.addEventListener('click', (e) => {
      // If an item is selected, prevent any action
      if (this.searchInput.classList.contains('has-selection')) {
        e.preventDefault();
        e.stopPropagation();
        return;
      }

      // If no item is selected, show results for non-empty queries
      let query = this.searchInput.dataset.originalValue || this.searchInput.value.trim();
      if (query !== '') {
        // Capitalize the first letter on click too
        if (query.length > 0 && !this.searchInput.dataset.originalValue) {
          query = query.charAt(0).toUpperCase() + query.slice(1);
          this.searchInput.value = query; // Update input value with capitalized version
        }
        this.filterAndDisplayResults(query);
      }
    });

    // Handle keyboard navigation
    // Remove any existing listener first
    if (this._keydownHandler) {
      this.searchInput.removeEventListener('keydown', this._keydownHandler);
    }

    // Store the handler so we can remove it later if needed
    this._keydownHandler = (e) => {
      // If an item is selected, prevent keyboard navigation
      if (this.searchInput.classList.contains('has-selection')) {
        // Only allow Escape key to clear selection
        if (e.key === 'Escape') {
          e.preventDefault();
          this.handleRemoveSelection();
        }
        return;
      }

      // Explicitly handle Space key for typing
      if (e.key === ' ' || e.key === 'Spacebar') {
        // Prevent Space from scrolling the page or triggering other actions
        // But allow it to be typed into the input
        e.stopPropagation(); // Stop it from bubbling to other handlers
        return; // Let the default typing behavior work
      }

      const isInputEmpty = this.searchInput.value.trim() === '';

      // Only handle specific navigation keys - let all other keys (including Space) work normally
      const isNavigationKey = e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === 'Escape';
      if (!isNavigationKey) {
        return; // Don't interfere with normal typing (letters, numbers, space, etc.)
      }

      // Special case: Show all countries when pressing down arrow on empty input and dropdown is not active
      if (e.key === 'ArrowDown' && isInputEmpty && !this.searchDropdown.classList.contains('active')) {
        e.preventDefault();

        // Get all countries and sort alphabetically ignoring accents
        const results = [...this.countries]
          .sort((a, b) => {
            // Sort alphabetically ignoring accents (Å sorts as A, etc.)
            const aNoAccents = removeAccents(a);
            const bNoAccents = removeAccents(b);
            return aNoAccents.localeCompare(bNoAccents);
          });

        // Clear previous results
        this.searchDropdown.innerHTML = '';

        // Show dropdown
        this.searchDropdown.classList.add('active');
        if (this.searchInput) this.searchInput.setAttribute('aria-expanded', 'true');
        this.searchInput.setAttribute('aria-expanded', 'true');

        // Create result items
        results.forEach((country, index) => {
          const item = this.createCountryDropdownItem(country, index);
          this.searchDropdown.appendChild(item);
        });

        // Highlight the first item but don't focus it
        const firstItem = this.searchDropdown.querySelector('.predictive-search__item');
        if (firstItem) {
          firstItem.classList.add('active');
        }

        return;
      }

      // Handle arrow key navigation when dropdown is active
      if (this.searchDropdown.classList.contains('active') && (e.key === 'ArrowDown' || e.key === 'ArrowUp')) {
        e.preventDefault();
        e.stopPropagation();

        const items = this.searchDropdown.querySelectorAll('.predictive-search__item:not(.no-results)');
        if (items.length === 0) return;

        const activeIndex = Array.from(items).findIndex(item => item.classList.contains('active'));

        let newIndex;
        if (e.key === 'ArrowDown') {
          // Move down: if nothing selected, select first; if at last, wrap to first
          if (activeIndex === -1) {
            newIndex = 0;
          } else if (activeIndex === items.length - 1) {
            newIndex = 0; // Wrap to first
          } else {
            newIndex = activeIndex + 1;
          }
        } else { // ArrowUp
          // Move up: if nothing selected or at first, do nothing; otherwise go to previous
          if (activeIndex === -1 || activeIndex === 0) {
            return; // Don't do anything
          } else {
            newIndex = activeIndex - 1;
          }
        }

        // Remove active class from all items
        items.forEach(item => item.classList.remove('active'));

        items[newIndex].classList.add('active');
        items[newIndex].scrollIntoView({ block: 'nearest', behavior: 'smooth' });

        return;
      }

      // Handle other keys when dropdown is active
      if (this.searchDropdown.classList.contains('active')) {
        const items = this.searchDropdown.querySelectorAll('.predictive-search__item:not(.no-results)');
        const activeIndex = Array.from(items).findIndex(item => item.classList.contains('active'));

        switch (e.key) {
          case 'Enter':
            e.preventDefault();
            if (activeIndex !== -1) {
              // Simulate a click on the active item
              items[activeIndex].click();
            } else if (items.length === 1) {
              // If there's only one result, select it on Enter
              items[0].click();
            } else if (items.length > 0 && !isInputEmpty) {
              // If there are results but none is focused, select the first one
              items[0].click();
            } else {
              // If no results, add no-results class
              this.searchInput.classList.add('no-results');
            }
            break;
          case 'Escape':
            e.preventDefault();
            this.searchDropdown.classList.remove('active');
      if (this.searchInput) this.searchInput.setAttribute('aria-expanded', 'false');
            this.searchInput.setAttribute('aria-expanded', 'false');
            this.searchInput.blur();
            break;
        }
      }
    };

    // Add the event listener
    this.searchInput.addEventListener('keydown', this._keydownHandler);

    // Handle click outside to close dropdown
    document.addEventListener('click', (e) => {
      if (!this.searchBox.contains(e.target) && !this.searchDropdown.contains(e.target)) {
        this.searchDropdown.classList.remove('active');
      if (this.searchInput) this.searchInput.setAttribute('aria-expanded', 'false');
      }
    });

    // Prevent clicks on the dropdown from closing it
    this.searchDropdown.addEventListener('click', (e) => {
      e.stopPropagation();
    });

    const searchInput = document.querySelector('.search-input');
    if (!searchInput) return;

    // Ensure input is always interactive
    searchInput.style.pointerEvents = 'auto';
    searchInput.style.userSelect = 'text';

    // Add these event listeners to the existing ones
    searchInput.addEventListener('focus', (e) => {
      e.stopPropagation();
      searchInput.style.cursor = 'text';
    }, { passive: true });

    searchInput.addEventListener('blur', (e) => {
      e.stopPropagation();
    }, { passive: true });
  }

  async handleProductSelect(handle) {

    // Update the search input and show selected item
    if (this.searchInput) {
      this.searchInput.value = handle;
      this.searchInput.classList.add('has-selection');
    }

    // Show selected item
    this.showSelectedItem(handle);

    // Hide dropdown
    if (this.searchDropdown) {
      this.searchDropdown.classList.remove('active');
      if (this.searchInput) this.searchInput.setAttribute('aria-expanded', 'false');
    }
  }

  checkCartState() {
    fetch('/cart.js')
      .then(response => response.json())
      .then(cart => {
        const cartDrawer = document.querySelector('#MinimogCartDrawer');
        if (cartDrawer) {
          const cartFooter = cartDrawer.querySelector('#MinimogCartDrawerFooter');
          const cartFooterWrapper = cartDrawer.querySelector('.m-cart-drawer__footer--wrapper');
          if (cartFooter && cartFooterWrapper) {
            if (cart.item_count > 0) {
              cartFooter.style.display = 'block';
              cartFooterWrapper.style.display = 'block';
              cartFooterWrapper.classList.remove('m:hidden');
              cartFooterWrapper.classList.add('m:block');
            } else {
              // Hide both elements completely
              cartFooter.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important; height: 0 !important; padding: 0 !important; margin: 0 !important; overflow: hidden !important; position: absolute !important;';
              cartFooterWrapper.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important; height: 0 !important; padding: 0 !important; margin: 0 !important; overflow: hidden !important; position: absolute !important;';

              // Also hide all child elements
              Array.from(cartFooterWrapper.children).forEach(child => {
                child.style.cssText = 'display: none !important; visibility: hidden !important; opacity: 0 !important; height: 0 !important; padding: 0 !important; margin: 0 !important; overflow: hidden !important; position: absolute !important;';
              });
            }
          }

          // Also check for empty cart message
          const emptyCartMessage = cartDrawer.querySelector('.m-cart-drawer__empty');
          if (emptyCartMessage) {
            emptyCartMessage.style.display = cart.item_count > 0 ? 'none' : 'block';
          }

          // Force a reflow to ensure styles are applied
          cartDrawer.offsetHeight;
        }
      })
      .catch(console.error);
  }

  updatePrice() {
    if (!this.currentVariant || !this.currentVariant.productData) {
      return;
    }

    const product = this.currentVariant.productData;
    const priceDisplay = document.querySelector('.hero-screen-3 .price-display');
    if (!priceDisplay) return;

    // Get quantity from the Minimog quantity input
    const quantity = parseInt(this.quantityInput?.value || 1);

    let priceInCents;
    let compareAtPriceInCents = null;

    const usesDailySlider = this.currentTabType === 'Daily Plans' && this.currentVariant?.hasDaysOption;

    // Only handle slider-driven price updates for Daily Plans with the day picker enabled
    if (usesDailySlider) {
      // Extract the High Speed Data value from the current variant's title
      const currentTitle = this.currentVariant.title;
      const highSpeedData = currentTitle.split(' / ')[0];

      // Find the variant that matches both the High Speed Data and Plans values
      const matchingVariant = product.variants.find(variant => {
        const [variantHighSpeedData, variantPlans] = variant.title.split(' / ');
        const plansValue = parseInt(variantPlans, 10);
        return variantHighSpeedData === highSpeedData && plansValue === this.days;
      });

      if (matchingVariant) {
        // Price is in cents, so multiply by quantity
        priceInCents = parseFloat(matchingVariant.price) * quantity;
        if (matchingVariant.compare_at_price) {
          compareAtPriceInCents = parseFloat(matchingVariant.compare_at_price) * quantity;
        }
      } else {
        priceInCents = parseFloat(this.currentVariant.price) * quantity;
        if (this.currentVariant.compare_at_price) {
          compareAtPriceInCents = parseFloat(this.currentVariant.compare_at_price) * quantity;
        }
      }
    } else {
      // For non-Daily Plans, just multiply the current variant price by quantity
      priceInCents = parseFloat(this.currentVariant.price) * quantity;
      if (this.currentVariant.compare_at_price) {
        compareAtPriceInCents = parseFloat(this.currentVariant.compare_at_price) * quantity;
      }
    }

    // Use the theme's price display logic with variant object
    updatePriceDisplay(priceDisplay, this.currentVariant, quantity, product.handle);
  }

  // Call this instead of setupDaysSlider() whenever you need to force a full
  // re-initialization (e.g. when the product/variant changes between screens).
  // All other callers that just want to show/hide the slider use setupDaysSlider()
  // which is now guarded against double-init.
  resetDaysSlider() {
    if (this.daysSlider) {
      delete this.daysSlider.dataset.sliderInitialized;
    }
    this.setupDaysSlider();
  }

  setupDaysSlider() {
    if (!this.daysSlider) return;

    const thumb = this.daysSlider.querySelector('.thumb');
    const thumbValue = this.daysSlider.querySelector('.thumb-value');
    const progress = this.daysSlider.querySelector('.slider-progress');
    const daysValueEl = document.querySelector('.days-value');
    const daysSliderContainer = document.querySelector('.slider-container');
    const sliderLabel = document.querySelector('.slider-label');

    // Add placeholder element
    if (daysSliderContainer && !daysSliderContainer.querySelector('.duration-placeholder')) {
      const placeholder = document.createElement('div');
      placeholder.className = 'duration-placeholder';
      placeholder.setAttribute('data-i18n-key', 'slide_to_set');
      placeholder.textContent = this.getDetailPanelText('slide_to_set');
      daysSliderContainer.appendChild(placeholder);
    }

    // Check if we're in the Daily Plans tab AND if the current variant has days option
    const isDailyPlan = this.currentTabType === 'Daily Plans';
    const hasDaysOption = this.currentVariant?.hasDaysOption || false;

    // Show or hide days slider based on whether it's a daily plan with days option
    if (daysSliderContainer) {
      if (isDailyPlan && hasDaysOption) {
        daysSliderContainer.style.display = 'block';
        daysSliderContainer.style.visibility = 'visible';
        if (sliderLabel) {
          sliderLabel.style.display = 'block';
          sliderLabel.style.visibility = 'visible';
        }
      } else {
        daysSliderContainer.style.display = 'none';
        if (sliderLabel) sliderLabel.style.display = 'none';
        return; // Exit early if slider should be hidden
      }
    }

    // GUARD: prevent duplicate event-listener stacking on mobile.
    // Every call to setupDaysSlider() creates new closure-bound handlers
    // (startDragging, onDrag, stopDragging). Without this guard, calling
    // setupDaysSlider() multiple times leaves stale handlers attached that
    // share a stale isDragging flag → flicker / snap-back on touch devices.
    if (this.daysSlider.dataset.sliderInitialized === 'true') {
      return;
    }
    this.daysSlider.dataset.sliderInitialized = 'true';

    let isDragging = false;
    const minDays = 1;
    const maxDays = 30;
    const maxPosition = daysSliderContainer.offsetWidth - (thumb.offsetWidth * 1.125) - 2;

    const getVariantsForCurrentDataAmount = () => {
      if (!this.currentVariant || !this.currentVariant.productData) return [];

      const product = this.currentVariant.productData;
      // Extract the full data amount including "Plus" if present
      // Format: "500MB/Day Plus / 30" or "500MB/Day / 30"
      // The data amount is everything before the last "/" (which contains the days)
      const titleParts = this.currentVariant.title.split('/');
      titleParts.pop(); // Remove the days part
      const dataAmountWithPlus = titleParts.join('/').trim(); // Rejoin in case there are multiple slashes

      return product.variants.filter(variant => {
        const variantTitleParts = variant.title.split('/');
        variantTitleParts.pop(); // Remove the days part
        const variantDataAmountWithPlus = variantTitleParts.join('/').trim();
        return variantDataAmountWithPlus === dataAmountWithPlus;
      });
    };

    const cacheVariantPrices = async (product) => {
      this.variantPricesCache.clear();

      // Get current data amount to filter relevant variants (including Plus if present)
      const titleParts = this.currentVariant?.title.split('/');
      if (!titleParts || titleParts.length < 2) return;
      titleParts.pop(); // Remove the days part
      const dataAmountWithPlus = titleParts.join('/').trim();
      if (!dataAmountWithPlus) return;

      const relevantVariants = product.variants.filter(variant => {
        const variantTitleParts = variant.title.split('/');
        variantTitleParts.pop(); // Remove the days part
        const variantDataAmountWithPlus = variantTitleParts.join('/').trim();
        return variantDataAmountWithPlus === dataAmountWithPlus;
      });

      // Pre-populate price cache using server-rendered prices (matches cart.currency exactly)
      // `product` is already the parameter of cacheVariantPrices — use it directly
      relevantVariants.forEach(variant => {
        try {
          const variantDays = parseInt(variant.title.split('/').pop().trim());
          // If already cached on the variant, use instantly
          if (variant._renderedPrice) {
            const priceHtml = `
              <div class="m-price__regular">
                <span class="m-price-item m-price-item--regular">${variant._renderedPrice}</span>
              </div>
            `;
            this.variantPricesCache.set(variantDays, {
              variant: variant,
              priceHtml: priceHtml,
              priceText: variant._renderedPrice
            });
          } else {
            this.fetchServerRenderedPrice(null, variant, product.handle).then(priceText => {
              if (priceText) {
                variant._renderedPrice = priceText; // Cache for reuse
                const priceHtml = `
                  <div class="m-price__regular">
                    <span class="m-price-item m-price-item--regular">${priceText}</span>
                  </div>
                `;
                this.variantPricesCache.set(variantDays, {
                  variant: variant,
                  priceHtml: priceHtml,
                  priceText: priceText
                });
              }
            }).catch(() => {});
          }
        } catch (error) {}
      });

    };

    const updateVariant = async (targetDays, isImmediate = false) => {
      if (!this.currentVariant || !this.currentVariant.productData) return;

      const product = this.currentVariant.productData;

      console.group('💰 Days Slider Variant Update');

      // Parse data amount from title like "1GB/Day Plus / 30" or "1GB/Day / 30"
      // Extract everything before the last "/" (which contains days)
      const titleParts = this.currentVariant.title.split('/');
      titleParts.pop();
      const dataAmountWithPlus = titleParts.join('/').trim();

      // Get variants for this data amount (including Plus distinction)
      const dataVariants = getVariantsForCurrentDataAmount();

      // Find the exact variant with matching days
      const exactMatch = dataVariants.find(variant => {
        const variantDays = parseInt(variant.title.split('/').pop().trim());
        return variantDays === targetDays;
      });

      if (exactMatch) {
        this.currentVariant = exactMatch;
        this.currentVariant.productData = product; // Preserve product data

        // Update form's variant ID
        const variantInput = document.querySelector('#hero-search-form input[name="id"]');
        if (variantInput) {
          variantInput.value = exactMatch.id;
        }

        const priceDisplay = document.querySelector('.hero-screen-3 .price-display');
        if (priceDisplay) {
          // For immediate updates (during sliding), use cached server-rendered prices
          if (isImmediate) {
            const cachedPrice = this.variantPricesCache.get(targetDays);
            if (cachedPrice) {
              // Use cached server-rendered price structure
              priceDisplay.innerHTML = cachedPrice.priceHtml;

              // Apply quantity multiplier if needed
              const quantity = parseInt(this.quantityInput?.value || 1);
              if (quantity > 1) {
                const priceNodes = priceDisplay.querySelectorAll('.m-price-item, .price-item, .price');
                priceNodes.forEach(node => {
                  const priceText = node.textContent.trim();
                  // FIX: capture full decimal value including comma-separated formats
                  // e.g. "€9,99" → base 9.99, not 9.00 (old broken pattern stopped at comma)
                  const numericMatch = priceText.match(/[€$£¥₹\s]*([\d]+[,.][\d]+|[\d]+)/);
                  if (numericMatch) {
                    const rawValue = numericMatch[1].replace(/,(?=\d{2}$)/, '.').replace(/[.,](?=\d{3})/g, '');
                    const baseValue = parseFloat(rawValue);
                    const currencyCode = this.currentVariant?.currencyCode || window.currentCurrency || 'EUR';
                    node.textContent = formatPriceByCurrency(baseValue * quantity, currencyCode);
                  }
                });
              }

            } else {
              // If no cached price, don't leave old price showing - fetch immediately
              // This will be handled by the server fetch below with no debounce
            }
          }

          // Cancel any pending server request
          if (this.priceUpdateAbortController) {
            this.priceUpdateAbortController.abort();
            this.priceUpdateAbortController = null;
          }

          // Clear existing timer
          if (this.priceUpdateTimer) {
            clearTimeout(this.priceUpdateTimer);
            this.priceUpdateTimer = null;
          }

          // OPTIMIZATION: Skip fetch entirely if we have cached price data
          const cachedPrice = this.variantPricesCache.get(targetDays);
          if (cachedPrice && isImmediate) {
            console.groupEnd();
            return; // Skip the fetch entirely
          }

          // FIX: Use the Storefront API price directly (already in correct currency
          // from the @inContext query). The previous page-fetch approach returned the
          // store's base currency HTML, causing a visible price drop on non-base
          // currencies when Minimog's currency converter then re-converted it.
          // updatePriceDisplay() calls formatPriceByCurrency() which delegates to
          // Minimog's formatMoney → correct symbol, decimal separator, and locale.
          if (cachedPrice) {
            // Already have server-rendered price for this day count — use it
            priceDisplay.innerHTML = cachedPrice.priceHtml;

            const quantity = parseInt(this.quantityInput?.value || 1);
            if (quantity > 1) {
              const priceNodes = priceDisplay.querySelectorAll('.m-price-item, .price-item, .price');
              priceNodes.forEach(node => {
                // FIX: use a regex that captures the full decimal value including
                // comma-as-separator formats like "9,99" (EUR/NL) — the old pattern
                // /(\d,\.]+)/ stopped at the comma, producing "9" instead of "9.99"
                // which caused the price to silently drop by the fractional part.
                const priceText = node.textContent.trim();
                const numericMatch = priceText.match(/[€$£¥₹\s]*([\d]+[,.][\d]+|[\d]+)/);
                if (numericMatch) {
                  // Normalise both "9,99" (European) and "9.99" (US/UK) to a float
                  const rawValue = numericMatch[1].replace(/,(?=\d{2}$)/, '.').replace(/[.,](?=\d{3})/g, '');
                  const baseValue = parseFloat(rawValue);
                  const currencyCode = this.currentVariant?.currencyCode || window.currentCurrency || 'EUR';
                  node.textContent = formatPriceByCurrency(baseValue * quantity, currencyCode);
                }
              });
            }
          } else {
            // No cache yet — render using Storefront API variant data (correct currency)
            const quantity = parseInt(this.quantityInput?.value || 1);
            const priceHtml = `<div class="m-price__regular"><span class="m-price-item m-price-item--regular">${formatPriceByCurrency((exactMatch.price || 0) * quantity, exactMatch.currencyCode || window.currentCurrency || 'EUR')}</span></div>`;
            priceDisplay.innerHTML = priceHtml;

            // Warm the cache asynchronously so the next slide is instant
            this.priceUpdateTimer = setTimeout(async () => {
              try {
                this.priceUpdateAbortController = new AbortController();
                const priceText = await this.fetchServerRenderedPrice(null, exactMatch, product.handle);
                if (priceText) {
                  exactMatch._renderedPrice = priceText;
                  const cachedHtml = `<div class="m-price__regular"><span class="m-price-item m-price-item--regular">${priceText}</span></div>`;
                  this.variantPricesCache.set(targetDays, {
                    variant: exactMatch,
                    priceHtml: cachedHtml,
                    priceText: priceText
                  });
                  // Only update display if this day is still the current one
                  if (this.days === targetDays) {
                    priceDisplay.innerHTML = cachedHtml;
                  }
                }
              } catch (error) {
                if (error.name !== 'AbortError') {
                  // Storefront API price already shown — silent fail is fine
                }
              }
            }, 400);
          }
        }
      }

      console.groupEnd();
    };

    const updateSlider = (clientX, isDragging = false) => {
      if (!daysSliderContainer || !thumb || !progress) return;

      const rect = daysSliderContainer.getBoundingClientRect();
      const offsetX = clientX - rect.left - (thumb.offsetWidth / 2);
      const position = Math.max(0, Math.min(offsetX, maxPosition));

      // Calculate percentage and days value
      const percentage = (position / maxPosition) * 100;
      const targetDays = Math.round((percentage / 100) * (maxDays - minDays) + minDays);

      // Update thumb position
      thumb.style.left = `${position}px`;

      // Update progress bar
      const thumbRightEdge = position + (thumb.offsetWidth);
      const progressWidth = Math.min(thumbRightEdge + 6, daysSliderContainer.offsetWidth - 2);
      progress.style.width = `${progressWidth}px`;

      // Set data-value attribute on progress bar for CSS targeting
      progress.setAttribute('data-value', targetDays);

      // Show placeholder only when value is 1
      if (targetDays === 1) {
        daysSliderContainer.classList.remove('has-value');
      } else {
        daysSliderContainer.classList.add('has-value');
      }

      // Update displayed values
      if (thumbValue) thumbValue.textContent = targetDays;
      if (daysValueEl) daysValueEl.textContent = targetDays === 1 ? '1 day' : `${targetDays} days`;

      // Store the days value
      this.days = targetDays;

      // Update the variant - use immediate updates during dragging, server updates when stopped
      updateVariant(targetDays, isDragging);
    };

    const startDragging = (e) => {
      if (e.type.includes('touch')) {
        e.preventDefault();
        e.stopPropagation();
      }

      isDragging = true;
      thumb.style.cursor = 'grabbing';

      const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
      updateSlider(clientX, true);

      if (e.type.includes('touch')) {
        document.addEventListener('touchmove', onDrag, { passive: false });
        document.addEventListener('touchend', stopDragging, { passive: false });
        document.addEventListener('touchcancel', stopDragging, { passive: false });
      } else {
        document.addEventListener('mousemove', onDrag);
        document.addEventListener('mouseup', stopDragging);
      }
    };

    const onDrag = (e) => {
      if (!isDragging) return;
      e.preventDefault();
      e.stopPropagation();

      const clientX = e.type.includes('touch') ? e.touches[0].clientX : e.clientX;
      updateSlider(clientX, true);
    };

    const stopDragging = (e) => {
      if (e && e.type.includes('touch')) {
        e.preventDefault();
        e.stopPropagation();
      }

      if (!isDragging) return;

      isDragging = false;
      thumb.style.cursor = 'grab';

      // Remove listeners FIRST — before any further DOM/state updates
      document.removeEventListener('mousemove', onDrag);
      document.removeEventListener('mouseup', stopDragging);
      document.removeEventListener('touchmove', onDrag);
      document.removeEventListener('touchend', stopDragging);
      document.removeEventListener('touchcancel', stopDragging);

      // Block the ghost click iOS synthesizes ~300ms after touchend.
      // Without this, handleClick fires with the touch-start coordinates
      // (the original thumb position), snapping the slider back.
      daysSliderContainer.dataset.blockNextClick = '1';
      setTimeout(() => { delete daysSliderContainer.dataset.blockNextClick; }, 400);

      // Thumb is already at the correct visual position from the last onDrag.
      // Just trigger the final non-immediate variant/price update using this.days.
      updateVariant(this.days, false);
    };

    const handleClick = (e) => {
      // Ignore ghost clicks synthesized by iOS after touchend
      if (daysSliderContainer.dataset.blockNextClick) return;
      if (e.target !== thumb) {
        updateSlider(e.clientX, false);
      }
    };

    // Clean up old event listeners
    const cleanupEvents = () => {
      thumb.removeEventListener('mousedown', startDragging);
      thumb.removeEventListener('touchstart', startDragging);
      daysSliderContainer.removeEventListener('click', handleClick);
      stopDragging();
    };

    // Remove old event listeners
    cleanupEvents();

    // Add new event listeners with proper touch handling
    thumb.addEventListener('mousedown', startDragging);
    thumb.addEventListener('touchstart', startDragging, { passive: false });
    daysSliderContainer.addEventListener('click', handleClick);

    // Prevent any parent element touch events from interfering
    daysSliderContainer.addEventListener('touchstart', (e) => {
      if (e.target === thumb) {
        e.stopPropagation();
      }
    }, { passive: false });

    // Initialize price cache when slider is set up
    if (this.currentVariant && this.currentVariant.productData) {
      cacheVariantPrices(this.currentVariant.productData).catch(error => {
      });
    }
  }

  setupVariantSelector() {
    // This is a simplified placeholder implementation
  }

  setupAddToCart() {
    const form = document.querySelector('#hero-search-form');
    const variantInput = form?.querySelector('input[name="id"]');
    if (!form || !variantInput) return;

    // Update variant ID when we have a variant selected
    if (this.currentVariant) {
      variantInput.value = this.currentVariant.id;
      variantInput.disabled = false;
    }

    // Handle Buy It Now button
    const buyItNowButton = document.querySelector('.hero-buy-now-button');
    if (buyItNowButton && !buyItNowButton.dataset.heroAtcListener) {
      buyItNowButton.dataset.heroAtcListener = 'true';
      buyItNowButton.addEventListener('click', async (e) => {
        e.preventDefault();

        if (!this.currentVariant) {
          console.error('No variant selected');
          return;
        }

        // Get quantity value
        const quantityInput = form.querySelector('input[name="quantity"]');
        const quantity = quantityInput?.value || 1;

        try {
          // Disable the button during processing
          const originalText = buyItNowButton.textContent;
          const loadingText = (window.themeTranslations && window.themeTranslations.t)
            ? window.themeTranslations.t('loading')
            : 'Loading...';
          buyItNowButton.textContent = loadingText;
          buyItNowButton.disabled = true;

          // Add the item to cart (preserving any existing items)
          const addResponse = await fetch('/cart/add.js', {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Accept': 'application/json'
            },
            body: JSON.stringify({
              items: [{
                id: this.currentVariant.id,
                quantity: parseInt(quantity)
              }]
            })
          });

          if (addResponse.ok) {
            // Redirect directly to checkout
            window.location.href = '/checkout';
          } else {
            const result = await addResponse.json();
            throw new Error(result.description || 'Error adding item to cart');
          }
        } catch (error) {
          console.error('Buy Now Error:', error);

          // Show error message
          if (window.MinimogTheme && window.MinimogTheme.Notification) {
            window.MinimogTheme.Notification.show({
              target: document.body,
              method: 'appendChild',
              type: 'error',
              message: error.message || 'Error adding item to cart',
              delay: 3000
            });
          }

          // Reset button
          buyItNowButton.textContent = originalText;
          buyItNowButton.disabled = false;
        }
      });
    }

    // Listen for form submission (Add to Cart) — only attach once
    if (!form.dataset.heroAtcListener) {
      form.dataset.heroAtcListener = 'true';
      form.addEventListener('submit', async (e) => {
        // Only handle add-to-cart submissions here (not checkout)
        if (e.submitter && e.submitter.name === 'checkout') {
          return; // Let the form submit normally for checkout
        }

        e.preventDefault();

        if (!this.currentVariant) {
          console.error('No variant selected');
          return;
        }

        const submitButton = form.querySelector('[type="submit"][name="add"]');
        if (!submitButton) {
          console.error('Submit button not found');
          return;
        }

        const originalText = submitButton.textContent;
        submitButton.textContent = 'Adding...';
        submitButton.disabled = true;

        try {
          // Get all the values
          const thumbValue = document.querySelector('.hero-screen-3 .thumb-value');
          const daysValue = thumbValue ? parseInt(thumbValue.textContent) : 1;
          const quantityInput = form.querySelector('input[name="quantity"]');
          const quantity = quantityInput?.value || 1;

          // Debug current state before submission
          console.group('Add to Cart Debug');
          console.groupEnd();

          // Create FormData
          const formData = new FormData();
          formData.append('id', this.currentVariant.id);
          formData.append('quantity', quantity);

          // Add to cart
          const response = await fetch(window.MinimogSettings.routes.cart_add_url, {
            method: 'POST',
            headers: {
              'X-Requested-With': 'XMLHttpRequest',
              'Accept': 'application/json'
            },
            body: formData
          });

          const result = await response.json();

          if (response.ok) {
            // Dispatch cart update event
            window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, {
              cart: result,
              source: 'hero-search'
            });

            // Show success message
            window.MinimogTheme.Notification.show({
              target: document.body,
              method: 'appendChild',
              type: 'success',
              message: window.MinimogStrings.itemAdded || 'Item added to cart',
              delay: 3000
            });

            // Update cart
            if (window.MinimogTheme.Cart) {
              MinimogTheme.Cart.updateCartCount(result.items_count);
              MinimogTheme.Cart.updateCart();
            }
            const cartDrawer = document.querySelector('#MinimogCartDrawer');
            if (window.sanitizeCartOptionLabels && cartDrawer) {
              window.sanitizeCartOptionLabels(cartDrawer);
              requestAnimationFrame(() => window.sanitizeCartOptionLabels(cartDrawer));
            }
            if (cartDrawer?.classList.contains('m-cart-drawer--active')) {
              clearTimeout(cartDrawer.autoCloseAfterAddTimer);
              cartDrawer.autoCloseAfterAddTimer = setTimeout(() => {
                cartDrawer.close();
              }, 6000);
            }

            // Reset button
            submitButton.textContent = 'Added!';
            setTimeout(() => {
              submitButton.textContent = originalText;
              submitButton.disabled = false;
            }, 2000);

            // Direct approach to reset hero search

            // First approach: Try global function
            setTimeout(() => {
              if (typeof window.resetHeroSearch === 'function') {
                window.resetHeroSearch();
              } else {
                console.error('Global resetHeroSearch function not available');

                // Second approach: Direct DOM manipulation
                const screen1 = document.querySelector('.hero-screen-1');
                const screen2 = document.querySelector('.hero-screen-2');
                const screen3 = document.querySelector('.hero-screen-3');

                if (screen1 && screen2 && screen3) {
                  // Reset screens
                  screen1.style.transform = 'translateX(0)';
                  screen1.style.opacity = '1';
                  screen1.style.visibility = 'visible';

                  screen2.style.transform = 'translateX(100%)';
                  screen2.style.opacity = '0';
                  screen2.style.visibility = 'hidden';

                  screen3.style.transform = 'translateX(100%)';
                  screen3.style.opacity = '0';
                  screen3.style.visibility = 'hidden';

                  // Reset search input
                  const searchInput = document.querySelector('#search-input');
                  if (searchInput) {
                    searchInput.value = '';
                    searchInput.classList.remove('has-selection');
                    searchInput.classList.remove('no-results');
                    searchInput.removeAttribute('readonly');
                    searchInput.style.display = '';
                    searchInput.style.opacity = '1';
                  }

                  // Reset selected item
                  const selectedItem = document.querySelector('.selected-item');
                  if (selectedItem) {
                    selectedItem.style.display = 'none';
                    selectedItem.innerHTML = '';
                  }

                  // Third approach: Click the debug button
                  const debugButton = document.getElementById('debug-reset-button');
                  if (debugButton) {
                    debugButton.click();
                  }
                }
              }
            }, 1000);

          } else {
            throw new Error(result.description || 'Error adding item to cart');
          }
        } catch (error) {
          console.error('Add to Cart Error:', error);

          // Show error message
          window.MinimogTheme.Notification.show({
            target: document.body,
            method: 'appendChild',
            type: 'error',
            message: error.message || 'Error adding item to cart',
            delay: 3000
          });

          // Reset button
          submitButton.textContent = 'Error';
          setTimeout(() => {
            submitButton.textContent = originalText;
            submitButton.disabled = false;
          }, 2000);
        }
      });
    }
  }

  // Add method to handle Find Out buttons specifically
  setupFindOutButtons() {
    const resolveCompatibilityContent = (triggerElement) => {
      if (typeof window.getCompatibilityModalContent === 'function') {
        return window.getCompatibilityModalContent(triggerElement);
      }

      const scopedSection = triggerElement && triggerElement.closest
        ? triggerElement.closest('[data-compatibility-section]')
        : null;

      if (scopedSection) {
        const scopedContent = scopedSection.querySelector('[data-compatibility-modal-source]');
        if (scopedContent) return scopedContent;
      }

        return document.querySelector('[data-compatibility-modal-source]');
    };

    const openCompatibilityModalFromContent = (compatibilityContent) => {
      if (!compatibilityContent) return false;

      if (typeof window.openCompatibilityModal === 'function') {
        try {
          window.openCompatibilityModal(compatibilityContent);
          return true;
        } catch (error) {
          console.error('Compatibility modal opener failed, falling back to direct modal:', error);
        }
      }

      if (window.MinimogTheme && MinimogTheme.Modal) {
        const modal = new MinimogTheme.Modal();
        const content = document.createElement('div');
        content.innerHTML = compatibilityContent.innerHTML;
        content.classList.add('compatibility-modal-content');
        if (typeof window.initializeCompatibilityFlow === 'function') {
          try {
            window.initializeCompatibilityFlow(content, modal);
          } catch (error) {
            console.error('Compatibility flow initialization failed in fallback modal:', error);
          }
        }
        modal.appendChild(content);
        modal.setWidth('90%');
        modal.setSizes('m-form-ask-question m-gradient');
        modal.open();
        return true;
      }

      return false;
    };

    const findOutButtons = document.querySelectorAll('.m-richtext__button a');

    findOutButtons.forEach((button, index) => {
      const opensCompatibilityModal = button.hasAttribute('data-open-compatibility-modal')
        || (button.getAttribute('href') && button.getAttribute('href').includes('compatibility'));

      if (opensCompatibilityModal) {

        // Create new button to replace the old one (to remove any previous event listeners)
        const newButton = button.cloneNode(true);
        button.parentNode.replaceChild(newButton, button);

        // Add click event listener to open compatibility modal
        newButton.addEventListener('click', function (event) {
          event.preventDefault();

          const compatibilityContent = resolveCompatibilityContent(this);

          if (compatibilityContent) {
            if (!openCompatibilityModalFromContent(compatibilityContent)) {
              console.error('MinimogTheme.Modal is not available');
            }
          } else {
            console.error('Compatibility content not found, fetching from server');

            // Fetch compatibility section as fallback
            fetch('/?section_id=compatibility-section&v=' + new Date().getTime())
              .then(response => response.text())
              .then(html => {
                const tempDiv = document.createElement('div');
                tempDiv.innerHTML = html;

                const fetchedContent = tempDiv.querySelector('[data-compatibility-modal-source]');
                if (!openCompatibilityModalFromContent(fetchedContent)) {
                  console.error('Could not find compatibility content in fetched HTML');
                }
              })
              .catch(error => console.error('Error fetching compatibility section:', error));
          }
        });

      }
    });

    // Also handle buttons with class open-modal
    const openModalLinks = document.querySelectorAll('.open-modal');

    openModalLinks.forEach((link, index) => {
      // Remove any existing event listeners to prevent duplication
      const newLink = link.cloneNode(true);
      link.parentNode.replaceChild(newLink, link);

      newLink.addEventListener('click', function (event) {
        event.preventDefault();

        const compatibilityContent = resolveCompatibilityContent(this);

        if (compatibilityContent) {
          openCompatibilityModalFromContent(compatibilityContent);
        } else {
          fetch('/?section_id=compatibility-section')
            .then(response => response.text())
            .then(html => {
              const tempDiv = document.createElement('div');
              tempDiv.innerHTML = html;

              const fetchedContent = tempDiv.querySelector('[data-compatibility-modal-source]');
              openCompatibilityModalFromContent(fetchedContent);
            })
            .catch(error => console.error('Error fetching compatibility section:', error));
        }
      });
    });
  }

  // Add a new method to handle the "What's the difference?" button
  setupWhatsTheDifferenceButton() {

    const explanationContainer = document.querySelector('.col-explanation-container');
    const explanationLink = explanationContainer
      ? explanationContainer.querySelector('.col-explanation-link')
      : null;
    const explanationTooltip = explanationContainer
      ? explanationContainer.querySelector('.col-explanation-tooltip')
      : null;
    const transitionDuration = 800;

    if (!explanationLink || !explanationTooltip) {
      return;
    }

    // Ensure tooltip is hidden initially
    explanationTooltip.style.display = 'none';

    // Make sure tooltip can receive mouse events
    explanationTooltip.style.pointerEvents = 'auto';

    // Remove any existing event listeners by cloning and replacing
    const newExplanationLink = explanationLink.cloneNode(true);
    explanationLink.parentNode.replaceChild(newExplanationLink, explanationLink);

    const isMobile = window.matchMedia('(max-width: 768px)').matches;

    const openTooltip = () => {
      if (explanationTooltip._hideTimeout) {
        clearTimeout(explanationTooltip._hideTimeout);
        explanationTooltip._hideTimeout = null;
      }
      explanationTooltip.style.display = 'block';
      explanationTooltip.offsetHeight;
      explanationTooltip.classList.add('active');
    };

    const closeTooltip = () => {
      explanationTooltip.classList.remove('active');
      if (explanationTooltip._hideTimeout) {
        clearTimeout(explanationTooltip._hideTimeout);
      }
      explanationTooltip._hideTimeout = setTimeout(() => {
        if (!explanationTooltip.classList.contains('active')) {
          explanationTooltip.style.display = 'none';
        }
        explanationTooltip._hideTimeout = null;
      }, transitionDuration);
    };

    const handleClick = (e) => {
      e.preventDefault();
      e.stopPropagation();

      if (explanationTooltip.classList.contains('active')) {
        closeTooltip();
      } else {
        openTooltip();
      }
    };

    // Add click event for both mobile and desktop
    newExplanationLink.addEventListener('click', handleClick, { passive: false });

    // Setup the close button
    const closeButton = explanationTooltip.querySelector('.tooltip-close-button');
    if (closeButton) {
      const newCloseButton = closeButton.cloneNode(true);
      closeButton.parentNode.replaceChild(newCloseButton, closeButton);

      newCloseButton.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        closeTooltip();
      });
    }

    // Handle outside clicks
    document.addEventListener('click', (event) => {
      if (!explanationTooltip.contains(event.target) &&
        event.target !== newExplanationLink) {
        closeTooltip();
      }
    });

    // Stop propagation of clicks inside the tooltip
    explanationTooltip.addEventListener('click', (e) => {
      e.stopPropagation();
    });

  }

  // Handle variant selection
  selectVariant(variant, product) {

    // Remove selected class from all variants
    const allVariants = document.querySelectorAll('.collection-product-variant');
    allVariants.forEach(item => {
      item.classList.remove('selected');

      // Reset any inline styles that might be overriding our CSS
      if (item.style) {
        item.style.borderColor = '';
        item.style.backgroundColor = '';
      }
    });

    // Find the clicked variant element and add selected class
    const variantElements = document.querySelectorAll(`.collection-product-variant[data-variant-id="${variant.id}"]`);
    variantElements.forEach(element => {
      element.classList.add('selected');

      // Apply styling directly to ensure it overrides any CSS
      element.style.borderColor = '#0071dc';
      element.style.backgroundColor = '#f5f9ff';
    });

    // Store the product data with the variant
    variant.productData = product;

    // Extract the data option (e.g., "500mb/day", "1gb/day", "2gb/day")
    const variantTitle = variant.title;
    const dataOptionMatch = variantTitle.match(/([\d.]+\s*[gmk]b\/day)/i);
    const optionTitle = dataOptionMatch ? dataOptionMatch[1] : variantTitle;
    variant.optionTitle = optionTitle;

    // Set the current variant
    this.currentVariant = variant;

    // Set up the days slider if applicable
    this.resetDaysSlider();

    // Update price display
    this.updatePrice();

    return this.currentVariant;
  }

  // Helper method to show a floating debug overlay with filtering information (now disabled)
  showFilterDebugOverlay(filterInfo) {
    // Debug functionality has been removed
    return;
  }

  // Helper method to update debug panel with product information (now disabled)
  updateDebugProductsInfo(matchingProducts) {
    // Debug panel has been removed
    return;
  }

  // Helper method to update debug panel with filter information (now disabled)
  updateDebugPanel(filters) {
    // Debug panel has been removed
    return;
    const debugFiltersEl = document.querySelector('.debug-filters');
    if (debugFiltersEl) {
      // Format the filters for display
      const formattedFilters = {
        country: filters.country || 'Not specified',
        type: filters.type || 'Not specified',
        top_up: filters.top_up || 'Not specified'
      };

      // Get the shop domain for admin links
      const shopDomain = window.Shopify?.shop || document.location.host;
      const adminUrl = `https://${shopDomain}/admin/products`;

      // Create a more helpful debug panel with formatting guidance
      debugFiltersEl.innerHTML = `
        <h4>Current Filter Values:</h4>
        <pre>${JSON.stringify(formattedFilters, null, 2)}</pre>
        
        <h4>Expected Metafield Values:</h4>
        <table style="width: 100%; border-collapse: collapse; margin-top: 10px;">
          <tr style="background-color: #f5f5f5;">
            <th style="text-align: left; padding: 8px; border: 1px solid #ddd;">Metafield</th>
            <th style="text-align: left; padding: 8px; border: 1px solid #ddd;">Expected Value</th>
            <th style="text-align: left; padding: 8px; border: 1px solid #ddd;">Case Sensitive</th>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd;">filters.country</td>
            <td style="padding: 8px; border: 1px solid #ddd;"><code>${filters.country || 'N/A'}</code></td>
            <td style="padding: 8px; border: 1px solid #ddd;">Yes</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd;">filters.type</td>
            <td style="padding: 8px; border: 1px solid #ddd;"><code>${filters.type || 'N/A'}</code></td>
            <td style="padding: 8px; border: 1px solid #ddd;">No (case insensitive)</td>
          </tr>
          <tr>
            <td style="padding: 8px; border: 1px solid #ddd;">filters.top_up</td>
            <td style="padding: 8px; border: 1px solid #ddd;"><code>${filters.top_up || 'N/A'}</code></td>
            <td style="padding: 8px; border: 1px solid #ddd;">Yes</td>
          </tr>
        </table>
        
        <div style="margin-top: 15px; background-color: #d1ecf1; padding: 10px; border: 1px solid #bee5eb; border-radius: 4px;">
          <h4>Format Handling:</h4>
          <p>The code now handles multiple data formats:</p>
          <ul>
            <li><strong>Multi-country arrays:</strong> If <code>filters.country</code> is stored as <code>["Japan","China","Korea"]</code>:
              <ul>
                <li>If filter is for <code>single</code> country: Product matches only if selected country is in the array</li>
                <li>If filter is for <code>multi</code> region: Product matches by default (regional product)</li>
              </ul>
            </li>
            <li><strong>Boolean format:</strong> We convert <code>true</code> → <code>True</code> and <code>false</code> → <code>False</code></li>
            <li><strong>Multiple namespaces:</strong> We check both <code>filters.X</code> and <code>global.X</code> formats</li>
          </ul>
        </div>
        
        <div style="margin-top: 15px; background-color: #fff3cd; padding: 10px; border: 1px solid #ffeeba; border-radius: 4px;">
          <h4>Step-by-Step Filter Debugging:</h4>
          
          <div style="margin-top: 10px; border-left: 3px solid #17a2b8; padding-left: 10px;">
            <button id="check-country-only" class="debug-filter-btn" style="margin-top: 5px; padding: 8px 15px; background-color: #0066cc; color: white; border: none; border-radius: 4px; cursor: pointer; width: 100%;">
              Step 1: Find Products with "${filters.country || 'Japan'}" only
            </button>
          </div>
          
          <div style="margin-top: 10px; border-left: 3px solid #28a745; padding-left: 10px;">
            <button id="check-country-type" class="debug-filter-btn" style="margin-top: 5px; padding: 8px 15px; background-color: #28a745; color: white; border: none; border-radius: 4px; cursor: pointer; width: 100%;">
              Step 2: Find Products with "${filters.country || 'Japan'}" + Type="${filters.type || 'single'}"
            </button>
          </div>
          
          <div style="margin-top: 10px; border-left: 3px solid #dc3545; padding-left: 10px;">
            <button id="check-all-filters" class="debug-filter-btn" style="margin-top: 5px; padding: 8px 15px; background-color: #dc3545; color: white; border: none; border-radius: 4px; cursor: pointer; width: 100%;">
              Step 3: Find Products with ALL Filters (including "${filters.top_up || 'True'}")
            </button>
          </div>
          
          <div id="step-debug-results" style="margin-top: 15px; background-color: white; border: 1px solid #ddd; padding: 10px; max-height: 300px; overflow-y: auto; display: none;"></div>
        </div>
        
        <div style="margin-top: 15px; background-color: #fff3cd; padding: 10px; border: 1px solid #ffeeba; border-radius: 4px;">
          <h4>Troubleshooting Tools:</h4>
          <button id="check-products-with-japan" style="margin-top: 10px; padding: 8px 15px; background-color: #0066cc; color: white; border: none; border-radius: 4px; cursor: pointer;">
            Check Products With "${filters.country || 'Japan'}"
          </button>
          <div id="japan-products-results" style="margin-top: 10px; display: none; max-height: 200px; overflow-y: auto;"></div>
        </div>
        
        <h4>Troubleshooting Tips:</h4>
        <ul style="padding-left: 20px; margin-top: 5px;">
          <li>Country names must match exactly with correct capitalization</li>
          <li>Type can be "single" or "multi" (case insensitive)</li>
          <li>Top-up must be "True" or "False" with capital first letter</li>
        </ul>
        
        <div style="margin-top: 15px;">
          <a href="${adminUrl}" target="_blank" style="display: inline-block; padding: 8px 15px; background-color: #404040; color: white; text-decoration: none; border-radius: 4px; margin-right: 10px;">
            Check Products in Admin
          </a>
          <button id="check-metafields-btn" style="margin-top: 15px; padding: 8px 15px; background-color: #0066cc; color: white; border: none; border-radius: 4px; cursor: pointer;">
            Check Actual Metafield Structure
          </button>
        </div>
        <div id="metafield-structure-results" style="margin-top: 10px; display: none;"></div>
      `;

      // Add event listener to the check metafields button
      const checkBtn = debugFiltersEl.querySelector('#check-metafields-btn');
      if (checkBtn) {
        checkBtn.addEventListener('click', () => {
          this.checkActualMetafieldStructure(filters);
        });
      }

      // Add event listener to the Japan products checker button
      const checkJapanBtn = debugFiltersEl.querySelector('#check-products-with-japan');
      if (checkJapanBtn) {
        checkJapanBtn.textContent = `Check Products With "${filters.country || 'Japan'}"`;

        checkJapanBtn.addEventListener('click', async () => {
          const resultsEl = document.querySelector('#japan-products-results');
          if (!resultsEl) return;

          resultsEl.style.display = 'block';
          resultsEl.innerHTML = '<p>Searching for products containing this country...</p>';

          try {
            // Call a special debug function to find products with this country
            const countryToFind = filters.country || 'Japan';
            const productsWithCountry = await this.findProductsWithCountry(countryToFind);

            if (productsWithCountry.length === 0) {
              resultsEl.innerHTML = `<p>No products found containing "${countryToFind}" in their country field.</p>`;
              return;
            }

            // Format the results
            let html = `<h5>Found ${productsWithCountry.length} products with "${countryToFind}":</h5>`;
            html += '<ul style="padding-left: 15px;">';

            productsWithCountry.forEach(product => {
              let countryDisplay = product.countryValue;
              if (Array.isArray(product.countryValue)) {
                countryDisplay = JSON.stringify(product.countryValue);
              }

              html += `<li><strong>${product.title}</strong>: ${countryDisplay}</li>`;
            });

            html += '</ul>';
            resultsEl.innerHTML = html;
          } catch (error) {
            resultsEl.innerHTML = `<p>Error finding products: ${error.message}</p>`;
          }
        });
      }

      // Set up the step-by-step filter debugging buttons
      const setupStepButton = (buttonId, filterConfig) => {
        const button = debugFiltersEl.querySelector(`#${buttonId}`);
        if (!button) return;

        button.addEventListener('click', async () => {
          const resultsEl = document.querySelector('#step-debug-results');
          if (!resultsEl) return;

          resultsEl.style.display = 'block';
          resultsEl.innerHTML = '<p>Searching for products...</p>';

          try {
            // Get products with the specified filter configuration
            const matchingProducts = await this.findProductsWithFilters(filterConfig);

            if (matchingProducts.length === 0) {
              resultsEl.innerHTML = `<p>No products found matching these filter criteria.</p>`;
              return;
            }

            // Format the results
            let html = `<h5>Found ${matchingProducts.length} products matching these filters:</h5>`;
            html += `<div style="margin-bottom: 10px; font-weight: bold; color: #666;">Applied filters: ${Object.entries(filterConfig).map(([k, v]) => `${k}="${v}"`).join(', ')}</div>`;
            html += '<table style="width: 100%; border-collapse: collapse;">';
            html += `
              <tr style="background-color: #f5f5f5; text-align: left;">
                <th style="padding: 8px; border: 1px solid #ddd;">Product ID</th>
                <th style="padding: 8px; border: 1px solid #ddd;">Product</th>
                <th style="padding: 8px; border: 1px solid #ddd;">Country</th>
                <th style="padding: 8px; border: 1px solid #ddd;">Type</th>
                <th style="padding: 8px; border: 1px solid #ddd;">Top-up</th>
                <th style="padding: 8px; border: 1px solid #ddd;">Actions</th>
              </tr>
            `;

            matchingProducts.forEach(product => {
              const productId = product.id.split('/').pop(); // Extract numeric ID
              const countryValue = Array.isArray(product.country)
                ? JSON.stringify(product.country)
                : product.country || 'Not set';

              html += `
                <tr>
                  <td style="padding: 8px; border: 1px solid #ddd;">${productId}</td>
                  <td style="padding: 8px; border: 1px solid #ddd;">${product.title}</td>
                  <td style="padding: 8px; border: 1px solid #ddd;">${countryValue}</td>
                  <td style="padding: 8px; border: 1px solid #ddd;">${product.type || 'Not set'}</td>
                  <td style="padding: 8px; border: 1px solid #ddd;">${getVariantPlanOptionValue(product.variants?.[0]) || 'Not set'}</td>
                  <td style="padding: 8px; border: 1px solid #ddd;">
                    <button 
                      class="check-metafields-for-product" 
                      data-product-id="${productId}"
                      style="padding: 4px 8px; background-color: #0066cc; color: white; border: none; border-radius: 4px; cursor: pointer; font-size: 12px;">
                      View Raw
                    </button>
                  </td>
                </tr>
              `;
            });

            html += '</table>';
            resultsEl.innerHTML = html;

            // Add event listeners to the "View Raw" buttons
            const viewRawButtons = resultsEl.querySelectorAll('.check-metafields-for-product');
            viewRawButtons.forEach(button => {
              button.addEventListener('click', async () => {
                const productId = button.getAttribute('data-product-id');
                if (!productId) return;

                button.textContent = 'Loading...';
                button.disabled = true;

                try {
                  // Fetch raw metafields for this product
                  const rawData = await this.fetchRawMetafields(productId);

                  // Show the data in a modal/overlay
                  this.showFilterDebugOverlay({
                    title: `Raw Metafields for Product ID: ${productId}`,
                    content: `<pre style="max-height: 400px; overflow-y: auto;">${JSON.stringify(rawData, null, 2)}</pre>`
                  });
                } catch (error) {
                  console.error('Error fetching raw metafields:', error);
                  alert(`Error fetching metafields: ${error.message}`);
                } finally {
                  button.textContent = 'View Raw';
                  button.disabled = false;
                }
              });
            });
          } catch (error) {
            resultsEl.innerHTML = `<p>Error finding products: ${error.message}</p>`;
          }
        });
      };

      // Set up each step button with its filter configuration
      setupStepButton('check-country-only', { country: filters.country });
      setupStepButton('check-country-type', { country: filters.country, type: filters.type });
      setupStepButton('check-all-filters', filters); // All filters

      // Call our debug helper
      this.debugMetafieldFiltering(filters);
    }
  }

  // New method to check the actual metafield structure in the store
  async checkActualMetafieldStructure(filters) {

    const resultsEl = document.querySelector('#metafield-structure-results');
    if (!resultsEl) return;

    // Show loading state
    resultsEl.style.display = 'block';
    resultsEl.innerHTML = '<p>Loading metafield information...</p>';

    try {
      // Ensure we have the Shopify domain
      const shopifyDomain = window.Shopify?.shop || document.location.host;

      // Get access token - this should be provided by your Shopify theme
      const storefrontAccessToken = window.storefrontAccessToken || 'YOUR_PUBLIC_TOKEN';

      if (false) { // token optional
        resultsEl.innerHTML = '<p style="color: red;">No Storefront API token found. Please configure a public access token in your theme.</p>';
        return;
      }

      // Query for products and their metafields
      const query = `
        query ProductsWithMetafields {
          products(first: 10) {
            edges {
              node {
                id
                title
                handle
                tags
                metafields(identifiers: [
                  {namespace: "filters", key: "country"},
                  {namespace: "filters", key: "type"},
                  {namespace: "global", key: "country"},
                  {namespace: "global", key: "type"}
                ]) {
                  namespace
                  key
                  value
                }
                variants(first: 250) {
                  edges {
                    node {
                      id
                      title
                      availableForSale
                      price {
                        amount
                        currencyCode
                      }
                    }
                  }
                }
              }
            }
          }
        }
      `;

      // Make the GraphQL request to Storefront API
      const response = await fetch(`https://${shopifyDomain}/api/2026-04/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(storefrontAccessToken ? {'X-Shopify-Storefront-Access-Token': storefrontAccessToken} : {}),
          'Accept-Language': window.currentLocale || document.documentElement.lang || 'en'
        },
        body: JSON.stringify({ query })
      });

      if (!response.ok) {
        const errorText = await response.text();
        console.error('API error response:', errorText);
        throw new Error(`Storefront API request failed with status ${response.status}: ${errorText}`);
      }

      const data = await response.json();

      // Check for errors in the response
      if (data.errors) {
        console.error('GraphQL errors:', data.errors);
        throw new Error(`GraphQL query returned errors: ${data.errors.map(e => e.message).join(', ')}`);
      }

      // Extract products and their metafields
      const products = data.data?.products?.edges?.map(edge => {
        const product = edge.node;

        // Extract metafields - now they are directly accessible
        const metafields = [];
        if (product.metafields && Array.isArray(product.metafields)) {
          product.metafields.forEach(metafield => {
            if (metafield) {
              metafields.push({
                namespace: metafield.namespace,
                key: metafield.key,
                value: metafield.value,
                fullKey: `${metafield.namespace}.${metafield.key}`
              });
            }
          });
        }

        return {
          id: product.id,
          title: product.title,
          handle: product.handle,
          tags: product.tags,
          metafields: metafields
        };
      }) || [];

      // Find products with filter-related metafields
      const productsWithFilterMetafields = products.filter(product =>
        product.metafields.some(metafield =>
          metafield.namespace === 'filters' ||
          metafield.fullKey.includes('country') ||
          metafield.fullKey.includes('type') ||
          metafield.fullKey.includes('top_up')
        )
      );

      if (productsWithFilterMetafields.length === 0) {
        resultsEl.innerHTML = `
          <div style="padding: 10px; background-color: #fff3cd; border: 1px solid #ffeeba; border-radius: 4px;">
            <p><strong>No products with filter metafields found.</strong></p>
            <p>This could mean:</p>
            <ul>
              <li>No products have the expected metafields</li>
              <li>The metafields use a different namespace or key format</li>
              <li>The Storefront API token doesn't have access to metafields</li>
            </ul>
            <p>Try checking your product metafields in the Shopify admin.</p>
          </div>
        `;
        return;
      }

      // Build HTML to display the metafield structure
      let html = `
        <h4>Found ${productsWithFilterMetafields.length} products with filter-related metafields:</h4>
        <div style="max-height: 300px; overflow-y: auto; border: 1px solid #ddd; padding: 10px; margin-top: 10px;">
      `;

      productsWithFilterMetafields.forEach(product => {
        html += `<h5>${product.title}</h5>`;
        html += `<table style="width: 100%; border-collapse: collapse; margin-bottom: 15px;">
          <tr style="background-color: #f5f5f5;">
            <th style="text-align: left; padding: 8px; border: 1px solid #ddd;">Namespace</th>
            <th style="text-align: left; padding: 8px; border: 1px solid #ddd;">Key</th>
            <th style="text-align: left; padding: 8px; border: 1px solid #ddd;">Full Key</th>
            <th style="text-align: left; padding: 8px; border: 1px solid #ddd;">Value</th>
          </tr>
        `;

        const filterMetafields = product.metafields.filter(metafield =>
          metafield.namespace === 'filters' ||
          metafield.fullKey.includes('country') ||
          metafield.fullKey.includes('type') ||
          metafield.fullKey.includes('top_up')
        );

        if (filterMetafields.length === 0) {
          html += `<tr><td colspan="4" style="padding: 8px; border: 1px solid #ddd;">No filter metafields found</td></tr>`;
        } else {
          filterMetafields.forEach(metafield => {
            html += `
              <tr>
                <td style="padding: 8px; border: 1px solid #ddd;">${metafield.namespace}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${metafield.key}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${metafield.fullKey}</td>
                <td style="padding: 8px; border: 1px solid #ddd;">${metafield.value}</td>
              </tr>
            `;
          });
        }

        html += `</table>`;
      });

      html += `</div>
        <div style="margin-top: 15px; padding: 10px; background-color: #d4edda; border: 1px solid #c3e6cb; border-radius: 4px;">
          <p><strong>How to fix:</strong></p>
          <ul>
            <li>Make sure your filter code uses the exact namespace and key format shown above</li>
            <li>Update your filter values to match the actual values in the metafields</li>
            <li>Check for case sensitivity issues</li>
          </ul>
        </div>
      `;

      resultsEl.innerHTML = html;

    } catch (error) {
      console.error('Error checking metafield structure:', error);
      resultsEl.innerHTML = `
        <div style="padding: 10px; background-color: #f8d7da; border: 1px solid #f5c6cb; border-radius: 4px;">
          <p><strong>Error checking metafield structure:</strong> ${error.message}</p>
          <p>Check the browser console for more details.</p>
          
          <div style="margin-top: 15px;">
            <h4>Troubleshooting Steps:</h4>
            <ol>
              <li>Verify your Storefront API token has access to metafields</li>
              <li>Check that your store domain is correct: <code>${window.Shopify?.shop || document.location.host}</code></li>
              <li>Make sure you have products with metafields in your store</li>
              <li>Try refreshing the page and trying again</li>
            </ol>
          </div>
          
          <div style="margin-top: 15px; background-color: #d1ecf1; padding: 10px; border: 1px solid #bee5eb; border-radius: 4px;">
            <h4>Important Format Information:</h4>
            <ul>
              <li>Country values might be stored as JSON arrays like <code>["Japan"]</code> (we now automatically extract the first value)</li>
              <li>Boolean values for top_up might be stored as lowercase <code>true</code> or <code>false</code> (we convert to <code>True</code>/<code>False</code>)</li>
              <li>Metafields might be stored in different namespaces (we check multiple locations)</li>
            </ul>
          </div>
          
          <div style="margin-top: 15px; padding: 10px; background-color: #e2e3e5; border: 1px solid #d6d8db; border-radius: 4px; font-family: monospace; white-space: pre-wrap; overflow-x: auto;">
            <strong>Technical Details:</strong>
            Token Status: ${window.storefrontAccessToken ? 'Token is set (starts with ' + window.storefrontAccessToken.substring(0, 5) + '...)' : 'No token found'}
            API Version: 2026-04
            Error: ${error.toString()}
          </div>
        </div>
      `;
    }
  }

  // Debug function to diagnose metafield filtering issues
  debugMetafieldFiltering(filters) {

    // If we're in a browser environment, add a global debug function
    if (typeof window !== 'undefined') {
      window.debugHeroSearch = {
        testProduct: (productTitle, country, type, topUp) => {

          // Create a mock product
          const product = {
            title: productTitle,
            metafields: {
              'filters.country': country,
              'filters.type': type,
              'filters.top_up': topUp
            }
          };

          // Test against current filters
          const matchesCountry = !filters.country || product.metafields['filters.country'] === filters.country;
          const matchesType = !filters.type || product.metafields['filters.type'].toLowerCase() === filters.type.toLowerCase();
          const matchesTopUp = !filters.top_up || product.metafields['filters.top_up'] === filters.top_up;

          return matchesCountry && matchesType && matchesTopUp;
        },

        currentFilters: filters,

        fetchProductMetafields: async (productId) => {
          try {
            const response = await fetch(`/products/${productId}.json`);
            if (!response.ok) {
              throw new Error(`Failed to fetch product: ${response.status}`);
            }
            const product = await response.json();
            return product;
          } catch (error) {
            console.error('Error fetching product:', error);
            return null;
          }
        },

        help: () => {
        }
      };

      // Add price debugging helper
      window.debugPrices = {
        testCurrentSettings: () => {

          // Test price formatting
          const testPrice = 1140; // $11.40 in cents

          if (typeof formatMoney === 'function') {
            const formatted = formatMoney(testPrice, window.MinimogSettings?.money_format || '${{amount}}');
          }

          if (window.Shopify && window.Shopify.formatMoney) {
            const shopifyFormatted = window.Shopify.formatMoney(testPrice);
          }
        },

        checkVariantPrices: () => {
          const heroSearch = window.heroSearchInstance;
          if (heroSearch && heroSearch.currentVariant) {
          } else {
          }
        }
      };

    }
  }

  // Preload product data in background for instant first interaction
  async preloadProductData() {
    // Track preload status
    this.preloadComplete = false;
    this.preloadPromise = null;

    // Keep first page load light: only warm the country availability/image cache here.
    // Variant data for the selected country is fetched immediately after selection,
    // while the full variant catalog warms later in the background.
    this.preloadPromise = (async () => {
      try {
        await this.findProductsWithCountry('United States');
        this.preloadComplete = true;
      } catch (error) {
        // Silent fail - not critical
        this.preloadComplete = false;
      }
    })();
  }

  // Helper method to find products that contain a specific country (for debugging)
  async findProductsWithCountry(countryName) {
    // Use cached products if available
    if (!this.allProductsCache) {
      this.allProductsCache = null;
      this.allProductsCacheTime = 0;
    }

    const cacheMaxAge = 60000; // 1 minute cache
    const now = Date.now();

    try {
      // Check if we have valid cached data
      if (this.allProductsCache && (now - this.allProductsCacheTime) < cacheMaxAge) {
        // Use cached data - INSTANT!
        const products = this.allProductsCache;

        // Filter for matching country
        const matchingProducts = products.filter((product) =>
          productMatchesCountryName(product, countryName)
        );

        return preferExactCountryTitleProducts(matchingProducts, countryName);
      }

      if (!this.allProductsCachePromise) {
        this.allProductsCachePromise = (async () => {
          const shopifyDomain = window.Shopify?.shop || document.location.host;
          const storefrontAccessToken = window.storefrontAccessToken || 'YOUR_PUBLIC_TOKEN';

          if (false) { // token optional
            return [];
          }

          const query = `
        query ProductsWithMetafields($after: String) {
          products(first: 250, after: $after) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                title
                handle
                tags
                featuredImage {
                  url
                  altText
                }
                metafields(identifiers: [
                  {namespace: "filters", key: "country"},
                  {namespace: "global", key: "country"},
                  {namespace: "filters", key: "type"}
                ]) {
                  namespace
                  key
                  value
                  type
                }
              }
            }
          }
        }
      `;

          const productEdges = [];
          let after = null;

          do {
            const response = await fetch(`https://${shopifyDomain}/api/2026-04/graphql.json`, {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json',
                ...(storefrontAccessToken ? {'X-Shopify-Storefront-Access-Token': storefrontAccessToken} : {}),
                // Always use English so product titles are returned in English.
                // The filtering logic (productMatchesCountryName) compares against English
                // country names — if the locale is e.g. 'nl' or 'tr' Shopify would return
                // translated titles that no longer match, causing zero products to be found.
                'Accept-Language': 'en'
              },
              body: JSON.stringify({ query, variables: { after } })
            });

            if (!response.ok) {
              const errorText = await response.text();
              throw new Error(`Failed to fetch products: ${errorText}`);
            }

            const data = await response.json();
            const connection = data.data?.products;
            productEdges.push(...(connection?.edges || []));
            after = connection?.pageInfo?.hasNextPage ? connection.pageInfo.endCursor : null;
          } while (after);

          // Extract and process products
          const products = productEdges.map(edge => {
            const product = edge.node;
            let countryValue = null;
            let typeValue = null;

            // Look for country and type metafields
            if (product.metafields) {
              for (const metafield of product.metafields) {
                if (!metafield) continue;

                if (metafield.key === 'country') {
                  countryValue = metafield.value;
                } else if (metafield.key === 'type') {
                  typeValue = metafield.value;
                }
              }
            }

            countryValue = parseCountryMetafieldValue(countryValue);

            return {
              id: product.id,
              title: product.title,
              handle: product.handle,
              tags: product.tags,
              featuredImage: product.featuredImage,
              country: countryValue,
              type: typeValue
            };
          });

          this.countryAvailabilityIndex = buildCountryAvailabilityIndex(products);
          this.allProductsCache = products;
          this.allProductsCacheTime = Date.now();
          return products;
        })();
      }

      let products;
      try {
        products = await this.allProductsCachePromise;
      } finally {
        this.allProductsCachePromise = null;
      }

      // Filter to only include products with the specific country
      const matchingProducts = products.filter((product) =>
        productMatchesCountryName(product, countryName)
      );

      return preferExactCountryTitleProducts(matchingProducts, countryName);

    } catch (error) {
      console.error('Error finding products with country:', error);
      return [];
    }
  }

  getSelectedCountryPlanCacheKey(countryName, topUpValue) {
    return `${normalizeCountryMatchValue(countryName)}::${normalizeTopUpValue(topUpValue)}`;
  }

  getSelectedCountryPlanCacheEntry(countryName, topUpValue) {
    const cacheKey = this.getSelectedCountryPlanCacheKey(countryName, topUpValue);
    const cacheMaxAge = 60000;
    const cachedProducts = this.selectedCountryPlanCache.get(cacheKey);
    const cachedTime = this.selectedCountryPlanCacheTime.get(cacheKey) || 0;

    if (!cachedProducts || (Date.now() - cachedTime) >= cacheMaxAge) {
      return null;
    }

    return cachedProducts;
  }

  normalizeSelectedCountryPlanProduct(product) {
    const processedProduct = {
      id: product.id,
      title: product.title,
      handle: product.handle,
      tags: product.tags,
      featuredImage: product.featuredImage,
      variantsCount: product.variantsCount
    };

    if (product.metafields) {
      for (const metafield of product.metafields) {
        if (!metafield) continue;

        if (metafield.key === 'country') {
          processedProduct.country = parseCountryMetafieldValue(metafield.value);
        }

        if (metafield.key === 'type') {
          processedProduct.type = metafield.value;
        }

        if (metafield.namespace === 'custom' && metafield.key === 'top_networks') {
          processedProduct.top_networks = metafield.value;
        }
      }
    }

    if (product.variants?.edges) {
      processedProduct.variants = product.variants.edges.map(({ node }) => ({
        id: String(node.id || '').split('/').pop(),
        title: node.title,
        available: node.availableForSale,
        price: parseFloat(node.price?.amount || 0),
        currencyCode: node.price?.currencyCode || window.currentCurrency || 'EUR',
        compare_at_price: node.compareAtPrice ? parseFloat(node.compareAtPrice.amount || 0) : null,
        selectedOptions: node.selectedOptions || []
      }));
    } else {
      processedProduct.variants = [];
    }

    return processedProduct;
  }

  async fetchSelectedCountryPlanProducts(countryName) {
    if (!countryName) return [];

    const shopifyDomain = window.Shopify?.shop || document.location.host;
    const storefrontAccessToken = window.storefrontAccessToken || 'YOUR_PUBLIC_TOKEN';
    const currentCountry = this.getHeroSearchContextCountry();

    const query = `
      query ProductsByCountrySearch($country: CountryCode!, $searchQuery: String!) @inContext(country: $country) {
        products(first: 20, query: $searchQuery) {
          edges {
            node {
              id
              title
              handle
              tags
              featuredImage {
                url(transform: {maxWidth: 100, maxHeight: 100})
                altText
              }
              variantsCount {
                count
              }
              metafields(identifiers: [
                {namespace: "filters", key: "country"},
                {namespace: "filters", key: "type"},
                {namespace: "global", key: "country"},
                {namespace: "global", key: "type"},
                {namespace: "custom", key: "top_networks"}
              ]) {
                namespace
                key
                value
              }
              variants(first: 250) {
                edges {
                  node {
                    id
                    title
                    availableForSale
                    price {
                      amount
                      currencyCode
                    }
                    compareAtPrice {
                      amount
                      currencyCode
                    }
                    selectedOptions {
                      name
                      value
                    }
                  }
                }
              }
            }
          }
        }
      }
    `;

    const response = await fetch(`https://${shopifyDomain}/api/2026-04/graphql.json`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(storefrontAccessToken ? { 'X-Shopify-Storefront-Access-Token': storefrontAccessToken } : {}),
        // Always use English so product titles are returned in English.
        // The filtering logic (productMatchesCountryName) compares against English
        // country names — if the locale is e.g. 'nl' or 'tr' Shopify would return
        // translated titles that no longer match, causing zero products to be found.
        'Accept-Language': 'en'
      },
      body: JSON.stringify({
        query,
        variables: {
          country: currentCountry,
          searchQuery: countryName
        }
      })
    });

    if (!response.ok) {
      throw new Error(`Selected country plan fetch failed with status ${response.status}`);
    }

    const data = await response.json();
    const matchingProducts = (data.data?.products?.edges || [])
      .map((edge) => this.normalizeSelectedCountryPlanProduct(edge.node))
      .filter((product) =>
        productMatchesCountryName(product, countryName) &&
        product.variants &&
        product.variants.length > 0
      );

    return preferExactCountryTitleProducts(matchingProducts, countryName);
  }

  warmAllProductsWithVariantsInBackground() {
    if (this.backgroundVariantWarmupPromise ||
      this.allProductsWithVariantsCache ||
      this.allProductsWithVariantsCachePromise) {
      return;
    }

    this.backgroundVariantWarmupPromise = Promise.allSettled([
      this.findProductsWithFilters({
        country: 'United States',
        type: 'single',
        planType: 'True'
      }),
      this.findProductsWithFilters({
        country: 'United States',
        type: 'single',
        planType: 'False'
      })
    ]).finally(() => {
      this.backgroundVariantWarmupPromise = null;
    });
  }

  async prefetchSelectedCountryPlans(countryName) {
    const countryKey = normalizeCountryMatchValue(countryName);
    if (!countryKey) {
      return { fixedProducts: [], dailyProducts: [] };
    }

    const fixedCache = this.getSelectedCountryPlanCacheEntry(countryName, 'True');
    const dailyCache = this.getSelectedCountryPlanCacheEntry(countryName, 'False');
    if (fixedCache && dailyCache) {
      return {
        fixedProducts: fixedCache,
        dailyProducts: dailyCache
      };
    }

    if (!this.selectedCountryPlanPromiseCache.has(countryKey)) {
      this.selectedCountryPlanPromiseCache.set(countryKey, (async () => {
        const products = await this.fetchSelectedCountryPlanProducts(countryName);
        const singleCountryProducts = products.filter((product) =>
          product.type?.toLowerCase() === 'single'
        );
        const fixedProducts = prioritizeHeroPlanProducts(singleCountryProducts, 'True');
        const dailyProducts = prioritizeHeroPlanProducts(singleCountryProducts, 'False');

        this.selectedCountryPlanCache.set(this.getSelectedCountryPlanCacheKey(countryName, 'True'), fixedProducts);
        this.selectedCountryPlanCache.set(this.getSelectedCountryPlanCacheKey(countryName, 'False'), dailyProducts);
        this.selectedCountryPlanCacheTime.set(this.getSelectedCountryPlanCacheKey(countryName, 'True'), Date.now());
        this.selectedCountryPlanCacheTime.set(this.getSelectedCountryPlanCacheKey(countryName, 'False'), Date.now());

        if (dailyProducts.length > 0) {
          const contextCountry = this.getHeroSearchContextCountry();
          // Fire variant pagination in background only — do NOT await.
          // selectedCountryPrefetchPromise must resolve quickly so the drawer
          // can open. Full variant data is not needed until screen 3.
          dailyProducts.forEach((product) => {
            this.fetchVisibleHeroSearchVariants(product, { country: contextCountry }).catch(() => {});
            this.fetchMissingHeroSearchVariants(product, { country: contextCountry }).catch(() => {});
          });
        }

        return { fixedProducts, dailyProducts };
      })());
    }

    try {
      return await this.selectedCountryPlanPromiseCache.get(countryKey);
    } finally {
      this.selectedCountryPlanPromiseCache.delete(countryKey);
      setTimeout(() => this.warmAllProductsWithVariantsInBackground(), 0);
    }
  }

  // Helper method to find products with specific filters applied step by step (for debugging)
  async findProductsWithFilters(filterConfig) {

    // Use cached products with variants if available
    if (!this.allProductsWithVariantsCache) {
      this.allProductsWithVariantsCache = null;
      this.allProductsWithVariantsCacheTime = 0;
    }

    const cacheMaxAge = 60000; // 1 minute cache
    const now = Date.now();

    try {
      let products;

      // Check if we have valid cached data
      if (this.allProductsWithVariantsCache && (now - this.allProductsWithVariantsCacheTime) < cacheMaxAge) {
        // Use cached data - INSTANT!
        products = this.allProductsWithVariantsCache;
      } else {
        if (!this.allProductsWithVariantsCachePromise) {
          this.allProductsWithVariantsCachePromise = (async () => {
            const shopifyDomain = window.Shopify?.shop || document.location.host;
            const storefrontAccessToken = window.storefrontAccessToken || 'YOUR_PUBLIC_TOKEN';

            if (false) { // token optional
              return [];
            }

            let currentCountry = window.currentCountry || 'US';
            if (window.currentCurrency) {
              const currencyCountry = this.getCountryContext(window.currentCurrency);
              if (currencyCountry) {
                currentCountry = currencyCountry;
              }
            }

            const query = `
        query ProductsWithMetafields($country: CountryCode!, $after: String) @inContext(country: $country) {
          products(first: 250, after: $after) {
            pageInfo {
              hasNextPage
              endCursor
            }
            edges {
              node {
                id
                title
                handle
                tags
                featuredImage {
                  url(transform: {maxWidth: 100, maxHeight: 100})
                  altText
                }
                variantsCount {
                  count
                }
                metafields(identifiers: [
                  {namespace: "filters", key: "country"},
                  {namespace: "filters", key: "type"},
                  {namespace: "global", key: "country"},
                  {namespace: "global", key: "type"},
                  {namespace: "custom", key: "top_networks"}
                ]) {
                  namespace
                  key
                  value
                }
                variants(first: 250) {
                  edges {
                  node {
                    id
                    title
                    availableForSale
                    price {
                      amount
                      currencyCode
                    }
                    compareAtPrice {
                      amount
                      currencyCode
                    }
                    selectedOptions {
                      name
                      value
                    }
                  }
                }
              }
            }
            }
          }
        }
      `;

            const productEdges = [];
            let after = null;

            do {
              const response = await fetch(`https://${shopifyDomain}/api/2026-04/graphql.json`, {
                method: 'POST',
                headers: {
                  'Content-Type': 'application/json',
                  ...(storefrontAccessToken ? {'X-Shopify-Storefront-Access-Token': storefrontAccessToken} : {}),
                  // Always use English so product titles are returned in English.
                  // The filtering logic compares against English country names —
                  // a non-English locale would return translated titles that won't match.
                  'Accept-Language': 'en'
                },
                body: JSON.stringify({
                  query,
                  variables: { country: currentCountry, after }
                })
              });

              if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Failed to fetch products: ${errorText}`);
              }

              const data = await response.json();
              const connection = data.data?.products;
              productEdges.push(...(connection?.edges || []));
              after = connection?.pageInfo?.hasNextPage ? connection.pageInfo.endCursor : null;
            } while (after);

            const nextProducts = productEdges.map(edge => {
              const product = edge.node;

              const processedProduct = {
                id: product.id,
                title: product.title,
                handle: product.handle,
                tags: product.tags,
                featuredImage: product.featuredImage,
                variantsCount: product.variantsCount
              };

          // Process metafields
          if (product.metafields) {
            for (const metafield of product.metafields) {
              if (!metafield) continue;

              // Process country metafield
              if (metafield.key === 'country') {
                let countryValue = metafield.value;

                // Try to parse JSON arrays
                processedProduct.country = parseCountryMetafieldValue(countryValue);
              }

              // Process type metafield
              if (metafield.key === 'type') {
                processedProduct.type = metafield.value;
              }

              // Process custom top_networks metafield
              if (metafield.namespace === 'custom' && metafield.key === 'top_networks') {
                let topNetworksValue = metafield.value;

                // Some stores may store this as a JSON array string; normalize to a display string.
                if (typeof topNetworksValue === 'string') {
                  const trimmed = topNetworksValue.trim();
                  try {
                    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
                      const parsed = JSON.parse(trimmed);
                      if (Array.isArray(parsed)) {
                        topNetworksValue = parsed.join(', ');
                      }
                    }
                  } catch (e) {
                    // Keep raw string if parsing fails
                  }
                }

                processedProduct.top_networks = topNetworksValue;
              }
            }
          }

          // Process variants
          if (product.variants && product.variants.edges) {
            processedProduct.variants = product.variants.edges.map(variantEdge => {
              const variant = variantEdge.node;
              return {
                id: variant.id.split('/').pop(),
                title: variant.title,
                available: variant.availableForSale,
                price: parseFloat(variant.price.amount),
                currencyCode: variant.price.currencyCode,
                compare_at_price: variant.compareAtPrice ? parseFloat(variant.compareAtPrice.amount || 0) : null,
                selectedOptions: variant.selectedOptions || [],
                handle: product.handle
              };
            });

            // Log the variants found
            if (processedProduct.variants.length > 0) {
            } else {
            }
          } else {
            processedProduct.variants = [];
          }

              return processedProduct;
            });

            this.allProductsWithVariantsCache = nextProducts;
            this.allProductsWithVariantsCacheTime = Date.now();
            nextProducts.forEach((product) => {
              this.fetchMissingHeroSearchVariants(product, { country: currentCountry }).catch(() => {});
            });
            return nextProducts;
          })();
        }

        try {
          products = await this.allProductsWithVariantsCachePromise;
        } finally {
          this.allProductsWithVariantsCachePromise = null;
        }
      }

      // Create a fresh copy of products for filtering
      let filteredProducts = [...products];

      // Determine the product type (regional vs single country)
      const isRegionalPlan = filterConfig.type && filterConfig.type.toLowerCase() === 'multi';
      const requestedPlanType = filterConfig.planType || filterConfig.top_up || null;

      // DIFFERENT FILTER ORDERS BASED ON PRODUCT TYPE

      if (isRegionalPlan) {
        // === REGIONAL PLAN FILTERING FLOW ===

        // 1. First filter by type='multi' - this is the most important for regional plans
        filteredProducts = filteredProducts.filter(product => {
          if (!product.type) return false;
          const isMultiType = product.type.toLowerCase() === 'multi';
          if (isMultiType) {
          }
          return isMultiType;
        });

        // 2. Then filter by country but only if specifically requested
        if (filterConfig.country && filteredProducts.length > 0) {

          // Try to find products that explicitly include this country
          const countryMatchingProducts = filteredProducts.filter((product) =>
            productMatchesCountryName(product, filterConfig.country)
          );

          // If we found regional products that include this country, use those
          if (countryMatchingProducts.length > 0) {
            filteredProducts = countryMatchingProducts;
          } else {
            // Return empty array when no matches found
            filteredProducts = [];
            return filteredProducts; // Early return to prevent fallback logic
          }
        }

        // 3. Finally, filter by requested plan type
        if (requestedPlanType && filteredProducts.length > 0) {
          const planScopedProducts = prioritizeHeroPlanProducts(filteredProducts, requestedPlanType);

          if (planScopedProducts.length > 0) {
            filteredProducts = planScopedProducts;
          } else {
            filteredProducts = [];
            return filteredProducts;
          }
        }
      } else {
        // === SINGLE COUNTRY FILTERING FLOW ===

        // 1. First filter by country (most important for single country plans)
        if (filterConfig.country) {
          filteredProducts = filteredProducts.filter(product =>
            productMatchesCountryName(product, filterConfig.country)
          );

          filteredProducts = preferExactCountryTitleProducts(filteredProducts, filterConfig.country);

        }

        // 2. Then filter by type='single'
        if (filterConfig.type) {
          filteredProducts = filteredProducts.filter(product => {
            if (!product.type) return false;

            // Case insensitive type matching
            const isCorrectType = product.type.toLowerCase() === filterConfig.type.toLowerCase();
            return isCorrectType;
          });

        }
      }

      // 3. Finally, filter by requested plan type for all product types
      if (requestedPlanType) {
        filteredProducts = prioritizeHeroPlanProducts(filteredProducts, requestedPlanType);
      }

      // FALLBACK STRATEGIES - If no exact matches found
      if (filteredProducts.length === 0) {

        // === FALLBACK APPROACH FOR REGIONAL PLANS ===
        if (isRegionalPlan) {

          // Find products with type='multi' that include the specified country
          const multiTypeProducts = products.filter(product => {
            if (!product.type || product.type.toLowerCase() !== 'multi') return false;
            if (!product.variants || product.variants.length === 0) return false;

            // If country filter is specified, check if this regional product includes it
            if (filterConfig.country) {
              if (!product.country) return false;

              let includesCountry = false;
              if (Array.isArray(product.country)) {
                includesCountry = product.country.some(c =>
                  typeof c === 'string' &&
                  normalizeCountryMatchValue(c) === normalizeCountryMatchValue(filterConfig.country)
                );
              } else if (typeof product.country === 'string') {
                includesCountry = normalizeCountryMatchValue(product.country) === normalizeCountryMatchValue(filterConfig.country);
              }

              return includesCountry;
            }

            // If no country filter, return all multi-type products
            return true;
          });

          if (multiTypeProducts.length > 0) {

            // If we have a requested plan type, apply it at variant level
            if (requestedPlanType) {
              const topUpFiltered = prioritizeHeroPlanProducts(multiTypeProducts, requestedPlanType);

              if (topUpFiltered.length > 0) {
                return topUpFiltered;
              } else {
                // Don't return products with wrong top_up value
                return [];
              }
            }

            // If no plan-type filter, return matching multi-type products
            return multiTypeProducts;
          }
        }
        // === FALLBACK APPROACH FOR SINGLE COUNTRY PLANS ===
        else if (filterConfig.country) {

          // Find products that match country, ignoring type filter
          const countryProducts = products.filter(product => {
            if (!product.country) return false;

            return productMatchesCountryName(product, filterConfig.country) && product.variants && product.variants.length > 0;
          });

          if (countryProducts.length > 0) {
            const exactCountryProducts = preferExactCountryTitleProducts(countryProducts, filterConfig.country);

            // If we have a requested plan type, MUST apply it
            if (requestedPlanType) {
              const topUpFiltered = prioritizeHeroPlanProducts(exactCountryProducts, requestedPlanType);

              if (topUpFiltered.length > 0) {
                return topUpFiltered;
              } else {
                // Don't show products with the wrong plan type
                return [];
              }
            }

            // If no top_up filter, return all country-matching products
            return exactCountryProducts;
          }
        }

        // === NO MORE FALLBACKS ===
        // If we reach here, no products match the criteria
        // Return empty array to show "not available" message
        return [];
      }

      // If we applied all filters and still have products, return them
      return filteredProducts;

    } catch (error) {
      console.error('Error in step-by-step filtering:', error);
      return [];
    }
  }

  // Helper method to fetch raw metafields for a product
  async fetchRawMetafields(productId) {

    try {
      // Extract the numeric ID from the Storefront API ID if needed
      if (productId.includes('/')) {
        productId = productId.split('/').pop();
      }

      // First get all products through the Storefront API
      const shopifyDomain = window.Shopify?.shop || document.location.host;
      const storefrontAccessToken = window.storefrontAccessToken || 'YOUR_PUBLIC_TOKEN';

      if (false) { // token optional
        return { error: 'No API token available' };
      }

      // Query for products and their metafields
      const query = `
        query ProductMetafields {
          product(id: "gid://shopify/Product/${productId}") {
            id
            title
            handle
            metafields(first: 50) {
              edges {
                node {
                  id
                  namespace
                  key
                  value
                  type
                }
              }
            }
          }
        }
      `;

      const response = await fetch(`https://${shopifyDomain}/api/2026-04/graphql.json`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...(storefrontAccessToken ? {'X-Shopify-Storefront-Access-Token': storefrontAccessToken} : {}),
          'Accept-Language': window.currentLocale || document.documentElement.lang || 'en'
        },
        body: JSON.stringify({ query })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to fetch metafields: ${errorText}`);
      }

      const data = await response.json();

      // Extract metafields
      const product = data.data?.product;
      if (!product) {
        return { error: 'Product not found' };
      }

      // Format metafields
      const metafields = {};
      if (product.metafields?.edges) {
        product.metafields.edges.forEach(edge => {
          const metafield = edge.node;
          if (metafield) {
            const key = `${metafield.namespace}.${metafield.key}`;

            // Try to parse values like JSON
            let value = metafield.value;
            try {
              // Try to parse JSON if it looks like it
              if (typeof value === 'string' &&
                (value.startsWith('{') || value.startsWith('[') ||
                  value === 'true' || value === 'false')) {
                value = JSON.parse(value);
              }
            } catch (e) {
              // Keep original value if parsing fails
            }

            metafields[key] = {
              value: value,
              type: metafield.type
            };
          }
        });
      }

      return {
        id: product.id,
        title: product.title,
        handle: product.handle,
        metafields: metafields,
        // Include raw data for inspection
        raw: product
      };

    } catch (error) {
      console.error('Error fetching raw metafields:', error);
      return { error: error.message };
    }
  }

  // Dynamically set tab content position based on header and footer heights
  setTabContentPosition(tabContent) {
    if (!tabContent) return;
    // Layout is owned by CSS flex: .detail-panel (flex column) →
    // .detail-panel-content (flex:1) → .detail-panel-tab-content (flex:1, scrolls)
    //                                 → .detail-panel-footer (flex-shrink:0, always last)
    // Setting inline height/maxHeight here overrides flex and causes the footer
    // to jump on paint. Clear any stale values and let CSS handle it.
    tabContent.style.height = '';
    tabContent.style.maxHeight = '';
  }

  async showTabContent(tabElement) {
    // Don't proceed if the tab is disabled
    if (tabElement.classList.contains('disabled')) {
      return;
    }

    const filter = tabElement.getAttribute('data-filter');

    // Track loading start time
    this.loadingStartTime = Date.now();

    // Get the current filter type from the content panel
    const filterType = this.detailPanelContent?.getAttribute('data-filter-type') || this.activeFilterType || 'single';

    // Get existing loading bar or create a new one if needed


    // Handle sliding background animation
    const tabsContainer = tabElement.closest('.detail-panel-tabs');
    if (tabsContainer) {
      const allTabs = tabsContainer.querySelectorAll('.detail-panel-tab');
      const isSecondTab = Array.from(allTabs).indexOf(tabElement) === 1;
      if (isSecondTab) {
        tabsContainer.classList.add('second-active');
      } else {
        tabsContainer.classList.remove('second-active');
      }
    }

    // Show the selected tab content
    const targetContent = document.querySelector(`.detail-panel-tab-content[data-filter="${filter}"]`);
    if (!targetContent) return;

    // Store the tab type for later use
    const tabId = tabElement.getAttribute('data-tab');
    this.currentTabType = tabId === 'daypass' ? 'Daily Plans' : 'Other Plans';

    // First load the new data before modifying display

    // Show hidden loading state in target content while keeping old content visible
    // Show loading in the tab content
    targetContent.innerHTML = '<div class="loading" aria-hidden="true"></div>';

    // Clear any existing product selectors
    const existingSelector = document.querySelector('.product-selector-wrapper');
    if (existingSelector) {
      existingSelector.remove();
    }

    // Update visibility of tab contents
    const allContent = document.querySelectorAll('.detail-panel-tab-content');
    allContent.forEach(content => {
      if (content === targetContent) {
        content.classList.add('active');
        content.style.display = 'block';
        content.style.visibility = 'visible';
        content.style.opacity = '1';
      } else {
        content.classList.remove('active');
        content.style.display = 'none';
        content.style.visibility = 'hidden';
        content.style.opacity = '0';
      }
    });

    // Pre-load the data for the new tab
    await this.applyFilter(filterType, filter, targetContent);



    // Reset current variant when switching tabs
    this.currentVariant = null;

    // Disable continue button until a new variant is selected
    const continueButton = document.querySelector('.continue-with-variant');
    if (continueButton) {
      continueButton.disabled = true;
    }

    // Setup days slider based on the current tab
    this.setupDaysSlider();
  }

  // Unified method to apply filters and populate the detail panel tabs with products
  async applyFilter(filterType, tabFilter, tabContent) {

    try {
      // If no content to update, we can't proceed
      if (!tabContent) {
        console.error('No tab content to update');
        return;
      }



      // Get the filter parts (e.g., from "top_up.True" to { field: "top_up", value: "True" })
      const [field, value] = tabFilter.split('.');

      // Get the selected country from the search input
      const selectedCountry = this.selectedProduct?.title || '';

      // Format the filter value with proper capitalization for boolean metafield values
      const formattedFilterValue = value ? value.charAt(0).toUpperCase() + value.slice(1).toLowerCase() : value;

      // Create filter object
      const filters = {
        country: selectedCountry,
        type: filterType // 'single' or 'multi'
      };

      // Add the tab-specific filter (e.g., top_up: True/False)
      if (field && formattedFilterValue) {
        filters[field] = formattedFilterValue;
      }

      // Clear any existing product selectors when switching tabs
      const existingSelector = document.querySelector('.product-selector-wrapper');
      if (existingSelector) {
        existingSelector.remove();
      }

      // PERFORMANCE: Use tab-level cache when available.
      // tabData is keyed by tabFilter (e.g. "top_up.True") and is only cleared
      // when the selected country changes (see showSimplifiedDetail).
      // This means repeat opens of the same panel for the same country are instant.
      if (!this.tabData) this.tabData = {};

      let validProducts;
      if (this.tabData[tabFilter] && typeof this.tabData[tabFilter].products !== 'undefined') {
        // Cache hit — serve filter results from memory, no API call needed
        validProducts = this.tabData[tabFilter].products;
      } else if (filterType === 'single' && filters.country && filters.top_up) {
        const prefetchedProducts = this.getSelectedCountryPlanCacheEntry(filters.country, filters.top_up);
        if (prefetchedProducts) {
          validProducts = prefetchedProducts;
          this.tabData[tabFilter] = { products: validProducts };
        } else {
          // Cache miss — fetch from API (allProductsWithVariantsCache handles the heavy lifting)
          validProducts = await this.findProductsWithFilters(filters);
          // Store results so the next open is instant
          this.tabData[tabFilter] = { products: validProducts };
        }
      } else {
        // Cache miss — fetch from API (allProductsWithVariantsCache handles the heavy lifting)
        validProducts = await this.findProductsWithFilters(filters);
        // Store results so the next open is instant
        this.tabData[tabFilter] = { products: validProducts };
      }

      // Update the tab content with the results
      if (validProducts.length > 0) {

        // Enable the tab since we have products
        const tab = this.detailPanelContent.querySelector(`.detail-panel-tab[data-filter="${tabFilter}"]`);
        if (tab) {
          tab.classList.remove('disabled');
        }

        // Ensure the tab content is properly visible before displaying products
        if (tabContent.classList.contains('active')) {
          tabContent.style.display = 'block';
          tabContent.style.visibility = 'visible';
          tabContent.style.opacity = '1';
        }

        // Display the products
        await this.handleProductSelectors(validProducts, tabContent, tabFilter);
      } else {

        // Disable the tab since we have no products
        const tab = this.detailPanelContent.querySelector(`.detail-panel-tab[data-filter="${tabFilter}"]`);
        if (tab) {
          tab.classList.add('disabled');

          // If this is the active tab, only switch to the other tab if it actually has products
          if (tab.classList.contains('active')) {
            const allTabs = this.detailPanelContent.querySelectorAll('.detail-panel-tab');
            const otherTab = Array.from(allTabs).find(t => t !== tab);

            // Check if the other tab has products by checking its data attribute or by testing
            let otherTabHasProducts = false;
            if (otherTab) {
              // Check if the other tab has been marked as having products
              const hasProductsAttr = otherTab.getAttribute('data-has-products');
              if (hasProductsAttr === 'true') {
                otherTabHasProducts = true;
              } else if (hasProductsAttr !== 'false') {
                // If not explicitly marked, test it by fetching its products
                const otherFilter = otherTab.getAttribute('data-filter');
                const [otherField, otherValue] = otherFilter.split('.');
                const otherFilters = {
                  country: this.selectedProduct?.title || '',
                  type: this.activeFilterType || 'single',
                  [otherField]: otherValue
                };
                try {
                  const otherProducts = await this.findProductsWithFilters(otherFilters);
                  otherTabHasProducts = otherProducts.length > 0;
                  // Mark the tab with the result for future reference
                  otherTab.setAttribute('data-has-products', otherTabHasProducts ? 'true' : 'false');
                  if (!otherTabHasProducts) {
                    otherTab.classList.add('disabled');
                  }
                } catch (error) {
                  console.error('Error checking other tab products:', error);
                  otherTabHasProducts = false;
                }
              }
            }

            // Only switch if the other tab has products
            if (otherTab && otherTabHasProducts && !otherTab.classList.contains('disabled')) {
              tab.classList.remove('active');
              otherTab.classList.add('active');
              await this.showTabContent(otherTab);
              return; // Exit early since we're switching tabs
            } else {
              // Disable all tabs to prevent switching
              allTabs.forEach(t => {
                t.classList.add('disabled');
                t.style.pointerEvents = 'none';
                t.style.opacity = '0.5';
              });
              // Show the active tab as selected but disabled
              tab.classList.add('active');
              tab.style.opacity = '1';
            }
          }
        }

        // Show a message that no products are available
        if (variantsList) {
          // Use the stored collection button name (e.g., "Fixed Plan" or "Daily Plan")
          const collectionName = this.selectedCollectionName || 'plan';
          variantsList.innerHTML = `<div class="no-products-message">${collectionName} aren’t available for this destination yet</div>`;
        }

      }
    } catch (error) {
      console.error('Error applying filter:', error);

      // Use the stored collection button name (e.g., "Fixed Plan" or "Daily Plan")
      const collectionName = this.selectedCollectionName || 'plan';

      // Show error in the tab content
      tabContent.innerHTML = `
        <div class="error-message">
          <p>${collectionName} aren’t available for this destination yet</p>
        </div>
      `;
    }
  }

  // Handle product selectors for tabs with multiple products - completely simplified approach
  async handleProductSelectors(products, tabContent, tabFilter) {
    if (!products || !tabContent || !tabFilter) {
      console.error('Missing required parameters for handleProductSelectors');
      return;
    }

    const tabActiveProductId = tabContent.getAttribute('data-active-product-id');

    // If there's only one product, no need for selector UI
    if (products.length === 1) {
      // Hide any existing selector
      const existingSelector = document.querySelector('.product-selector-wrapper');
      if (existingSelector) {
        existingSelector.style.display = 'none';
      }

      // Mark this tab with the single product ID
      const productId = typeof products[0].id === 'string' ?
        (products[0].id.includes('/') ? products[0].id.split('/').pop() : products[0].id) :
        products[0].id;

      tabContent.setAttribute('data-active-product-id', productId);

      // Directly display variants for the single product
      await this.displayProductVariantsInTab(products[0], tabContent);
      return;
    }

    // Ensure the tab-style CSS is in place
    this.ensureProductSelectorStyles();

    // Create or get product selector wrapper
    let selectorWrapper = document.querySelector('.product-selector-wrapper');
    if (!selectorWrapper) {
      selectorWrapper = document.createElement('div');
      selectorWrapper.className = 'product-selector-wrapper';

      // Insert after tabs wrapper
      const tabsWrapper = document.querySelector('.detail-panel-tabs-wrapper');
      if (tabsWrapper && tabsWrapper.parentNode) {
        tabsWrapper.parentNode.insertBefore(selectorWrapper, tabsWrapper.nextSibling);
      }
    }

    // Show the selector
    selectorWrapper.style.display = 'block';

    // Create container
    let selectorContainer = selectorWrapper.querySelector('.product-selector-container');
    if (!selectorContainer) {
      selectorContainer = document.createElement('div');
      selectorContainer.className = 'product-selector-container';
      selectorWrapper.appendChild(selectorContainer);
    }

    // Sort products alphabetically by title
    const sortedProducts = [...products].sort((a, b) => {
      return a.title.localeCompare(b.title);
    });

    // Find the product to select by default
    let defaultActiveIndex = 0;

    if (tabActiveProductId) {
      // If we had a previously selected product, find it in the list
      const prevSelectedIndex = sortedProducts.findIndex(product => {
        const productId = String(product.id).split('/').pop();
        return productId === tabActiveProductId || product.id === tabActiveProductId;
      });

      if (prevSelectedIndex >= 0) {
        defaultActiveIndex = prevSelectedIndex;
      }
    }

    // Clear previous buttons and recreate them
    selectorContainer.innerHTML = '';

    // Create product selector buttons
    sortedProducts.forEach((product, index) => {
      const button = document.createElement('button');
      button.className = 'product-selector-button';
      button.setAttribute('data-product-id', product.id);
      button.setAttribute('data-filter', tabFilter);
      button.setAttribute('data-index', index);

      // Set active state if this is the default product
      if (index === defaultActiveIndex) {
        button.classList.add('active');
      }

      // Use actual product title
      button.textContent = product.title;

      // Add click event - display variants for this product
      button.addEventListener('click', async () => {
        // Update active state of buttons
        selectorContainer.querySelectorAll('.product-selector-button').forEach(btn => {
          btn.classList.remove('active');
        });

        button.classList.add('active');

        // Store the selected product ID on the tab content
        const productId = typeof product.id === 'string' ?
          (product.id.includes('/') ? product.id.split('/').pop() : product.id) :
          product.id;

        tabContent.setAttribute('data-active-product-id', productId);

        // Show loading and then display variants
        if (variantsList) {
          // Add a hidden loading placeholder to maintain layout
          variantsList.innerHTML = '<div class="loading" aria-hidden="true"></div>';

          // Display variants immediately for better performance
          await this.displayProductVariantsInTab(product, tabContent);
        }
      });

      selectorContainer.appendChild(button);
    });

    // Display variants for the default active product
    if (sortedProducts.length > 0) {
      const activeProduct = sortedProducts[defaultActiveIndex];
      const productId = typeof activeProduct.id === 'string' ?
        (activeProduct.id.includes('/') ? activeProduct.id.split('/').pop() : activeProduct.id) :
        activeProduct.id;

      tabContent.setAttribute('data-active-product-id', productId);

      // Use Promise for proper sequencing
      // Add a hidden loading placeholder to maintain layout
      tabContent.innerHTML = '<div class="loading" aria-hidden="true"></div>';

      // Display variants immediately for better performance
      await this.displayProductVariantsInTab(activeProduct, tabContent);
    }
  }

  // Add product selector tab-like styles to the page
  ensureProductSelectorStyles() {
    // Check if styles already exist
    if (document.getElementById('product-selector-styles')) {
      return;
    }

    // Create style element
    const styleElement = document.createElement('style');
    styleElement.id = 'product-selector-styles';

    // Add CSS for tab-like product selectors
    styleElement.textContent = `
       .product-selector-wrapper {
         margin: 10px 0 20px 0;
         width: 100%;
         overflow: hidden;
       }
       
       .product-selector-container {
         display: flex;
         flex-wrap: nowrap;
         border-bottom: 1px solid #e0e0e0;
         width: 100%;
         max-width: 400px;
         margin: 0 auto;
       }
       
       .product-selector-button {
         position: relative;
         flex: 1;
         padding: 8px 0;
         background: transparent;
         border: none;
         border-bottom: 2px solid transparent;
         cursor: pointer;
         font-size: 13px;
         font-weight: 500;
         color: #555;
         transition: all 0.2s ease;
         text-align: center;
         white-space: nowrap;
         overflow: hidden;
         text-overflow: ellipsis;
       }
       
       .product-selector-button:hover {
         color: #000;
       }
       
       .product-selector-button.active {
         color: #000;
         border-bottom: 2px solid #000;
         font-weight: 600;
       }
       
       /* For exact matching with image, limit to 4 buttons max with equal width */
       .product-selector-container:has(button:nth-child(4)) .product-selector-button {
         width: 25%;
       }
       
       .product-selector-container:has(button:nth-child(3)):not(:has(button:nth-child(4))) .product-selector-button {
         width: 33.333%;
       }
       
       .product-selector-container:has(button:nth-child(2)):not(:has(button:nth-child(3))) .product-selector-button {
         width: 50%;
       }
       
       /* Responsive styles */
       @media (max-width: 480px) {
         .product-selector-container {
           max-width: 100%;
         }
         
         .product-selector-button {
           padding: 6px 0;
           font-size: 12px;
         }
       }
       
       @media (max-width: 360px) {
         .product-selector-button {
           font-size: 11px;
         }
       }
     `;

    // Add to document head
    document.head.appendChild(styleElement);
  }

  // Helper to get a representative country code for a given currency
  getCountryContext(currencyCode) {
    if (!currencyCode) return null;

    // Map currencies to representative countries to fetch correct pricing
    const currencyMap = {
      'USD': 'US',
      'EUR': 'DE', // Use Germany as representative for Euro
      'GBP': 'GB',
      'JPY': 'JP',
      'CAD': 'CA',
      'AUD': 'AU',
      'NZD': 'NZ',
      'CHF': 'CH',
      'SGD': 'SG',
      'HKD': 'HK',
      'SEK': 'SE',
      'DKK': 'DK',
      'NOK': 'NO',
      'INR': 'IN',
      'BRL': 'BR',
      'MXN': 'MX',
      'KRW': 'KR',
      'CNY': 'CN',
      'AED': 'AE',
      'SAR': 'SA',
      'TRY': 'TR'
    };

    return currencyMap[currencyCode];
  }

  // Helper function to format price with currency.
  // Delegates to the global formatPriceByCurrency which uses Intl.NumberFormat —
  // supports every ISO 4217 currency code automatically (TRY, JPY, AED, INR, etc.)
  formatPrice(amount, currencyCode) {
    return formatPriceByCurrency(amount, currencyCode || window.currentCurrency || 'EUR');
  }

  // Helper function to format price with currency symbol
  async displayProductVariantsInTab(product, tabContent) {
    if (!product || !tabContent) {
      console.error('Missing product or tab content element for display');
      return;
    }

    // Start performance tracking
    const perfStart = performance.now();

    // Create a document fragment to batch DOM updates
    const fragment = document.createDocumentFragment();

    // Add a data attribute to know which product's variants are displayed
    const productId = typeof product.id === 'string' ?
      (product.id.includes('/') ? product.id.split('/').pop() : product.id) :
      product.id;

    tabContent.setAttribute('data-showing-product-id', productId);

    // Store the product reference in the tab content for later access
    tabContent.product = product;
    tabContent.selectedProduct = product;

    // Clear any stale variant selection when the displayed product changes.
    if (!this.selectedVariant?.productData || this.selectedVariant.productData.handle !== product.handle) {
      this.selectedVariant = null;
    }

    // Set the product type based on the tab
    const tabId = tabContent.getAttribute('id');
    const activeTabFilter = tabContent?.getAttribute('data-filter');
    product.type = tabId === 'daily-plans' ? 'Daily Plans' :
      tabId === 'monthly-plans' ? 'Monthly Plans' :
        tabId === 'unlimited-plans' ? 'Unlimited Plans' :
          'Other';

    if (activeTabFilter === 'top_up.False') {
      const contextCountry = this.getHeroSearchContextCountry();
      const { cache } = this.getHeroVisibleVariantCache(product, { country: contextCountry });

      // Step 1: Get Day 1 variants immediately for screen 2 render.
      // fetchDay1HeroVariants returns existing Day 1 variants instantly if already in product.variants,
      // otherwise fires a single focused fetch. Screen 2 only shows Day 1 per plan — this is all we need.
      const day1Variants = await this.fetchDay1HeroVariants(product, { country: contextCountry });

      // Step 2: Fire full variant pagination in background for screen 3 (days slider).
      // No await — screen 2 renders immediately with Day 1 variants above.
      this.fetchVisibleHeroSearchVariants(product, { country: contextCountry }).catch(() => {});
      this.fetchMissingHeroSearchVariants(product, { country: contextCountry }).catch(() => {});

      // If we got no Day 1 variants at all, fall back to full fetch so screen 2 is not empty
      if (day1Variants.length === 0 && !cache.visibleResolved && !cache.resolved) {
        await this.fetchVisibleHeroSearchVariants(product, { country: contextCountry });
      }
    }

    // Check if product has variants
    if (!product.variants || product.variants.length === 0) {
      const noVariantsMessage = document.createElement('div');
      noVariantsMessage.className = 'no-variants-message';
      noVariantsMessage.innerHTML = `<p>No plans available for ${product.title}.</p>`;
      tabContent.innerHTML = '';
      tabContent.appendChild(noVariantsMessage);
      return;
    }

    // Create a variants grid with modern styling
    const variantsGrid = document.createElement('div');
    variantsGrid.className = 'variants-grid with-info-section';

    // Cache top networks text - derive once per product
    let networksText = '';
    if (!product._cachedNetworksText) {
      // Prefer the new metafield-driven value.
      const rawNetworks = product.top_networks || product.topNetworksText || '';

      const normalizeTopNetworksText = (value) => {
        if (!value) return '';

        // If it's already an array, join it into the same "networks list" shape.
        if (Array.isArray(value)) return value.join(', ').trim();

        // Decode common HTML entities that may come through as a string value
        // and strip any accidental markup.
        const str = String(value);
        return str
          .replace(/<[^>]*>/g, '')
          .replace(/&amp;/g, '&')
          .trim();
      };

      networksText = normalizeTopNetworksText(rawNetworks);
      product._cachedNetworksText = networksText; // Cache it
    } else {
      networksText = product._cachedNetworksText; // Use cached value
    }

    const infoSection = document.createElement('div');
    infoSection.className = 'info-section';

    // Set data attributes for product identification
    infoSection.setAttribute('data-product-id', productId);
    infoSection.setAttribute('data-product-title', product.title || '');
    infoSection.setAttribute('data-product-type', product.type || '');

    // Get country name from product title (usually country is first word or before "eSIM")
    const countryMatch = product.title?.match(/^([^eSIM]+?)(?:\s+eSIM|\s*$)/i);
    const countryName = countryMatch ? countryMatch[1].trim() : '';
    infoSection.setAttribute('data-country', countryName);

    // Get the active tab's filter to determine which text to show
    const isTopUpTab = tabContent?.getAttribute('data-filter') === 'top_up.True';
    const openInfoSection = () => {
      infoSection.classList.add('flipped');
      clearInterval(infoAutoTimer);
    };

    // Create 5 info items with icons and text - with different versions for each tab
    const infoItems = [
      {
        icon: window.heroSearchIcons?.linkHotspot ?
          `<img src="${window.heroSearchIcons.linkHotspot}" width="24" height="24" alt="Hotspot link icon">` : '',
        keys: {
          topUp: 'info_local_networks',
          nonTopUp: 'info_local_networks'
        }
      },
      {
        icon: window.heroSearchIcons?.network ?
          `<img src="${window.heroSearchIcons.network}" width="24" height="24" alt="Network icon">` : '',
        keys: {
          topUp: 'info_hotspot',
          nonTopUp: 'info_hotspot'
        }
      },
      {
        topUpIcon: window.heroSearchIcons?.topUp ?
          `<img src="${window.heroSearchIcons.topUp}" width="24" height="24" alt="Top-up icon">` : '',
        nonTopUpIcon: window.heroSearchIcons?.noTopUp ?
          `<img src="${window.heroSearchIcons.noTopUp}" width="24" height="24" alt="No top-up icon">` : '',
        keys: {
          topUp: 'info_topup_available',
          nonTopUp: 'info_topup_unavailable'
        }
      },
      {
        icon: window.heroSearchIcons?.activation ?
          `<img src="${window.heroSearchIcons.activation}" width="24" height="24" alt="Activation icon">` : '',
        keys: {
          topUp: 'info_start_arrival',
          nonTopUp: 'info_start_arrival'
        }
      },
      {
        icon: window.heroSearchIcons?.speedLimit ?
          `<img src="${window.heroSearchIcons.speedLimit}" width="24" height="24" alt="Speed limit icon">` : '',
        keys: {
          topUp: 'info_speed_stop',
          nonTopUp: 'info_speed_slow'
        }
      }
    ];

    // Add all info items to the section
    infoItems.forEach(item => {
      const infoItem = document.createElement('div');
      infoItem.className = 'info-section-item';

      const iconMarkup = isTopUpTab
        ? (item.topUpIcon || item.icon || '')
        : (item.nonTopUpIcon || item.icon || '');

      const iconEl = document.createElement('div');
      iconEl.className = 'info-section-icon';
      iconEl.innerHTML = iconMarkup;

      const textEl = document.createElement('div');
      textEl.className = 'info-section-text';

      // Use the appropriate key based on the active tab
      const key = isTopUpTab ? item.keys.topUp : item.keys.nonTopUp;

      // Store key for re-translation
      textEl.setAttribute('data-i18n-key', key);

      // Set initial translated text
      textEl.textContent = this.getDetailPanelText(key);

      if (key === 'info_local_networks') {
        textEl.style.display = 'inline';
        textEl.style.cursor = 'pointer';
        textEl.style.lineHeight = '1.2';
        textEl.style.paddingBottom = '2px';
        textEl.style.textDecorationLine = 'underline';
        textEl.style.textDecorationColor = 'currentColor';
        textEl.style.textDecorationThickness = '1px';
        textEl.style.textUnderlineOffset = '3px';
        textEl.style.textDecorationSkipInk = 'none';
        textEl.style.setProperty('text-decoration-line', 'underline', 'important');
        textEl.style.setProperty('text-decoration-color', 'currentColor', 'important');
        textEl.style.setProperty('text-decoration-thickness', '1px', 'important');
        textEl.style.setProperty('text-underline-offset', '3px', 'important');
        textEl.style.setProperty('text-decoration-skip-ink', 'none', 'important');
        textEl.style.setProperty('cursor', 'pointer', 'important');
        textEl.addEventListener('click', (event) => {
          event.preventDefault();
          openInfoSection();
        });
      }

      infoItem.appendChild(iconEl);
      infoItem.appendChild(textEl);
      infoSection.appendChild(infoItem);
    });

    // ── Info Section Slider ────────────────────────────────────────────────────
    // Wrap each .info-section-item in a slide, add dots + auto-advance
    const infoItems_nodes = Array.from(infoSection.querySelectorAll('.info-section-item'));

    if (infoItems_nodes.length > 1) {
      // Build the slider track
      const infoSliderTrack = document.createElement('div');
      infoSliderTrack.className = 'info-slider-track';

      // Move each item into its own slide
      infoItems_nodes.forEach((item, idx) => {
        const slide = document.createElement('div');
        slide.className = 'info-slide ' + (idx === 0 ? 'slide-visible' : 'slide-hidden');

        // Add info icon button to the local networks slide
        if (idx === 0 && window.heroSearchIcons?.circleInfo) {
          const infoBtn = document.createElement('button');
          infoBtn.className = 'info-slide-info-btn';
          infoBtn.setAttribute('aria-label', 'More information');
          infoBtn.innerHTML = `<img src="${window.heroSearchIcons.circleInfo}" width="28" height="28" alt="">`;

          // Add click listener to trigger the 3D flip effect
          infoBtn.addEventListener('click', (e) => {
            e.preventDefault();
            e.stopPropagation();
            openInfoSection();
          });

          slide.appendChild(infoBtn);
        }

        slide.appendChild(item);
        infoSliderTrack.appendChild(slide);
      });

      // Build dots row
      const infoDotsRow = document.createElement('div');
      infoDotsRow.className = 'info-slider-dots';

      let infoCurrentIdx = 0;
      let infoAutoTimer = null;

      const goToInfoSlide = (targetIdx) => {
        const slides = infoSliderTrack.querySelectorAll('.info-slide');
        const dots = infoDotsRow.querySelectorAll('.info-slider-dot');

        const prevIdx = infoCurrentIdx;

        // Exit current slide to the LEFT
        slides[prevIdx].className = 'info-slide slide-leaving';
        dots[prevIdx].classList.remove('active');

        infoCurrentIdx = targetIdx;

        // New slide enters from the RIGHT (was sitting at translateX(100%))
        slides[infoCurrentIdx].className = 'info-slide slide-visible';
        dots[infoCurrentIdx].classList.add('active');

        // After animation completes, park the leaving slide back off-screen right
        setTimeout(() => {
          if (slides[prevIdx].classList.contains('slide-leaving')) {
            slides[prevIdx].className = 'info-slide slide-hidden';
          }
        }, 450);
      };

      const startInfoAutoAdvance = () => {
        clearInterval(infoAutoTimer);
        infoAutoTimer = setInterval(() => {
          goToInfoSlide((infoCurrentIdx + 1) % infoItems_nodes.length);
        }, 5000);
      };

      infoItems_nodes.forEach((_, idx) => {
        const dot = document.createElement('button');
        dot.className = 'info-slider-dot' + (idx === 0 ? ' active' : '');
        dot.setAttribute('aria-label', `Info item ${idx + 1}`);
        dot.addEventListener('click', () => {
          clearInterval(infoAutoTimer);
          if (idx !== infoCurrentIdx) goToInfoSlide(idx);
          startInfoAutoAdvance();
        });
        infoDotsRow.appendChild(dot);
      });

      // Replace infoSection children with a real two-sided flip card.
      infoSection.innerHTML = '';

      const infoFlipCard = document.createElement('div');
      infoFlipCard.className = 'info-section-flip-card';

      const infoFrontSide = document.createElement('div');
      infoFrontSide.className = 'info-section-front-side';
      infoFrontSide.appendChild(infoSliderTrack);
      infoFrontSide.appendChild(infoDotsRow);

      // Create the back side element for the flip
      const infoBackSide = document.createElement('div');
      infoBackSide.className = 'info-section-back-side';

      const flipBackBtn = document.createElement('button');
      flipBackBtn.className = 'info-section-flip-back-btn';
      if (window.heroSearchIcons?.flipBack) {
        flipBackBtn.innerHTML = `<img src="${window.heroSearchIcons.flipBack}" width="24" height="24" alt="Return to front">`;
      } else {
        flipBackBtn.innerHTML = '<svg viewBox="0 0 24 24"><path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z"/></svg>';
      }
      flipBackBtn.setAttribute('aria-label', 'Return to front');

      flipBackBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        infoSection.classList.remove('flipped');
        startInfoAutoAdvance();
      });

      const backSideContent = document.createElement('div');
      backSideContent.className = 'info-section-back-content';
      
      // Render only the dynamically filled network text
      const networksSpan = document.createElement('span');
      networksSpan.className = 'info-section-back-networks';
      networksSpan.textContent = networksText || 'Not specified';
      
      backSideContent.appendChild(networksSpan);

      infoBackSide.appendChild(flipBackBtn);
      infoBackSide.appendChild(backSideContent);

      infoFlipCard.appendChild(infoFrontSide);
      infoFlipCard.appendChild(infoBackSide);
      infoSection.appendChild(infoFlipCard);

      // ── Touch swipe support ───────────────────────────────────────────────
      let swipeTouchStartX = 0;
      let swipeTouchStartY = 0;
      let swipeIsDragging = false;

      infoSliderTrack.addEventListener('touchstart', (e) => {
        swipeTouchStartX = e.touches[0].clientX;
        swipeTouchStartY = e.touches[0].clientY;
        swipeIsDragging = true;
      }, { passive: true });

      infoSliderTrack.addEventListener('touchmove', (e) => {
        if (!swipeIsDragging) return;
        // If vertical scroll is dominant, cancel swipe tracking
        const dx = e.touches[0].clientX - swipeTouchStartX;
        const dy = e.touches[0].clientY - swipeTouchStartY;
        if (Math.abs(dy) > Math.abs(dx)) {
          swipeIsDragging = false;
        }
      }, { passive: true });

      infoSliderTrack.addEventListener('touchend', (e) => {
        if (!swipeIsDragging) return;
        swipeIsDragging = false;
        const dx = e.changedTouches[0].clientX - swipeTouchStartX;
        const threshold = 30; // px — minimum swipe distance
        if (Math.abs(dx) < threshold) return;

        clearInterval(infoAutoTimer);
        if (dx < 0) {
          // Swiped left → next slide
          goToInfoSlide((infoCurrentIdx + 1) % infoItems_nodes.length);
        } else {
          // Swiped right → previous slide
          goToInfoSlide((infoCurrentIdx - 1 + infoItems_nodes.length) % infoItems_nodes.length);
        }
        startInfoAutoAdvance();
      }, { passive: true });
      // ── End touch swipe support ───────────────────────────────────────────

      // Kick off auto-advance
      startInfoAutoAdvance();
    }
    // ── End Info Section Slider ────────────────────────────────────────────────

    // First add the info section to the grid
    variantsGrid.appendChild(infoSection);

    // Check if this product has "days" as a second option by analyzing variants
    let hasDaysOption = false;

    // First, determine if this product has the "Days" option by checking the variant titles
    product.variants.forEach(variant => {
      const variantTitle = variant.title;

      // Check for day patterns in title: "N days", "N day", "/N", "/ N", etc.
      const hasDays = /\b\d+\s*days?\b|\s*\/\s*\d+\b/i.test(variantTitle);
      if (hasDays) {
        hasDaysOption = true;
      }
    });

    // Store whether this product has days option for later use
    product.hasDaysOption = hasDaysOption;

    // Check which tab we're displaying variants for
    const isFixedPlansTab = activeTabFilter === 'top_up.True';
    const isDailyPlansTab = activeTabFilter === 'top_up.False';
    const requestedTopUp = isFixedPlansTab ? 'True' : isDailyPlansTab ? 'False' : null;

    // Get all available variants, scoped to the selected plan type when a merged
    // Shopify product contains both Fixed and Unlimited variants under one product.
    const allAvailableVariants = product.variants.filter((variant) => {
      if (variant?.available === false) return false;

      if (!requestedTopUp) return true;

      const variantTopUp = getVariantTopUpValue(variant);
      if (!variantTopUp) return true;
      return variantTopUp === requestedTopUp;
    });

    const getVariantDayCount = (variant) => {
      const selectedOptions = Array.isArray(variant?.selectedOptions) ? variant.selectedOptions : [];
      const dayOption = selectedOptions.find((option) =>
        /^(days?|dagen|dag|gün|gun|gunn|jours?|tage|tag|dias|dia|giorni|giorno)$/i.test(option?.name || '')
      );
      if (dayOption?.value) {
        const dayOptionValue = parseInt(dayOption.value, 10);
        if (Number.isFinite(dayOptionValue)) return dayOptionValue;
      }

      const variantTitle = variant?.title || '';
      const dayMatch = variantTitle.match(/(\d+)\s*days?/i) || variantTitle.match(/\/\s*(\d+)\s*$/);
      return dayMatch ? parseInt(dayMatch[1], 10) : null;
    };

    const dailyUnlimitedDayOrder = [3, 7, 15, 30, 1];
    const dailyUnlimitedDayOrderIndex = new Map(dailyUnlimitedDayOrder.map((dayCount, index) => [dayCount, index]));
    const isPlusVariant = (variant) => variant.title && variant.title.toLowerCase().includes('plus');
    const assignDailyUnlimitedVariantMetadata = (variant) => {
      const dayCount = getVariantDayCount(variant);
      const isChooseYourDaysVariant = dayCount === 1;

      variant.dailyUnlimitedDayCount = dayCount;
      variant.forceQuantityOnly = dayCount !== null && dayCount !== 1;
      variant.dailyUnlimitedShowPriceInLabel = isChooseYourDaysVariant;

      return variant;
    };

    let variantsToShow = [];

    if (isDailyPlansTab && hasDaysOption) {
      const buildDailyUnlimitedVariants = (variants) => {
        const variantsByDay = new Map();

        variants.forEach((variant) => {
          const dayCount = getVariantDayCount(variant);
          if (!dailyUnlimitedDayOrderIndex.has(dayCount) || variantsByDay.has(dayCount)) return;
          variantsByDay.set(dayCount, assignDailyUnlimitedVariantMetadata(variant));
        });

        return dailyUnlimitedDayOrder
          .map((dayCount) => variantsByDay.get(dayCount))
          .filter(Boolean);
      };

      // "Plus" variants are excluded from the daily/unlimited tab too - only
      // ever build the list from non-Plus variants.
      variantsToShow = buildDailyUnlimitedVariants(
        allAvailableVariants.filter((variant) => !isPlusVariant(variant))
      );
    } else {
      // For Fixed Plans tab or products without days options: Show all variants
      variantsToShow = allAvailableVariants;
    }

    // CHECK: If no variants to show after filtering, display error message immediately
    if (!variantsToShow || variantsToShow.length === 0) {
      const noVariantsMessage = document.createElement('div');
      noVariantsMessage.className = 'no-variants-message';
      noVariantsMessage.style.cssText = 'padding: 1rem; text-align: center; color: #666;';
      noVariantsMessage.innerHTML = `<p>No matching plans are available for your selection.</p>`;
      variantsList.innerHTML = '';
      variantsList.appendChild(noVariantsMessage);
      return;
    }

    // Prefetch all missing prices in parallel so variants render with prices immediately
    const variantsNeedingPrice = variantsToShow.filter(v => !v._renderedPrice);
    if (variantsNeedingPrice.length > 0) {
      await Promise.all(
        variantsNeedingPrice.map(v =>
          this.fetchServerRenderedPrice(null, v, product.handle)
            .then(priceText => { if (priceText) v._renderedPrice = priceText; })
            .catch(() => {})
        )
      );
    }

    const getVariantLabelParts = (variant) => {
      if (isDailyPlansTab && getVariantDayCount(variant) === 1) {
        return {
          optionName: '',
          packName: this.getDetailPanelText('unlimited_custom_days'),
          isCustomDays: true
        };
      }

      const selectedOptions = Array.isArray(variant?.selectedOptions) ? variant.selectedOptions : [];
      const isDaysOption = (option) => /^(days?|dagen|dag|gün|gun|gunn|jours?|tage|tag|dias|dia|giorni|giorno)$/i.test(option?.name || '');
      const planOption = selectedOptions.find((option) =>
        /^plans?$/i.test(option?.name || '')
      ) || selectedOptions.find((option) =>
        !isDaysOption(option) && !/^packs?$/i.test(option?.name || '')
      );
      const packOption = selectedOptions.find((option) =>
        /^packs?$/i.test(option?.name || '')
      ) || selectedOptions.find((option) => option !== planOption && !isDaysOption(option))
        || selectedOptions.find((option) => option !== planOption);

      const optionName = typeof planOption?.value === 'string' ? planOption.value.trim() : '';
      const packName = typeof packOption?.value === 'string' ? packOption.value.trim() : '';

      if (isDailyPlansTab && optionName) {
        return { optionName, packName };
      }

      if (packName) {
        return { optionName: '', packName };
      }

      const variantTitle = variant.title === "Default Title" ? "Standard" : variant.title;
      return { optionName: '', packName: variantTitle };
    };

    const getDisplayTitle = (variant) => {
      const { optionName, packName } = getVariantLabelParts(variant);
      return [optionName, packName].filter(Boolean).join(' ');
    };

    const getVariantSortValue = (variant) => {
      const source = `${getDisplayTitle(variant)} ${variant.title || ''}`;
      const amountMatch = source.match(/(\d+(?:\.\d+)?)\s*(mb|gb|tb)/i);
      const unitMap = { mb: 1, gb: 1024, tb: 1024 * 1024 };
      const amountValue = amountMatch
        ? parseFloat(amountMatch[1]) * (unitMap[amountMatch[2].toLowerCase()] || 1)
        : Number.MAX_SAFE_INTEGER;

      const dayMatch = source.match(/(\d+)\s*days?/i) || source.match(/\/\s*(\d+)\s*$/);
      const dayValue = dayMatch ? parseInt(dayMatch[1], 10) : 0;

      return {
        amountValue,
        dayValue,
        priceValue: Number.isFinite(variant.price) ? variant.price : Number.MAX_SAFE_INTEGER,
        titleValue: getDisplayTitle(variant).toLowerCase()
      };
    };

    const sortVariantsLowToHigh = (left, right) => {
      if (isDailyPlansTab && hasDaysOption) {
        const leftOrder = dailyUnlimitedDayOrderIndex.get(getVariantDayCount(left));
        const rightOrder = dailyUnlimitedDayOrderIndex.get(getVariantDayCount(right));

        if (leftOrder !== undefined && rightOrder !== undefined && leftOrder !== rightOrder) {
          return leftOrder - rightOrder;
        }
      }

      const leftValue = getVariantSortValue(left);
      const rightValue = getVariantSortValue(right);

      if (leftValue.amountValue !== rightValue.amountValue) {
        return leftValue.amountValue - rightValue.amountValue;
      }

      if (leftValue.dayValue !== rightValue.dayValue) {
        return leftValue.dayValue - rightValue.dayValue;
      }

      if (leftValue.priceValue !== rightValue.priceValue) {
        return leftValue.priceValue - rightValue.priceValue;
      }

      return leftValue.titleValue.localeCompare(rightValue.titleValue);
    };

    const createVariantCard = (variant) => {
      const displayTitle = getDisplayTitle(variant);
      const labelParts = getVariantLabelParts(variant);
      const isDailyUnlimitedVariant = isDailyPlansTab
        && hasDaysOption
        && dailyUnlimitedDayOrderIndex.has(getVariantDayCount(variant));
      const usesDailySlider = isDailyUnlimitedVariant && !variant.forceQuantityOnly;
      const variantEl = document.createElement('div');
      variantEl.className = 'collection-product-variant';
      if (isDailyUnlimitedVariant) {
        variantEl.classList.add('collection-product-variant--daily-unlimited');
      }
      variantEl.setAttribute('data-variant-id', variant.id);
      variantEl.setAttribute('data-product-id', product.id);
      variantEl.setAttribute('data-has-days-option', usesDailySlider.toString());
      variantEl.setAttribute('data-option-title', displayTitle);
      variantEl.setAttribute('data-is-daily-plans-tab', isDailyPlansTab.toString());

      variantEl.style.cssText = `
        width: 100%;
        box-sizing: border-box;
        margin: 0;
        padding: 8px 10px;
        border: 1px solid #e0e0e0;
        border-radius: 8px;
        cursor: pointer;
        transition: all 0.2s ease;
        background-color: white;
        height: auto;
        display: flex;
        flex-direction: row;
        align-items: center;
        justify-content: space-between;
        overflow: visible;
        position: relative;
      `;

      const contentEl = document.createElement('div');
      contentEl.className = 'variant-content';

      const topRowEl = document.createElement('div');
      topRowEl.className = 'variant-top-row';

      const variantInfoEl = document.createElement('div');
      variantInfoEl.className = 'variant-info';

      const nameEl = document.createElement('div');
      nameEl.className = 'variant-name';
      nameEl.style.cssText = 'font-weight: 400; font-size: 16px; color: #333333;';

      const priceEl = document.createElement('div');
      priceEl.className = 'variant-price';
      priceEl.style.cssText = 'font-weight: 600; font-size: 16px; color:#9a84c8;';
      priceEl.textContent = variant._renderedPrice || 'Loading...';

      const setVariantLabel = () => {
        nameEl.replaceChildren();
        const dayCount = getVariantDayCount(variant);
        const packNameIsDayOnly = dayCount !== null && String(labelParts.packName || '').trim() === String(dayCount);

        if (labelParts.optionName) {
          const optionNameEl = document.createElement('span');
          optionNameEl.className = 'variant-option-name';
          const translatedOptionName = translateVariantDisplayText(labelParts.optionName, (key) => this.getDetailPanelText(key));
          optionNameEl.textContent = isDailyPlansTab
            ? appendUnlimitedDisplayDays(translatedOptionName, dayCount, (key) => this.getDetailPanelText(key))
            : translatedOptionName;
          nameEl.appendChild(optionNameEl);
        }

        if (labelParts.optionName && labelParts.packName && !packNameIsDayOnly) {
          nameEl.appendChild(document.createTextNode(' '));
        }

        if (labelParts.packName && !packNameIsDayOnly) {
          const packNameEl = document.createElement('span');
          packNameEl.className = 'variant-pack-name';
          packNameEl.textContent = labelParts.isCustomDays
            ? labelParts.packName
            : appendUnlimitedDisplayDays(
                translateVariantDisplayText(labelParts.packName, (key) => this.getDetailPanelText(key)),
                getVariantDayCount(variant),
                (key) => this.getDetailPanelText(key)
              );
          nameEl.appendChild(packNameEl);
        }

        if (variant.dailyUnlimitedShowPriceInLabel) {
          const labelPrice = variant._renderedPrice || 'Loading...';
          priceEl.textContent = labelPrice === 'Price unavailable'
            ? labelPrice
            : `${labelPrice}/${this.getDetailPanelText('variant_label_day_singular')}`;
        } else {
          priceEl.textContent = variant._renderedPrice || 'Loading...';
        }
      };

      setVariantLabel();

      const dailyDetailEl = document.createElement('div');
      dailyDetailEl.className = 'daily-unlimited-description';
      dailyDetailEl.textContent = this.getDetailPanelText('unlimited_speed_note');

      if (!variant._renderedPrice) {
        this.fetchServerRenderedPrice(priceEl, variant, product.handle).then(priceText => {
          if (priceText) {
            variant._renderedPrice = priceText;
            priceEl.textContent = priceText;
            setVariantLabel();
          } else {
            priceEl.textContent = 'Price unavailable';
            setVariantLabel();
          }
        }).catch(error => {
          console.error('Error fetching price:', error);
          priceEl.textContent = 'Price unavailable';
          setVariantLabel();
        });
      }

      variantInfoEl.appendChild(nameEl);
      topRowEl.appendChild(variantInfoEl);
      topRowEl.appendChild(priceEl);
      contentEl.appendChild(topRowEl);
      if (isDailyUnlimitedVariant) {
        contentEl.appendChild(dailyDetailEl);
      }
      variantEl.appendChild(contentEl);

      variantEl.addEventListener('click', () => {
        tabContent.querySelectorAll('.collection-product-variant').forEach(el => {
          el.classList.remove('selected');
          el.style.borderColor = '#e0e0e0';
          el.style.backgroundColor = 'white';
          el.style.boxShadow = 'none';

          const itemNameEl = el.querySelector('.variant-name');
          if (itemNameEl) {
            itemNameEl.style.color = '#333333';
            itemNameEl.style.fontWeight = '400';
          }
        });

        variantEl.classList.add('selected');
        variantEl.style.borderColor = '#0071dc';
        variantEl.style.backgroundColor = '#f5f9ff';
        variantEl.style.boxShadow = '0 2px 4px rgba(0, 113, 220, 0.1)';
        nameEl.style.color = '#0071dc';
        if (isDailyUnlimitedVariant) {
          nameEl.style.fontWeight = '600';
        }

        tabContent.setAttribute('data-selected-variant-id', variant.id);
        tabContent.selectedVariant = variant;
        tabContent.selectedProduct = product;
        tabContent.hasDaysOption = usesDailySlider;
        tabContent.allVariants = allAvailableVariants;

        variant.hasDaysOption = usesDailySlider;
        variant.optionTitle = displayTitle;
        variant.allVariants = allAvailableVariants;
        variant.productData = product;
        this.days = getVariantDayCount(variant) || 1;

        const selectedEvent = new CustomEvent('variant:selected', {
          detail: {
            variant,
            product,
            hasDaysOption: usesDailySlider,
            allVariants: allAvailableVariants
          },
          bubbles: true
        });
        variantEl.dispatchEvent(selectedEvent);

        this.selectedVariant = variant;

        const continueBtn = document.querySelector('.continue-with-variant');
        if (continueBtn) {
          continueBtn.disabled = false;
          continueBtn.style.backgroundColor = '#9a84c8';
          continueBtn.style.cursor = 'pointer';
        }
      });

      return variantEl;
    };

    // "Plus" variants are excluded from the picker entirely, not just
    // grouped into their own accordion - they're left out of normalVariants,
    // so nothing below ever renders or lets a shopper select them.
    const normalVariants = variantsToShow
      .filter(variant => !isPlusVariant(variant))
      .sort(sortVariantsLowToHigh);

    const createVariantColumns = (variants, wrapperClassName) => {
      const wrapper = document.createElement('div');
      wrapper.className = `${wrapperClassName} detail-panel-variant-columns`;

      // Append cards in natural ascending order and let the CSS grid handle
      // column wrapping. This keeps reading order correct at any column
      // count (2-up on desktop, 1-up on mobile) instead of pre-splitting
      // into separate left/right DOM groups that only read correctly
      // when exactly 2 columns are rendered.
      variants.forEach(variant => {
        wrapper.appendChild(createVariantCard(variant));
      });

      return wrapper;
    };

    const normalVariantsWrapper = createVariantColumns(normalVariants, 'detail-panel-normal-variants');
    variantsGrid.appendChild(normalVariantsWrapper);

    // Clear existing content and add the variants grid directly to tab content
    tabContent.innerHTML = '';
    tabContent.appendChild(variantsGrid);

    // Debug: Check if scroll is working after variants are added
    setTimeout(() => {
      if (tabContent) {

        // Check if there's any CSS blocking the height
        const tabContentStyles = window.getComputedStyle(tabContent);
      }
    }, 100);



    // Don't auto-select any variant by default, user must explicitly choose
  }

  setupQuantityInput() {
    const container = document.querySelector('.hero-screen-3');
    if (!container) return;

    this.quantityInput = container.querySelector('.m-quantity__input');
    if (!this.quantityInput) return;

    // Multiple event listeners for reliable mobile support
    const updatePriceHandler = () => {
      if (this.currentVariant) {
        this.updatePrice();
      }
    };

    // Listen for standard change event
    this.quantityInput.addEventListener('change', updatePriceHandler);

    // Listen for input event (fires on mobile)
    this.quantityInput.addEventListener('input', updatePriceHandler);

    // Also listen for quantity button clicks directly
    const quantityButtons = container.querySelectorAll('.m-quantity__button');
    quantityButtons.forEach(button => {
      button.addEventListener('click', () => {
        setTimeout(updatePriceHandler, 50); // Small delay to ensure value has updated
      });

      // Add touch event for mobile reliability
      button.addEventListener('touchend', () => {
        setTimeout(updatePriceHandler, 50);
      });
    });
  }

  // Returns the formatted price for a variant using data already fetched by the
  // Storefront API @inContext query. No extra HTTP fetch to the product page needed,
  // which was the root cause of broken prices when a non-default currency was selected
  // (the product page fetch had no currency/country session context).
  async fetchServerRenderedPrice(priceEl, variant, productHandle) {
    if (!variant) return null;

    const amount = variant.price || 0;
    const currencyCode = variant.currencyCode || window.currentCurrency || 'EUR';
    const priceText = formatPriceByCurrency(amount, currencyCode);

    if (priceEl) priceEl.textContent = priceText;
    return priceText;
  }

  // Add this method after updateDetailPanelPosition
  reinitializeDetailPanel() {

    if (!this.detailPanel) {
      console.error('Detail panel not found, cannot reinitialize');
      return;
    }

    // Remove any existing event listeners
    if (this.outsideClickListenerAdded) {
      document.removeEventListener('click', this.handleOutsideDetailPanelClick.bind(this));
      this.outsideClickListenerAdded = false;
    }

    // Hide backdrop (mobile only)
    if (this.detailPanelBackdrop) {
      this.detailPanelBackdrop.classList.remove('active');
      this.detailPanelBackdrop.style.display = 'none';
    }

    // Reset panel state
    this.detailPanel.style.display = 'none';
    this.detailPanel.classList.remove('active');
    this.detailPanel.style.opacity = '0';
    this.detailPanel.style.visibility = 'hidden';

    // Clear content
    if (this.detailPanelContent) {
      this.detailPanelContent.innerHTML = '';
    }

    // PERFORMANCE: Do NOT clear tabData here — country-aware invalidation in
    // showSimplifiedDetail handles cache freshness. Clearing here would discard
    // valid cache entries every time the user navigates back to Screen 2,
    // forcing unnecessary API re-fetches even for the same country.
    // tabData is only reset when the country actually changes.
    this.selectedVariant = null;
    this.activeFilterType = null;
    // Reset the last-country tracker so the panel is always re-evaluated on next open
    // (this covers the case where the user goes back to Screen 1 and selects a new country)
    this._lastDetailPanelCountry = null;

    // Restore background scrolling if it was locked
    this.restoreBackgroundScroll();

    // Reattach click listener
    document.addEventListener('click', this.handleOutsideDetailPanelClick.bind(this));
    this.outsideClickListenerAdded = true;

  }

  preventBackgroundScroll() {
    // Store the current scroll position
    const currentScrollY = window.pageYOffset || document.documentElement.scrollTop;
    this.savedScrollPosition = currentScrollY;

    // Compensate for scrollbar width BEFORE adding overflow:hidden.
    // When overflow:hidden removes the scrollbar the viewport widens by ~15px,
    // causing a reflow that shifts fixed/absolute positioned elements — this was
    // the "starts correct → snaps wrong → snaps back" jump on panel open.
    const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
    if (scrollbarWidth > 0) {
      document.body.style.paddingRight = `${scrollbarWidth}px`;
    }

    // Apply CSS classes for scroll prevention
    document.documentElement.classList.add('detail-panel-scroll-locked');
    document.body.classList.add('detail-panel-scroll-locked');
  }

  positionDetailPanelInLandscape() {
    // No-op: inline transform overrides were causing the snap-back jump.
    // Landscape positioning is handled by CSS media queries.
  }

  restoreBackgroundScroll() {
    // Remove scroll lock classes and restore scrollbar compensation
    document.body.classList.remove('detail-panel-scroll-locked');
    document.documentElement.classList.remove('detail-panel-scroll-locked');
    document.body.style.paddingRight = '';

    // Remove class that lowers header/footer z-index
    document.body.classList.remove('detail-panel-open');
    document.documentElement.classList.remove('detail-panel-open');

    // Reset detail panel transform if it was modified
    if (this.detailPanel) {
      this.detailPanel.style.transform = '';
    }

    // Restore scroll position if it was saved
    if (typeof this.savedScrollPosition === 'number') {
      window.scrollTo({
        top: this.savedScrollPosition,
        behavior: 'auto'
      });
    }
  }
}

// Clear any orphaned tooltips and panels from previous initializations
function cleanupExistingElements() {
  // Remove any floating tooltips or panels that might be orphaned
  const orphanedElements = document.querySelectorAll('.detail-panel-tooltip, .detail-panel.active, .product-selector-wrapper');
  orphanedElements.forEach(el => {
    if (el.style) {
      el.style.display = 'none';
    }
  });

  // Clear any loading indicators that might be stuck
  const loadingElements = document.querySelectorAll('.loading');
  loadingElements.forEach(el => {
    if (el.parentNode) {
      el.parentNode.removeChild(el);
    }
  });
}

function teardownHeroSearchInstance() {
  const instance = window.heroSearch;
  if (!instance) {
    window.__heroSearchBootstrapping = false;
    return;
  }

  if (typeof instance.destroy === 'function') {
    instance.destroy();
  }

  window.heroSearch = null;
  window.heroSearchInstance = null;
  window.__heroSearchBootstrapping = false;
}

function triggerHeroSearchAttentionCue() {
  const heroSearchRoot = document.querySelector('.hero-search');
  const heroSearchInstance = window.heroSearch || window.heroSearchInstance;
  const heroSearchLeft = heroSearchRoot?.querySelector('.hero-search__left');
  const searchBox = heroSearchRoot?.querySelector('.search-box');
  const searchInput = searchBox?.querySelector('.search-input');

  if (!heroSearchRoot || !heroSearchLeft || !searchBox || !searchInput) {
    return false;
  }

  const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  const currentScreen = heroSearchInstance?.currentScreen || 1;
  const detailPanelHeader = heroSearchInstance?.detailPanel?.classList.contains('active')
    ? heroSearchInstance.detailPanel.querySelector('.detail-panel-header') || heroSearchInstance.detailPanel
    : null;
  const contextTarget = detailPanelHeader
    || (currentScreen === 2
      ? heroSearchLeft
      : currentScreen === 3
        ? heroSearchLeft
        : null);
  const useSearchBarAttention = !contextTarget || currentScreen === 1;
  const attentionTarget = useSearchBarAttention ? searchBox : contextTarget;
  const attentionRect = attentionTarget.getBoundingClientRect();
  const isVisible = attentionRect.bottom > 0 && attentionRect.top < window.innerHeight;

  heroSearchRoot.scrollIntoView({
    behavior: prefersReducedMotion ? 'auto' : 'smooth',
    block: 'start'
  });

  const focusSearchInput = () => {
    try {
      searchInput.focus({ preventScroll: true });
    } catch (error) {
      searchInput.focus();
    }
  };

  const runCue = () => {
    if (prefersReducedMotion) {
      focusSearchInput();
      return;
    }

    let cueDuration = 700;

    if (useSearchBarAttention) {
      heroSearchLeft.classList.add('hero-search__left--attention-active');
      cueDuration = 1500;

      if (typeof searchBox.getAnimations === 'function') {
        searchBox.getAnimations().forEach((animation) => animation.cancel());
      }

      if (typeof searchInput.getAnimations === 'function') {
        searchInput.getAnimations().forEach((animation) => animation.cancel());
      }

      if (typeof searchBox.animate === 'function' && typeof searchInput.animate === 'function') {
        searchBox.animate(
          [
            { transform: 'scale(1)' },
            { transform: 'scale(1.34)', offset: 0.32 },
            { transform: 'scale(1.22)', offset: 0.52 },
            { transform: 'scale(1.1)', offset: 0.74 },
            { transform: 'scale(1)' }
          ],
          {
            duration: cueDuration,
            easing: 'cubic-bezier(0.2, 0.72, 0.22, 1)'
          }
        );

        searchInput.animate(
          [
            { boxShadow: '0 0 0 rgba(154, 132, 200, 0)' },
            { boxShadow: '0 18px 42px rgba(154, 132, 200, 0.32)', offset: 0.32 },
            { boxShadow: '0 14px 32px rgba(154, 132, 200, 0.22)', offset: 0.52 },
            { boxShadow: '0 8px 18px rgba(154, 132, 200, 0.12)', offset: 0.74 },
            { boxShadow: '0 0 0 rgba(154, 132, 200, 0)' }
          ],
          {
            duration: cueDuration,
            easing: 'cubic-bezier(0.2, 0.72, 0.22, 1)'
          }
        );
      } else {
        searchBox.classList.remove('search-box--attention');
        void searchBox.offsetWidth;
        searchBox.classList.add('search-box--attention');
      }
    } else {
      cueDuration = 900;

      if (typeof attentionTarget.getAnimations === 'function') {
        attentionTarget.getAnimations().forEach((animation) => animation.cancel());
      }

      if (typeof attentionTarget.animate === 'function') {
        attentionTarget.animate(
          [
            { transform: 'translateY(0) scale(1)' },
            { transform: 'translateY(-10px) scale(1.045)', offset: 0.34 },
            { transform: 'translateY(-5px) scale(1.025)', offset: 0.58 },
            { transform: 'translateY(-2px) scale(1.01)', offset: 0.78 },
            { transform: 'translateY(0) scale(1)' }
          ],
          {
            duration: cueDuration,
            easing: 'cubic-bezier(0.2, 0.72, 0.22, 1)'
          }
        );
      } else {
        attentionTarget.classList.remove('hero-search-context-attention');
        void attentionTarget.offsetWidth;
        attentionTarget.classList.add('hero-search-context-attention');
      }
    }

    let didComplete = false;
    const finishCue = () => {
      if (didComplete) return;
      didComplete = true;
      searchBox.classList.remove('search-box--attention');
      heroSearchLeft.classList.remove('hero-search__left--attention-active');
      if (!useSearchBarAttention) {
        attentionTarget.classList.remove('hero-search-context-attention');
      }
      focusSearchInput();
    };

    attentionTarget.addEventListener('animationend', finishCue, { once: true });
    window.setTimeout(finishCue, cueDuration + 100);
  };

  const cueDelay = prefersReducedMotion ? 0 : (isVisible ? 120 : 500);
  window.setTimeout(() => {
    window.requestAnimationFrame(runCue);
  }, cueDelay);

  return true;
}

function getHeroSearchFallbackUrl(link) {
  const explicitFallback = link?.getAttribute('data-hero-search-fallback');
  if (explicitFallback) {
    return explicitFallback;
  }

  const pathSegments = window.location.pathname.split('/').filter(Boolean);
  const firstSegment = pathSegments[0] || '';
  const localePrefix = /^[a-z]{2}$/i.test(firstSegment) ? `/${firstSegment}` : '';

  return `${localePrefix}/pages/esim`;
}

function normalizeHeroSearchNavLabel(value) {
  return (value || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .replace(/[^a-z0-9]+/g, ' ')
    .trim();
}

function isSearchbarZoomHref(href) {
  if (!href) return false;
  if (href === '#searchbarzoom') return true;

  try {
    const url = new URL(href, window.location.href);
    return url.hash === '#searchbarzoom';
  } catch (error) {
    return false;
  }
}

function isEsimPageHref(href) {
  if (!href) return false;

  try {
    const url = new URL(href, window.location.href);
    const path = url.pathname.replace(/\/+$/, '') || '/';
    return /\/pages\/esim$/i.test(path);
  } catch (error) {
    return false;
  }
}

function shouldHandleEsimsNavLink(link) {
  if (!link) {
    return false;
  }

  if (!link.closest('.m-header')) {
    return false;
  }

  if (link.closest('.m-menu-drawer')) {
    return false;
  }

  if (link.hasAttribute('data-hero-search-attention')) {
    return true;
  }

  const label = normalizeHeroSearchNavLabel(link.textContent || '');
  const href = link.getAttribute('href') || '';
  if (isSearchbarZoomHref(href) || isEsimPageHref(href)) {
    return true;
  }

  const looksLikeEsimLink = label.includes('esim') || label.includes('e sim');
  if (!looksLikeEsimLink) {
    return false;
  }

  if (!href || href === '#' || href.startsWith('#')) {
    return true;
  }

  try {
    const url = new URL(href, window.location.href);
    const currentPath = window.location.pathname.replace(/\/+$/, '') || '/';
    const targetPath = url.pathname.replace(/\/+$/, '') || '/';
    return url.origin === window.location.origin && (targetPath === currentPath || targetPath === '/');
  } catch (error) {
    return false;
  }
}

let hasTriggeredSearchZoom = false;

function getHeroSearchQueryValue() {
  const heroSearchRoot = document.querySelector('.hero-search');
  const searchInput = heroSearchRoot?.querySelector('#search-input') || heroSearchRoot?.querySelector('.search-input');
  return (searchInput?.value || '').trim();
}

function getEsimHubUrlWithQuery(link, queryValue) {
  const baseUrl = getHeroSearchFallbackUrl(link);
  if (!queryValue) {
    return baseUrl;
  }

  try {
    const url = new URL(baseUrl, window.location.href);
    url.searchParams.set('q', queryValue);
    return `${url.pathname}${url.search}${url.hash}`;
  } catch (error) {
    return baseUrl;
  }
}

function bindHeroSearchNavAttention() {
  if (window.__heroSearchNavAttentionBound) {
    return;
  }

  window.__heroSearchNavAttentionBound = true;

  document.addEventListener('click', (event) => {
    const link = event.target.closest('a');

    if (!shouldHandleEsimsNavLink(link)) {
      return;
    }

    event.preventDefault();

    if (document.querySelector('.hero-search')) {
      if (!hasTriggeredSearchZoom) {
        hasTriggeredSearchZoom = true;
        triggerHeroSearchAttentionCue();
      } else {
        window.location.assign(getEsimHubUrlWithQuery(link, getHeroSearchQueryValue()));
      }
      return;
    }

    window.location.assign(getHeroSearchFallbackUrl(link));
  });
}

function bootstrapHeroSearch(options = {}) {
  const { force = false, root = document } = options;
  const heroSearchRoot = root.querySelector?.('.hero-search') || document.querySelector('.hero-search');

  if (!heroSearchRoot) return;
  if (!force && (window.heroSearch || window.__heroSearchBootstrapping)) return;

  if (force) {
    teardownHeroSearchInstance();
  }

  cleanupExistingElements();
  window.__heroSearchBootstrapping = true;

  window.requestAnimationFrame(() => {
    setTimeout(() => {
      if (!force && window.heroSearch) {
        window.__heroSearchBootstrapping = false;
        return;
      }

      try {
        window.heroSearch = new HeroSearch();
      } finally {
        window.__heroSearchBootstrapping = false;
      }

      document.body.offsetHeight;

      const detailPanels = document.querySelectorAll('.detail-panel, .detail-panel-content');
      detailPanels.forEach((panel) => {
        if (!panel) return;

        void panel.offsetHeight;
        panel.style.willChange = 'transform';

        setTimeout(() => {
          panel.style.willChange = 'auto';
        }, 100);
      });
    }, 50);
  });
}

window.bootstrapHeroSearch = bootstrapHeroSearch;

if (!window.__heroSearchGlobalBindingsAdded) {
  window.__heroSearchGlobalBindingsAdded = true;
  bindHeroSearchNavAttention();

  document.addEventListener('DOMContentLoaded', () => {
    bootstrapHeroSearch();
  });

  document.addEventListener('shopify:section:load', (event) => {
    if (!event.target?.querySelector?.('.hero-search')) return;
    bootstrapHeroSearch({ force: true, root: event.target });
  });
}
